// ═══════════════════════════════════════════════════════════════════
// LEGACY ACCESS CODES REMOVED IN AUTH DEVELOPMENT v2.5
// ═══════════════════════════════════════════════════════════════════
const CODES = Object.freeze({}); // Legacy demo access-code credentials intentionally removed.

// ═══════════════════════════════════════════════════════════════════
// DATA — EMPLOYEE UNITS (1-12) + LEADERSHIP UNITS (13-17)
// ═══════════════════════════════════════════════════════════════════

const EMP_UNITS = [{"id":1,"num":"01","title":"The Healthcare Logistics Industry","sections":[{"h":"What Is Healthcare Logistics?","body":"Healthcare logistics is the movement of medical materials between facilities. Every day across America, millions of biological specimens, medications, blood products, medical devices, and clinical supplies move between hospitals, laboratories, pharmacies, clinics, surgery centers, and patient homes. These movements require disciplined handling, documentation, communication, and escalation.\n\nCorevian was built to provide healthcare organizations with a controlled logistics operating system rather than ordinary package delivery."},{"h":"What Gets Moved Every Day","body":"Understanding what we carry is fundamental. Not just what it is called — but what happens to a patient if it is handled wrong. That understanding is what makes a Corevian driver fundamentally different from every other courier.\n\nClinical Laboratory Specimens\n\nThese are biological samples from patients — blood draws, urine samples, tissue biopsies, swabs, and cultures. A phlebotomist draws the blood, labels the tube, packages it in a UN3373-compliant container, and hands it to us. We deliver it to the reference lab. The lab runs the test. The doctor gets the result. The doctor makes a treatment decision.\n\nCategory A vs. Category B Biological Substances — Biological substances are classified by transport risk. Category A infectious substances are materials capable, when exposure occurs, of causing permanent disability or life-threatening or fatal disease in otherwise healthy humans or animals; they require Category A dangerous-goods handling and must never be treated as UN3373 Category B material. Category B biological substances are infectious substances that do not meet Category A criteria and are assigned UN3373. Routine diagnostic specimens may fall into Category B only after the shipper has properly classified and packaged them.\n\nBlood Products\n\nBlood and blood components are among the most time-sensitive and temperature-critical materials in existence. Packed red blood cells (pRBCs) must be maintained between 1 and 6 degrees Celsius from the moment they leave the blood bank to the moment they are handed to the transfusion service. Platelets must be kept between 20 and 24 degrees Celsius and agitated continuously. Fresh frozen plasma and cryoprecipitate must remain in the frozen condition specified by the supplying facility/client and controlled blood-product procedure. Any reading outside the assigned product/client range is treated as a temperature excursion and escalated for disposition.\n\nPharmaceuticals\n\nMedications transported in healthcare logistics include specialty biologics, compounded sterile preparations, IV infusion medications, controlled substances, and standard prescription drugs. Many require refrigeration between 2 and 8 degrees Celsius. All require HIPAA-compliant handling because they are linked to specific patients. Specialty drugs can cost $10,000 to $50,000 per dose — a temperature failure is not just a compliance event, it is a massive financial loss.\n\nMedical Devices\n\nSurgical instrument trays, orthopedic implants, cardiac devices, DME for patients at home, and sterile processing outputs all move through healthcare logistics networks every day. A surgical loaner tray that arrives 20 minutes late can delay an entire operating room, which costs a hospital $50 to $150 per minute in direct costs — plus downstream schedule disruption."},{"h":"The Healthcare Customer Types","body":""},{"h":"The Regulatory Landscape","body":""},{"h":"The Problem — Why The Industry Needs Corevian","body":"Here is what the healthcare logistics market actually looks like at the regional level. This is not speculation. This is what hospital procurement directors, lab managers, and pharmacy directors tell us when they describe their current courier situation:\n\nThe majority of regional healthcare couriers have no formal HIPAA training — drivers sign a form, call it done, and never think about it again\n\nMost use personal vehicles with consumer coolers and grocery store ice — no temperature logging, no calibration records, no excursion documentation\n\nMost have no chain-of-custody system — delivery means a driver rings the bell and hands the package to whoever answers\n\nGig economy platforms have entered healthcare transport — an anonymous driver who delivered pizza yesterday is picking up blood specimens today\n\nWhen incidents occur — and they do — most regional couriers have no incident report, no CAPA, and no documentation. They apologize and move on\n\nMost provide zero performance reporting — clients have no visibility into on-time rates, temperature compliance, or documentation quality"}],"questions":["Define healthcare logistics in your own words and explain why Corevian treats transportation as part of the clinical operation rather than ordinary package delivery.","Name the four major material categories introduced in this unit—clinical laboratory specimens, blood products, pharmaceuticals, and medical devices—and explain one operational or patient-care consequence of mishandling each.","Explain the difference between Category A and Category B biological substances. Which category is assigned UN3373, and who is responsible for properly classifying and packaging the specimen before transport?","What temperature range is taught for packed red blood cells (pRBCs), and why is maintaining that range important?","What temperature range is taught for platelets, and what additional handling requirement makes platelets operationally different from pRBCs?","What frozen-temperature requirement is taught for fresh frozen plasma and cryoprecipitate?","Name the healthcare customer types described in this unit and explain why their logistics needs may differ.","Describe the regulatory landscape introduced in this unit and explain why a Corevian specialist must understand which requirements apply to the material being transported.","Identify three recurring failures the unit describes in regional healthcare courier operations and explain how Corevian's infrastructure is intended to address them.","In one clear statement, explain the operational problem Corevian was built to solve."],"quiz":[{"q":"The US healthcare logistics market is valued at approximately how much, and what is the annual growth rate?","opts":["$3.1 billion, growing at 2% per year","$7.2 billion, growing at 6% per year","$12 billion, growing at 10% per year","$1.8 billion, growing at 4% per year"],"correct":1,"feedback":"$7.2 billion growing at 6% annually. The drivers: aging population, expanding outpatient care, specialty pharmacy growth, and home infusion expansion \\u2014 all generating more transport demand every year."},{"q":"A specialty biologic costs $40,000 per dose and requires refrigeration at 2\\u00b0C to 8\\u00b0C. During transport the temperature reaches 11\\u00b0C for 20 minutes. What has occurred and what must you do?","opts":["Nothing \\u2014 biologics tolerate minor variation. Continue delivery.","A temperature excursion has occurred. Stop. Notify Operations immediately. Do not deliver. The client determines product disposition.","The product is likely fine. Deliver and note the excursion in Onfleet at end of shift.","Lower the cooler temperature and continue \\u2014 the product self-stabilizes."],"correct":1,"feedback":"Any reading outside the required range is an excursion \\u2014 regardless of duration. Operations within two minutes. Operations contacts the client within three minutes. The clinical decision belongs to the client, not the driver."},{"q":"Name the six federal regulatory standards that govern Corevian operations and the primary consequence of violating each.","opts":["HIPAA, DOT, OSHA, AABB, CAP, Joint Commission \\u2014 violations range from fines to criminal prosecution to loss of institutional accreditation","FDA, CDC, EPA, DEA, CMS, NIH \\u2014 violations result in fines only","OSHA, DOT, FDA, DEA, CDC, state health boards \\u2014 violations result in operating license suspension","JCAHO, CAP, AABB, state EMS, DOT, local health departments \\u2014 violations are administrative only"],"correct":0,"feedback":"HIPAA: $100\\u2013$50,000 per violation, criminal prosecution. UN3373/DOT: up to $16,000 per violation. OSHA: up to $15,625 per violation. AABB: loss of blood bank accreditation. CAP: loss of laboratory accreditation. Joint Commission: loss of hospital accreditation \\u2014 catastrophic for the facility."},{"q":"Why did Corevian build all SOPs and training programs before seeking its first client \\u2014 and why does this matter to a hospital procurement director?","opts":["It reduced startup costs by bundling vendor contracts","It allows Corevian to answer any compliance question immediately with existing documentation \\u2014 not promises about what will be built later","It was required by Louisiana state law","It was recommended by a business advisor as a marketing strategy"],"correct":1,"feedback":"Most courier businesses start with a vehicle and build operations as they go. Corevian started with infrastructure. When a hospital asks about HIPAA training records or chain-of-custody documentation \\u2014 Corevian produces it immediately. That is the competitive differentiator."},{"q":"You are entering a hospital for a delivery but forgot your ID badge today. What is the correct action?","opts":["Enter anyway \\u2014 the delivery is time-sensitive","Contact operations. Do not enter the clinical facility without your badge. Operations will advise on how to handle the delivery.","Ask a hospital staff member to vouch for you","Wear your personal ID as a substitute"],"correct":1,"feedback":"Your Corevian ID badge is your credential in every clinical facility. Entering without it violates professional appearance standards and may prevent facility access. Contact operations \\u2014 they determine how to handle the delivery."}]},{"id":2,"num":"02","title":"Corevian — The Company","sections":[{"h":"The Company — The Facts","body":""},{"h":"The Mission Statement","body":""},{"h":"The Why — Why This Company Exists","body":"Raquia built Corevian because she saw the failure clearly. Healthcare organizations — the institutions responsible for human health — were being forced to trust their most critical materials to services that operated without systems, without documentation, without training, and without accountability. The gap between what healthcare logistics should look like and what it actually looked like at the regional level was enormous. And no one was closing it.\n\nThe decision to build Corevian the right way — SOPs first, technology first, compliance first, then clients — was a deliberate choice that most courier businesses never make. They start with a vehicle and figure out operations as they go. Corevian started with infrastructure and then went to market. That decision is the reason we can walk into any hospital procurement meeting in New Orleans and answer any compliance question they have. That decision is our competitive advantage."},{"h":"Our Six Core Values — What They Actually Mean","body":"VALUE 1: RELIABILITY\n\nReliability means your clients never wonder if you are coming. It means the route runs whether the driver had a hard morning or not. It means when Corevian accepts an SLA target, performance is measured against that documented commitment. Reliability is not a personality trait. It is a system. Routes are pre-built in Onfleet. Drivers know their schedules in advance. Backup protocols exist for vehicle breakdowns and driver illness. Reliability is engineered, not hoped for.\n\nVALUE 2: COMPLIANCE\n\nCompliance means every run meets the legal and professional standard required by HIPAA, DOT, OSHA, and every accrediting body our clients answer to. It means BAAs are signed before first runs. It means drivers are trained before they are authorized. It means documentation is generated automatically and archived completely. Compliance is not bureaucracy. It is the foundation of trust. Our clients trust us with materials that affect patient health because we have proven we take compliance as seriously as they do.\n\nVALUE 3: ACCOUNTABILITY\n\nAccountability means that when something goes wrong — and in any logistics operation, something occasionally will — Corevian owns it immediately. We do not wait to be asked. We call the client within 5 minutes of knowing there is a problem. We write the incident report within 24 hours. We generate a CAPA. We tell the client what we changed so it never happens again. Accountability under pressure is what separates a company that survives from a company that clients leave.\n\nVALUE 4: PRECISION\n\nPrecision means every scan happens. Every temperature is logged. Every recipient signature is captured. Every delivery photo is taken. Nothing is assumed, estimated, or approximated. In healthcare logistics, precision is the difference between a completed run and a patient safety event. We are not precise because it makes our reports look good. We are precise because the materials we carry demand it.\n\nVALUE 5: PROFESSIONALISM\n\nProfessionalism means every Corevian representative — driver, manager, founder — shows up looking and acting like they belong in a clinical environment. Uniform clean and worn correctly. Company ID visible. Vehicle clean inside and out. Language professional at all times. Interactions with clinical staff handled with respect and efficiency. Interactions with patients handled with warmth and dignity. Professionalism is not a dress code. It is a statement about what kind of company we are.\n\nVALUE 6: GROWTH\n\nGrowth means we are building something. Not just revenue — infrastructure, relationships, reputation, and systems that allow Corevian to expand from New Orleans into a regional platform and eventually beyond. Every route we run perfectly, every client we report to monthly, every employee we train to the standard — all of it builds the foundation for growth. Every person on this team is not just doing a job. They are building a company."},{"h":"The Competitive Comparison — What We Actually Do vs The Alternatives","body":""},{"h":"Our Technology Stack — Every Platform and Why It Matters","body":"Our technology infrastructure was built before our first client. That is not normal in this industry. Most regional couriers use a personal phone and a spreadsheet. Corevian uses an integrated platform stack that generates automatic documentation, live client visibility, fleet safety monitoring, and monthly analytics — on every run."}],"questions":["State Corevian's legal entity name and explain why employees must represent the company consistently and accurately.","State the Corevian mission statement as taught in this unit.","Explain each of Corevian's six core values—Reliability, Compliance, Accountability, Precision, Professionalism, and Growth—in terms of observable employee behavior.","Explain the statement 'Reliability is engineered, not hoped for' using examples from the unit.","What does accountability require when an operational problem occurs, and what documentation expectations are described?","Give four examples of precision during a healthcare logistics run.","Describe the professional appearance and conduct expected of every Corevian representative in a clinical environment.","Explain why Corevian built SOPs, technology, compliance systems, and documentation infrastructure before going to market.","Name the technology platforms taught in this unit and state the specific role of each. In your answer, distinguish Samsara fleet safety/telematics from Onfleet dispatch/route workflow and LogTag temperature monitoring.","How many active Corevian SOPs are identified in v1.1, and what numbering range does the current controlled set use?"],"quiz":[{"q":"A hospital compliance officer asks how Corevian documents chain of custody. Give the complete accurate answer.","opts":["\"We use GPS tracking on all our drivers.\"","\"We track deliveries through a mobile app.\"","\"Every run generates seven GPS-verified scan events in Onfleet \\u2014 pickup scan, package photograph, cold chain activation, tamper seal record, facility arrival, delivery with recipient full name and title, and a GPS-embedded delivery photograph. Records are retrievable within 24 hours.\"","\"We have our drivers sign a delivery log at each stop.\""],"correct":2,"feedback":"The complete answer demonstrates specific knowledge of each COC event, the technology, and the retrieval standard. Vague answers like GPS tracking or a mobile app do not answer a compliance officer's question."},{"q":"What is the Corevian mission statement? Select the exact, complete version.","opts":["To deliver medical materials safely and on time to healthcare organizations across Louisiana.","To provide healthcare organizations with a transport partner they can trust \\u2014 with real systems, real documentation, real accountability, and the professional precision that patient care demands.","To be the most reliable and affordable healthcare courier in the New Orleans area.","To build a healthcare logistics company serving hospitals, labs, and pharmacies with HIPAA-compliant transport."],"correct":1,"feedback":"The complete mission statement must be memorized exactly. \"Real systems, real documentation, real accountability, and the professional precision that patient care demands\" is the specific language reflecting Corevian position in the market."},{"q":"Which core value is being demonstrated when a driver files an incident report for a temperature deviation the client never noticed?","opts":["Reliability \\u2014 because the driver showed up on time","Professionalism \\u2014 because the uniform was correct","Integrity \\u2014 doing the right thing when no one is watching","Accountability \\u2014 because the driver owned the problem"],"correct":2,"feedback":"Integrity: doing the right thing when no one is watching. The incident report is filed even when the client did not notice. This is what separates a Corevian driver from every other courier."},{"q":"What do WOSB and MBE certifications mean, and why do they matter to hospital procurement departments?","opts":["Women-Owned Small Business and Minority Business Enterprise \\u2014 they open supplier diversity procurement channels giving Corevian access to hospital contracts requiring diverse vendor spending","Worker Safety Board and Medical Bureau of Enforcement \\u2014 they confirm OSHA and medical transport compliance","Workplace Standards Board and Medical Business Enterprise \\u2014 Louisiana state certifications","Women's Operations Standards Board and Mobile Business Endorsement \\u2014 federal transport certifications"],"correct":0,"feedback":"Hospital systems often have supplier diversity spending mandates requiring a percentage of procurement from WOSB and MBE vendors. These certifications open contract pathways not available to competitors regardless of their service quality."},{"q":"A prospect says: \"I see you are a small company. We need a vendor who can handle high volume.\" What is the correct Corevian response?","opts":["\"We are growing quickly and will be large enough soon.\"","\"Volume is irrelevant \\u2014 what matters is whether our documentation can survive your next CAP audit. Can your current courier say the same?\"","\"We offer the lowest rates which compensates for our size.\"","\"We can handle any volume with our network of contractors.\""],"correct":1,"feedback":"Size is not the differentiator. Documentation, compliance, and operational infrastructure are. Redirect the conversation to what actually matters to a healthcare client's compliance decision."}]},{"id":3,"num":"03","title":"HIPAA & Patient Privacy","sections":[{"h":"What Is HIPAA and Why Does It Exist","body":"HIPAA — the Health Insurance Portability and Accountability Act — was signed into law in 1996. It exists because before HIPAA, there was no federal standard protecting patient health information. Insurance companies shared medical records with employers. Medical information was used to deny housing and employment. Patients had no right to control who saw their health data.\n\nHIPAA created a uniform national standard for protecting patient health information. The Privacy Rule (2003) and Security Rule (2005) established exactly what information is protected, who can access it, how it must be secured, and what happens when it is violated. HIPAA applies to covered entities and to business associates when they perform covered functions involving PHI. Corevian follows the privacy requirements applicable to the work it performs."},{"h":"What Is PHI — Protected Health Information","body":"PHI is any information that can identify a specific patient AND relates to their health, healthcare treatment, or payment for healthcare. The law defines 18 specific identifiers. If any one of these identifiers appears alongside health information, the entire record is PHI."},{"h":"The Business Associate Agreement (BAA)","body":"A Business Associate Agreement is a legally binding contract between a covered entity (the hospital, lab, or pharmacy) and a business associate (Corevian). When Corevian is acting as a business associate and a BAA is required, the applicable BAA must be executed before covered PHI services begin."},{"h":"HIPAA in Our Daily Operations — The Six Rules","body":""},{"h":"Violation Consequences","body":""}],"questions":["Define HIPAA and explain the purpose of the Privacy Rule and Security Rule as taught in this unit.","Define PHI. What two conditions make information PHI under the definition taught in this program?","Name all 18 PHI identifiers without looking at your notes.","Explain the minimum-necessary principle in your own words and give one field example of applying it.","Explain what a Business Associate Agreement (BAA) is and why it matters when Corevian handles PHI on behalf of a healthcare client.","Describe the safeguards a driver must use when handling labels, manifests, delivery information, devices, photographs, and conversations that could expose PHI.","State Corevian's internal 1-Hour Reporting Rule. What triggers it, who must be notified, and why must employees understand that it is a Corevian internal reporting standard rather than a federal one-hour statutory deadline?","Describe what you should do immediately if you believe PHI may have been exposed, viewed by an unauthorized person, photographed, lost, or sent to the wrong recipient.","Explain why an employee should report a suspected privacy event even when they are not certain a breach occurred.","Summarize the HIPAA penalty framework taught in this unit and explain why employees should never try to conceal or privately resolve a possible privacy incident."],"quiz":[{"q":"HIPAA was signed in what year, and what specific problem did it solve that did not exist before?","opts":["1989 \\u2014 it created the first federal medical insurance system","1996 \\u2014 it established a uniform national standard protecting patient health information after insurance companies freely shared medical records with employers and patients had no federal right to control their health data","2003 \\u2014 it responded to electronic health record security breaches","2009 \\u2014 it was part of the Affordable Care Act's privacy provisions"],"correct":1,"feedback":"HIPAA was signed in 1996. Before it: insurance companies shared medical records with employers, medical information was used to deny housing and employment, and patients had no federal right to control who saw their health data."},{"q":"A specimen label shows: patient name, date of birth, and medical record number. How many PHI identifiers are present?","opts":["One \\u2014 only the patient name is PHI","Two \\u2014 name and medical record number","Three \\u2014 name, date of birth, and medical record number \\u2014 all three are individual PHI identifiers","The barcode determines whether it is PHI"],"correct":2,"feedback":"Name (identifier 1), date of birth (identifier 3 \\u2014 all dates except year), and medical record number (identifier 8) are each PHI identifiers. Any one of them alongside health information makes the entire record PHI."},{"q":"You overhear a staff member mention a patient's name and condition in a reception area. You know this patient personally. What is your HIPAA obligation?","opts":["You may share this with the patient's family since they would want to know","Nothing \\u2014 you heard it as a bystander and have no HIPAA obligation","Do not discuss, use, or disclose the information. Incidental disclosure does not create permission to use or share PHI.","Tell the patient you heard the conversation so they are aware"],"correct":2,"feedback":"Corevian employees are bound by HIPAA in all settings during operational hours. Incidental overhearing does not create permission to use or share what was heard. Never discuss, use, or disclose PHI regardless of how it was encountered."},{"q":"What is required BEFORE Corevian can begin transporting specimens or PHI-adjacent materials for a new client?","opts":["A signed delivery manifest","A Certificate of Insurance sent to the client","A signed Business Associate Agreement \\u2014 no PHI-adjacent transport may begin without it, regardless of timeline pressure","HIPAA training completion for all drivers assigned to that account"],"correct":2,"feedback":"A signed BAA is required before any PHI-adjacent transport begins. No exceptions regardless of how urgent the request. Operating without a signed BAA is a HIPAA violation even if no breach occurs."},{"q":"You are transporting a prescription package. The label shows the patient's name, address, and medication name. A colleague asks what the medication is for. What do you do?","opts":["Share the information since the colleague has HIPAA training","Tell them the medication name but not the diagnosis","\"I don't discuss delivery contents \\u2014 that's PHI.\" Change the subject.","Show them the label briefly \\u2014 it's not a formal disclosure"],"correct":2,"feedback":"The medication name, patient name, and address are all PHI. \"That's PHI\" is the complete, correct response. This applies inside the vehicle, at the facility, and in every location."}]},{"id":4,"num":"04","title":"UN3373 & Biological Safety","sections":[{"h":"What UN3373 Is and Why It Exists","body":"UN3373 is the United Nations designation for 'Biological Substance, Category B' — the classification that covers the diagnostic specimens we transport every day. Category B covers specimens unlikely to cause permanent disability or life-threatening disease when exposure occurs — blood draws, urine cultures, biopsies, and the vast majority of clinical specimens we handle."},{"h":"The Triple Packaging System — In Complete Detail","body":"Every Category B biological specimen must be shipped in a three-layer packaging system. This is a federal DOT requirement."},{"h":"The 8-Point Pre-Acceptance Inspection — Do This Every Time","body":"Before accepting any biological specimen package, every Corevian driver performs this inspection. A driver who accepts a non-compliant package is legally and operationally responsible for everything that happens after."},{"h":"OSHA Bloodborne Pathogens — Protecting Yourself","body":"Where occupational exposure to blood or other potentially infectious materials is reasonably anticipated, applicable personnel operate under OSHA's Bloodborne Pathogens Standard (29 CFR 1910.1030) and Corevian exposure-control requirements. Universal Precautions means treating every biological specimen as if it contains a bloodborne pathogen — regardless of the label, regardless of the source, regardless of what you are told about the contents.\n\nPrimary Bloodborne Pathogens — The three primary bloodborne pathogens emphasized in Corevian training are HIV (human immunodeficiency virus), HBV (hepatitis B virus), and HCV (hepatitis C virus). Universal Precautions apply regardless of whether a specimen is known or suspected to contain any of these pathogens.\n\nThe Spill Response Protocol — Step by Step"}],"questions":["What does UN3373 identify, and what type of biological substance does it apply to?","Explain the difference between Category A and Category B biological substances and why a driver must never assume a Category A material can be handled as UN3373 Category B.","Describe the three layers of the Category B triple-packaging system and the purpose of each layer.","Walk through all eight points of Corevian's pre-acceptance inspection in the correct order.","What is the required response when any pre-acceptance inspection point fails?","Name the three primary bloodborne pathogens identified in this unit: HIV, HBV, and HCV.","Describe the PPE and exposure-prevention principles taught for biological materials.","Walk through the spill-response sequence from the moment a spill is recognized through documentation.","Which Corevian incident form must be used for spill documentation, and when is the full report required?","Scenario: you arrive for pickup and see exterior staining on a UN3373 package. Explain exactly what you do and what you do not do."],"quiz":[{"q":"A package passes Points 1, 2, 3, 4, 5, and 7 of the eight-point inspection but Point 6 shows the recipient address is listed as \"Main Lab\" with no street address. What do you do?","opts":["Accept \\u2014 the facility name is sufficient","Accept and call the client to confirm the address after pickup","Do not accept. Contact the client and state the specific failure: recipient address is incomplete. Document the rejection in Onfleet with reason and exact time.","Accept and verify the address with the facility on arrival"],"correct":2,"feedback":"All eight points must pass. Point 6 \\u2014 recipient name and facility address present and complete \\u2014 has failed. Do not accept. Document the specific failure. The client is responsible for repackaging to compliance."},{"q":"Explain the purpose of the absorbent material placed between the primary and secondary containers for liquid specimens.","opts":["It insulates the primary container to maintain temperature","It is required only for blood products, not routine specimens","Federal regulation requires it to contain the complete contents of the primary container if it fails \\u2014 preventing liquid from reaching and compromising the outer packaging","It stabilizes the primary container to prevent breakage"],"correct":2,"feedback":"The absorbent material must be sufficient to absorb the complete contents of the primary container if it fails completely. Without it, a failed primary creates a liquid hazard that can compromise the outer packaging and create an exposure risk."},{"q":"What does Universal Precautions mean in daily practice under OSHA's Bloodborne Pathogens Standard?","opts":["Extra protection only with confirmed infectious specimens","All specimens must be refrigerated to limit pathogen viability","Treat every biological specimen as potentially infectious regardless of label or described contents \\u2014 you cannot determine from a label whether a bloodborne pathogen is present","Workers must complete bloodborne pathogen training once and are then exempt"],"correct":2,"feedback":"Universal Precautions is the OSHA standard: every specimen, every time, regardless of what you are told about contents. A label saying \"routine chemistry\" does not mean the specimen cannot carry HIV, HBV, or HCV."},{"q":"You have completed the spill response protocol. What must happen before you can resume deliveries?","opts":["Resume immediately \\u2014 the decontamination is complete","Call the next client to let them know you will be delayed","Contact operations and do not resume deliveries until Raquia authorizes. Complete CHL-F-001 Incident Report within 24 hours.","Spray the area again with bleach solution and continue"],"correct":2,"feedback":"You do not authorize yourself to resume after a biological incident. Operations determines whether you continue. The Incident Report is completed within 24 hours \\u2014 not at end of week."},{"q":"What specific fact about Hepatitis B (HBV) makes it particularly dangerous in a transport context?","opts":["HBV is always fatal if exposure occurs","HBV has no treatment or vaccine","HBV can survive on surfaces for up to 7 days \\u2014 a contaminated cargo area not properly decontaminated remains an exposure risk for days","HBV is airborne and spreads without direct contact"],"correct":2,"feedback":"HBV survives on dry surfaces for up to 7 days. A cargo area not fully decontaminated after a spill remains an active exposure risk for days. This is why the full spill protocol \\u2014 approved EPA-registered disinfectant, label-specified contact time, complete disposal \\u2014 is not optional."},{"q":"During transport you notice one corner of the outer packaging has been crushed and is now soft. The package passed all eight inspection points at pickup. What do you do?","opts":["Continue delivery \\u2014 the inspection was passed at pickup and the client bears responsibility","Contact operations immediately \\u2014 document the damage with exact time in Onfleet and follow operations guidance for disposition","Reinspect and if nothing is leaking, continue delivery","Transfer the specimen to a backup cooler and continue"],"correct":1,"feedback":"Damage occurring during transport is an incident requiring immediate operations notification. Document in Onfleet with exact time. Do not make a unilateral decision to continue or abort the delivery."}]},{"id":5,"num":"05","title":"Cold Chain Mastery","sections":[{"h":"What Cold Chain Means and Why It Is Different","body":"Cold chain logistics refers to the transport of temperature-sensitive materials within a controlled temperature range from origin to destination without interruption. A pharmaceutical that leaves the pharmacy at the correct temperature but warms up in our vehicle has experienced a chain break. That break has consequences."},{"h":"Temperature Requirements for Every Product Type","body":"You must know these cold. No looking them up. When a client asks if you can handle their product, you answer immediately and correctly."},{"h":"Cold Chain Equipment","body":"LogTag TRED30-16R Data Logger\n\nThis is the NIST-traceable temperature data logger that goes inside every cold chain shipment. 'NIST-traceable' means the device's accuracy has been verified against the National Institute of Standards and Technology's measurement standards. Every logger comes with a calibration certificate."},{"h":"LogTag Operation — Step-by-Step, Every Time","body":"BEFORE THE RUN — Logger Preparation\n\nDURING THE RUN — Active Monitoring\n\nAFTER THE RUN — Documentation"},{"h":"Excursion Detection and Response","body":"An excursion is any temperature reading outside the target range. Every excursion is documented and reported. The product may or may not be usable after an excursion — that is the client's medical determination, not ours."}],"questions":["Define cold chain and explain what constitutes a chain break.","State the product temperature ranges taught in this unit for pRBCs, platelets, frozen blood products, and refrigerated pharmaceuticals.","Explain why the listed product ranges are hard operating ranges rather than 'tolerances.'","What does NIST-traceable mean in the context of temperature-monitoring equipment and calibration records?","Explain where alert thresholds are configured for the LogTag workflow and why drivers should not attempt to configure them ad hoc on the device in the field.","Walk through the v1.1 LogTag workflow from pre-run preparation and logger activation through transport, delivery, download, and report generation.","How should logging end under the v1.1 procedure, and why should a driver not assume every LogTag model requires a manual STOP-button step?","Define a temperature excursion and state the immediate response and escalation principles taught in this unit.","Explain what information must be preserved so a client can evaluate the integrity of a temperature-controlled run.","Scenario: a logger reading is outside the required product range. Explain what you do before anyone decides whether the product remains usable."],"quiz":[{"q":"You are transporting a specialty biologic at 2\\u00b0C to 8\\u00b0C. At Step 9 you see a reading of 8.4\\u00b0C. What has occurred and what do you do?","opts":["8.4\\u00b0C is within normal tolerance \\u2014 continue delivery","The reading is a sensor anomaly. Check again before reporting.","A temperature excursion has occurred. Notify Operations immediately. State: product type, current temperature, and how long visible. Do not continue delivery until operations advises.","Add more ice and continue \\u2014 the temperature will drop back into range"],"correct":2,"feedback":"8.4\\u00b0C exceeds the maximum of 8\\u00b0C. Even a 0.4\\u00b0C excursion is reportable. Operations within two minutes. The client makes the clinical disposition decision \\u2014 not the driver."},{"q":"A colleague places the LogTag against the ice pack because \"the temperature reads lower and makes the report look better.\" What is wrong with this and what should you do?","opts":["Nothing is wrong \\u2014 lower readings indicate better cold chain performance","The LogTag should be near the lid for easy access","This produces a fraudulent record \\u2014 the ice pack temperature is not the specimen temperature. The product may be outside range while the report shows compliant readings. Address directly and report to operations.","Acceptable if the client does not specifically request payload zone placement"],"correct":2,"feedback":"Payload zone placement is not a preference \\u2014 it is the standard. Placing the logger against the cooling source creates a false record that does not reflect product temperature. This is a compliance failure and a potentially fraudulent document."},{"q":"You are 40 minutes from delivery with Fresh Frozen Plasma (FFP) and notice the container temperature is now \\u221214\\u00b0C. The FFP appears visually intact. What do you do?","opts":["Continue \\u2014 visual integrity means the product is acceptable","Deliver quickly and note the temperature in Onfleet","Call operations immediately. \\u221214\\u00b0C means partial thawing may have occurred. Do not deliver without client authorization. Operations determines disposition.","Add dry ice if available and continue"],"correct":2,"feedback":"\\u221214\\u00b0C on FFP means the product has been above \\u221218\\u00b0C and partial thawing may have occurred. Even partial thaw degrades clotting factors. The product may appear intact visually while clinically compromised. Immediate operations notification required."},{"q":"What are LogTag Steps 6 and 7, and why does the order of these two specific steps matter?","opts":["Step 6 is sealing the cooler, Step 7 is placing the logger \\u2014 order matters because the seal traps the temperature","Step 6 is verifying pack-out temperature is within target range before loading specimens, Step 7 is placing the logger in the payload zone \\u2014 order matters because you confirm the starting temperature is correct before the logger begins recording","Step 6 is emailing the client, Step 7 is downloading the data","Step 6 is recording the serial number, Step 7 is pressing START"],"correct":1,"feedback":"Step 6: verify pack-out temperature is within range BEFORE loading. Step 7: place logger in payload zone. This sequence matters because if the starting temperature is outside range, you address it before the run begins \\u2014 not as an excursion mid-transport."},{"q":"Your operational responsibilities when transporting platelets are temperature (20\\u00b0C\\u201324\\u00b0C) and continuous agitation. The platelets were collected 6 days ago \\u2014 1 day from expiration. Who determines whether the unit is clinically usable?","opts":["The driver \\u2014 if temperature and agitation were maintained, the unit is acceptable","The blood bank that released them \\u2014 they already made the decision","The receiving transfusion service \\u2014 clinical determinations of usability belong to clinical personnel, not the driver","No one \\u2014 a unit with 1 day remaining is automatically acceptable if correctly transported"],"correct":2,"feedback":"Your operational responsibility is temperature and agitation \\u2014 both maintained correctly. The clinical determination of whether a unit with 1 day remaining on its shelf life is usable belongs to the receiving transfusion service. That determination is never made by the driver."}]},{"id":6,"num":"06","title":"Blood Products Handling","sections":[{"h":"Why Blood Products Are Different","body":"Every material we transport is important. Blood products are in a category of their own. A damaged blood unit cannot be replaced with a substitute product. Blood typing and compatibility matching mean the correct unit for a specific patient took time and clinical effort to identify.\n\nBlood product logistics is governed by AABB standards, FDA regulations under 21 CFR, and The Joint Commission accreditation requirements. When they trust Corevian with blood transport, they are trusting us with their accreditation."},{"h":"Blood Product Types — What Each One Is and Requires","body":"Packed Red Blood Cells (pRBCs)\n\nTemperature range: 1°C to 6°C — NEVER exceed 6°C\n\nTransport equipment: Validated blood transport box with pre-cooled 6°C phase-change materials\n\nDo NOT freeze — temperatures below 1°C cause hemolysis (destruction of red blood cells)\n\nDo NOT warm — temperatures above 6°C accelerate bacterial growth risk\n\nPlatelets\n\nTemperature range: 20°C to 24°C — refrigeration destroys platelets\n\nMUST be continuously agitated throughout transport — platelet agitator device required\n\nShelf life: Only 5–7 days from collection — among the shortest of all blood products\n\nDo NOT place in a cooler with ice or cold packs — cold temperatures are fatal to platelets\n\nFresh Frozen Plasma (FFP)\n\nTemperature: ≤ −18°C — must be completely frozen solid throughout transport\n\nTransport: Validated dry ice shipper — sufficient dry ice for the total transport duration\n\nDo NOT allow any thawing — even partial thaw degrades clotting factors\n\nCryoprecipitate\n\nTemperature: ≤ −18°C — completely frozen, identical to FFP protocol\n\nWhole Blood\n\nTemperature: 1°C to 6°C — same as pRBCs"},{"h":"AABB-Aligned Chain of Custody for Blood Products","body":"ISBT 128 Label Verification — At pickup, verify the ISBT 128 barcode/label identifiers on each blood component against the transport manifest or blood bank documentation before accepting custody. The driver verifies identifier match and label integrity; the driver does not interpret clinical compatibility. Any mismatch, unreadable label, or identifier discrepancy is escalated to the blood bank before transport begins."},{"h":"Blood Product Emergency Response — Tier 1 STAT Protocol","body":""}],"questions":["Explain why blood products require a higher level of transport control than ordinary materials.","State the required temperature ranges taught for pRBCs, platelets, fresh frozen plasma, and cryoprecipitate.","What is the lower limit for platelets in v1.1, and why is a reading below 20°C treated as outside the required 20–24°C range?","Explain the platelet agitation requirement taught in this unit.","Describe the blood-product pickup checks required before accepting custody.","What is ISBT 128, and what blood-product identification information should be verified against the transport documentation before departure?","Describe the chain-of-custody documentation required specifically for a blood-product run.","Explain what a driver should do when the product label, ISBT 128 identification, paperwork, destination, seal, or transport condition does not match.","Describe the Tier 1 STAT blood-product emergency protocol taught in the unit.","Scenario: a platelet shipment is reading 19°C. Explain why the driver must treat this as an excursion and what escalation steps follow."],"quiz":[{"q":"You arrive at the blood bank to pick up pRBCs for a Tier 1 STAT. One unit shows visible hemolysis \\u2014 the plasma appears pink. What do you do?","opts":["Accept both units \\u2014 hemolysis can be normal variation","Accept the non-hemolyzed unit and note the other in Onfleet","Do not accept the hemolyzed unit. Contact operations immediately. Document the specific issue in Onfleet. The blood bank determines whether a replacement is available.","Accept both and let the receiving transfusion service make the determination"],"correct":2,"feedback":"Visible hemolysis \\u2014 plasma appearing pink \\u2014 indicates red blood cell destruction and is a visible integrity failure. Do not accept. Document the specific finding. The blood bank determines whether the unit is usable or a replacement is available. Clinical determinations about blood product acceptability are never made by the driver."},{"q":"State the temperature requirements for all five blood product types. What makes platelets categorically different from every other blood product in terms of handling?","opts":["pRBCs: 1\\u20136\\u00b0C, Platelets: 2\\u20138\\u00b0C, FFP: \\u221218\\u00b0C or below, Cryo: \\u221218\\u00b0C or below, Granulocytes: 2\\u20138\\u00b0C. Platelets differ because they have the shortest shelf life.","pRBCs: 1\\u20136\\u00b0C, Platelets: 20\\u201324\\u00b0C with continuous agitation, FFP: \\u221218\\u00b0C or below frozen solid, Cryo: \\u221218\\u00b0C or below frozen solid, Granulocytes: 20\\u201324\\u00b0C no refrigeration. Platelets require BOTH a specific temperature AND continuous agitation simultaneously \\u2014 and must NEVER be refrigerated.","pRBCs: 2\\u20138\\u00b0C, Platelets: 20\\u201324\\u00b0C, FFP: \\u221220\\u00b0C, Cryo: \\u221220\\u00b0C, Granulocytes: room temperature. Platelets differ because they expire fastest.","pRBCs: 1\\u20136\\u00b0C, Platelets: 18\\u201322\\u00b0C with occasional agitation, FFP: \\u221215\\u00b0C or below, Cryo: \\u221215\\u00b0C or below, Granulocytes: 20\\u201324\\u00b0C."],"correct":1,"feedback":"Exact ranges: pRBCs 1\\u20136\\u00b0C. Platelets: 20\\u201324\\u00b0C AND continuous agitation AND never refrigerated. FFP: at or below \\u221218\\u00b0C completely frozen. Cryoprecipitate: at or below \\u221218\\u00b0C completely frozen. Granulocytes: 20\\u201324\\u00b0C, never refrigerated. Platelets are unique because they require two simultaneous conditions and refrigeration permanently destroys their function."},{"q":"A blood product STAT run is dispatched at 2:47 AM. What must occur by 2:52 AM?","opts":["The driver must arrive at the blood bank by 2:52 AM","The GPS tracking link must be sent to both the blood bank and the receiving facility \\u2014 within five minutes of dispatch","The driver must depart the blood bank by 2:52 AM","Operations must have confirmed delivery by 2:52 AM"],"correct":1,"feedback":"Within five minutes of dispatch \\u2014 by 2:52 AM \\u2014 the GPS tracking link is sent to both the sending blood bank and the receiving facility. Both parties need live visibility from the start of a life-critical transport."},{"q":"You have delivered pRBCs to the transfusion service. The nurse says \"Just leave them here\" and walks away before you collect her name, title, and signature. What do you do?","opts":["Leave the units \\u2014 the transfusion service received them and delivery is complete","Document delivery complete with a note that the nurse declined to sign","Do not leave without completing the COC record. Politely state: \"I need your name, title, and signature to complete the chain-of-custody record before I can leave.\" Wait for the authorized staff member.","Take a photograph of the units on the counter and close the delivery"],"correct":2,"feedback":"Blood products must be received by authorized transfusion service or nursing personnel with full name, job title, and digital signature. AABB standards require documented transfer of possession. An unrecorded transfer is an incomplete COC and an AABB compliance gap."},{"q":"Granulocytes are in a refrigerator at the blood bank because storage space was limited. What is the correct action?","opts":["Accept them \\u2014 refrigeration was brief and room temperature can be restored during transport","Accept them and note the storage deviation in Onfleet","Do not accept. Refrigeration destroys granulocyte viability. Contact operations immediately. The blood bank's clinical staff determine whether the units are still usable.","Accept and agitate continuously during transport to restore viability"],"correct":2,"feedback":"Granulocytes must never be refrigerated. Cold temperatures destroy their function. If they have been refrigerated at any point, they are potentially compromised. Contact operations. Clinical staff at the blood bank make the viability determination \\u2014 not the driver."}]},{"id":7,"num":"07","title":"Chain of Custody System","sections":[{"h":"Why Chain of Custody Matters at a Legal Level","body":"Chain of custody (COC) documentation exists because in healthcare logistics, we take possession of materials that belong to clinical organizations, contain patient information, and affect patient care outcomes. When we accept a specimen at pickup, we are accepting responsibility for it. The COC record is the legal documentation of that responsibility.\n\nIn a legal dispute — a missing specimen, a compromised medication, a contested delivery — the party with complete COC documentation wins. The party without it is in a very difficult position. This is why Corevian generates COC documentation on every run regardless of how routine the delivery appears."},{"h":"The 7 COC Documentation Events — In Complete Detail","body":""},{"h":"Digital Signature Capture — The Legal Standard","body":"Every Corevian delivery that goes to a clinical facility requires a digital recipient signature captured in Onfleet. The signature is timestamped, GPS-located, and permanently attached to the job record. It constitutes legal proof that a specific person, at a specific location, at a specific time, received the package."},{"h":"Tamper-Evident Seals — When and How","body":""},{"h":"24-Hour Record Retrieval","body":"Any client, operations team member, or auditor can request the complete COC record for any Corevian run — from today or from 12 months ago. That record is retrieved and delivered within 24 hours of the request. The complete package includes every scan event, all GPS data, all timestamps, driver identification, photographs, recipient signature, and temperature data for cold chain runs.\n\nRecord Retention — HIPAA-related chain-of-custody documentation is retained for six years from creation or the last effective date, whichever is later. Drivers must understand that complete, accurate COC records are not temporary delivery notes; they become part of Corevian's retained compliance record."}],"questions":["Explain the purpose of chain-of-custody documentation in healthcare logistics.","Name all seven Corevian COC documentation events in order and state what is captured at each event.","What information must be recorded when a cold-chain logger is activated?","What must be documented when a tamper seal is present?","Why must the delivery record identify the recipient by full name and job title and include the required signature?","What should a driver do if a tamper seal appears broken or inconsistent with the pickup record?","State the record-retention standard taught in v1.1 for COC records: six years from creation or the date last in effect, whichever is later.","Explain the difference between creating a complete COC record and simply proving that a vehicle arrived at a location.","Describe how PHI should be protected when taking required package or delivery photographs.","Scenario: a client requests documentation from an earlier run. What elements should make the COC record retrievable and defensible?"],"quiz":[{"q":"The recipient says \"I'm the supervisor here \\u2014 just put your name down for mine, it's fine.\" What do you do and why?","opts":["Accept \\u2014 they are the client and the supervisor's authorization is sufficient","Enter the supervisor's information since they authorized the deviation","Decline professionally: \"I need your full name and job title for the chain-of-custody record \\u2014 it's a federal documentation requirement.\" Collect the actual recipient's information only.","Close the delivery without a recipient name since they declined"],"correct":2,"feedback":"The COC record requires the actual recipient's full name and job title \\u2014 the person physically receiving the package. Substituting another person's name creates a falsified record, which is a legal document problem and potentially a HIPAA violation."},{"q":"You realize you forgot to take the delivery photograph \\u2014 COC Event 7 \\u2014 45 minutes after the delivery. What do you do?","opts":["Return to the facility and take a photograph of the delivery location","Take any photograph of the facility exterior and upload it retroactively","Contact operations immediately. Document the incomplete COC event in Onfleet notes with exact time of discovery. Operations will advise on whether a return is warranted and will document the gap.","Note it in Onfleet at end of shift"],"correct":2,"feedback":"A missing COC Event 7 is an incomplete chain-of-custody record \\u2014 an incident requiring operations notification. A retroactive photograph taken 45 minutes later cannot substitute for the required GPS-embedded delivery photograph. Operations documents the gap."},{"q":"What must the delivery photograph capture, and what must it never contain?","opts":["Must show the package barcode clearly. Must not contain the recipient's face.","Must show the delivery location \\u2014 door, desk, lab window, or receiving area \\u2014 with GPS-embedded metadata. Must not contain any PHI \\u2014 no patient names, MRNs, labels, or identifying documents in frame.","Must show the recipient holding the package. Must not contain other patients.","Must show the vehicle outside the facility. Must not contain other staff."],"correct":1,"feedback":"COC Event 7 documents where delivery occurred and is GPS-embedded for independent location verification. PHI in the photograph is a HIPAA violation. Every frame must be reviewed before capture."},{"q":"A client requests the complete COC record for a run that occurred 8 months ago. What must happen according to Corevian standard?","opts":["Records older than 6 months may not be available \\u2014 advise the client to check their own records","The record must be retrieved and delivered to the client within 24 hours","The request must go through a formal process with a 30-day turnaround","Records older than 90 days require a written request"],"correct":1,"feedback":"Any COC record within the prior 12 months must be retrievable within 24 hours of any request. 8 months is within the 12-month window. 24-hour retrieval is the standard, not a goal."},{"q":"COC Event 3 \\u2014 Cold Chain Activation \\u2014 is required on cold chain runs. What three specific pieces of information must be entered in Onfleet notes?","opts":["Client name, driver name, and delivery window","Package barcode, temperature reading at pickup, and delivery address","Logger serial number, pack-out temperature, and cooler ID","Timestamp, driver signature, and vehicle identification number"],"correct":2,"feedback":"Cold Chain Activation requires: logger serial number (identifies the specific logger whose data will produce the PDF), pack-out temperature (documents starting temperature), and cooler ID (identifies the specific equipment used). All three are required for a complete cold chain COC record."}]},{"id":8,"num":"08","title":"Onfleet Technology Certification","sections":[{"h":"What Onfleet Is and Why We Use It","body":"Onfleet is a professional delivery management platform used by healthcare, pharmaceutical, and logistics companies worldwide. Corevian uses Onfleet as the operational core of every run — dispatch, routing, scanning, documentation, and reporting all flow through this single platform. No other regional healthcare courier in our market uses a platform at this level."},{"h":"Driver App — Complete Feature Mastery","body":"LOGGING IN AND SETTING AVAILABILITY\n\nACCEPTING AND STARTING A TASK\n\nAT PICKUP — DOCUMENTATION SEQUENCE\n\nDURING TRANSPORT\n\nAT DELIVERY — COMPLETION SEQUENCE"},{"h":"Common Onfleet Scenarios","body":""}],"questions":["Explain Onfleet's role in Corevian operations and distinguish it from Samsara and LogTag.","Walk through the complete Onfleet driver workflow taught in Unit 8 from login and availability through final delivery completion.","What information must be verified before starting an assigned task?","Identify the documentation events that must be completed during the run rather than reconstructed afterward.","In v1.1, when is the recipient's signature collected in relation to marking the delivery complete?","What recipient information must accompany the signature?","What should you do if the recipient is unavailable or the delivery cannot be completed as instructed?","What is the correct procedure if the Onfleet app crashes or connectivity is lost during an active run?","Explain why speed, a STAT designation, or client pressure never eliminates required Onfleet/COC documentation.","Practical scenario: demonstrate the final delivery flow, including recipient verification, signature capture, required documentation, and only then marking the task complete."],"quiz":[{"q":"You log into Onfleet and your assigned task shows a pickup address but the task notes are empty \\u2014 no special instructions, no temperature requirements. What do you do?","opts":["Proceed to pickup \\u2014 empty notes are common and the driver is expected to use standard procedure","Contact operations before departing. Confirm whether this is a cold chain run, whether there are special instructions, and whether notes need to be updated. Do not assume standard protocol when a run is not fully documented.","Call the client facility directly to ask for instructions","Check your training manual for the default protocol for this client type"],"correct":1,"feedback":"An undocumented task creates operational risk. You need to know before arriving at pickup whether cold chain equipment is required and whether special handling applies. Contact operations before departing."},{"q":"You are collecting the digital signature at Step 14 and the recipient says they do not sign electronic devices for privacy reasons and hands the package back. Walk through exactly what you do.","opts":["Leave the package \\u2014 they accepted it verbally which satisfies the COC requirement","Take a photograph of the recipient as an alternative","Do not leave the package. Contact operations immediately with the exact situation, location, and time. Document in Onfleet: \"Recipient declined signature at [time]. Operations contacted.\" Follow operations guidance \\u2014 this is not a decision you make independently.","Ask a nearby staff member to sign instead"],"correct":2,"feedback":"You cannot complete the COC record without a signature from the authorized recipient. Contact operations immediately \\u2014 they determine whether the delivery proceeds or the package is returned. Do not improvise."},{"q":"Steps 6 and 12 in the 18-step sequence both involve marking \"Arrived.\" What is the difference between them?","opts":["Step 6 marks arrived at the pickup location before entering \\u2014 establishing GPS arrival time before inspection begins. Step 12 marks arrived at the delivery facility before entering \\u2014 establishing GPS arrival time at the delivery destination.","Step 6 marks arrived at pickup and Step 12 marks departed from pickup. The sequence matters for mileage tracking.","Steps 6 and 12 both mark delivery completion \\u2014 one for specimens and one for medications.","Steps 6 and 12 are the same step applied at different facilities."],"correct":0,"feedback":"Step 6: Mark Arrived at pickup BEFORE entering \\u2014 GPS timestamp preceding the inspection and scan events. Step 12: Mark Arrived at delivery facility upon arrival, before entering \\u2014 GPS timestamp of arrival at the delivery destination. Both timestamps are COC-significant."},{"q":"The Onfleet app crashes at Step 13 \\u2014 you have just handed the package to the recipient but have not collected the signature. The recipient is walking away. What do you do?","opts":["Let the recipient go \\u2014 reopen Onfleet and try to reconstruct the delivery","Ask the recipient to wait. Reopen Onfleet immediately. If not restored, call operations within 60 seconds. Collect the signature in Onfleet as soon as possible or on a paper COC form. Document the crash with exact time.","Complete the delivery without the signature since the app failure is not your fault","Take a photograph of the recipient as an alternative"],"correct":1,"feedback":"The recipient cannot leave without completing the COC record. Ask them to wait. Reopen the app. If not restored in 60 seconds, call operations and collect a paper signature. An app failure does not suspend the COC requirement."},{"q":"For STAT runs, at what point in the 18-step sequence is the GPS tracking link sent to the client, and what is the time requirement?","opts":["At Step 18 \\u2014 delivery confirmation \\u2014 so the client knows the run is complete","At Step 11 \\u2014 within five minutes of task acceptance \\u2014 so the client has live visibility from the start","At Step 12 \\u2014 arriving at the delivery facility \\u2014 so the client can prepare for receipt","After Step 7 \\u2014 marking pickup complete \\u2014 so tracking begins from the pickup location"],"correct":1,"feedback":"Step 11: send GPS tracking link to client within five minutes of task acceptance. For Tier 1 STAT, both the sending and receiving facilities receive the link. The client needs live visibility from the beginning of the run."}]},{"id":9,"num":"09","title":"Home Patient Delivery Protocol","sections":[{"h":"Why Home Patient Delivery Is Different","body":"When we deliver to a patient's home, we are entering someone's personal space during a time in their life when they are managing a health condition. They may be elderly. They may be in pain. They may be confused or anxious. The medication in our hands may be something they have been waiting for all day, or something they depend on for a chronic condition that has no cure."},{"h":"Preparation Before Arriving at the Patient's Home","body":""},{"h":"At the Door — The Complete Protocol","body":""},{"h":"Special Patient Situations","body":"Elderly Patients\n\nBe patient. You have time. Rushing an elderly patient is one of the most visible unprofessional behaviors that gets reported back to pharmacies.\n\nPatients Who Appear Confused or Disoriented\n\nIf a patient comes to the door and appears significantly confused or disoriented, do NOT just hand over the medication. Call operations. The pharmacy needs to know.\n\nChildren Answering the Door\n\nIf a child answers the door with no adult present, do NOT leave any medication. Say: 'I need to speak with an adult — is your mom or dad home?' Follow the undeliverable protocol and contact operations.\n\nPatient Appears to Be in Medical Distress\n\nIf a patient appears to be in medical distress — significant pain, difficulty breathing, confusion suggesting a medical emergency — call 911 if the situation is serious. Stay with the person if it is safe to do so. Notify operations."}],"questions":["Explain why home-patient delivery requires a different communication and privacy standard from a facility-to-facility delivery.","Describe the preparation required before arriving at a patient's home.","State the introduction and identity-verification process taught in this unit.","What should you do if the person at the door cannot complete the required identity verification?","What should you do if a child answers the door and no authorized adult is available?","What is the correct response if a patient appears confused, disoriented, distressed, or otherwise unable to safely complete the normal handoff?","How should you respond when a patient asks what a medication is for, how much to take, or another clinical question?","How should you respond when a patient asks whether the medication should be refrigerated or otherwise stored in a particular way? Refer them to the pharmacy-provided storage instructions or the dispensing pharmacy rather than giving independent clinical/storage advice.","Explain how dignity, privacy, and professionalism should be maintained at a patient's home.","Scenario: the patient asks you to leave the medication at the door because they cannot come outside. Explain how you determine whether you may do so under the delivery instructions and when Operations must be contacted."],"quiz":[{"q":"An elderly man answers the door and can verify his name but is uncertain about his date of birth. What do you do?","opts":["Accept name verification alone and proceed since he appears to be the correct person","Leave the medication at the door since he answered and appears to live there","Contact the pharmacy while remaining at the door. Explain that you are with the patient and need guidance on completing identity verification. Do not transfer the medication until the pharmacy advises.","Ask a neighbor to assist with identification"],"correct":2,"feedback":"Identity verification requires name AND date of birth. Partial verification is not verification. Contact the pharmacy \\u2014 they have the patient's record and can advise. The patient is present; this is not an undeliverable situation."},{"q":"The Corevian door introduction uses only the patient's first name \\u2014 not the full name. Why?","opts":["It is friendlier and more personal to use only the first name","Using the full name could reveal the patient's identity to anyone within earshot \\u2014 which is a PHI disclosure in a public space","First names are required by the delivery app","Using the last name requires additional written authorization"],"correct":1,"feedback":"Saying the patient's full name at a front door can be heard by neighbors and passersby. Saying the full name alongside a health-related delivery context discloses PHI in a public space. First name only is the HIPAA-conscious standard."},{"q":"A child who appears to be about 10 years old answers the door and says \"My mom is sleeping, just leave it.\" What is your complete response?","opts":["Leave the medication with a note since the child is old enough to understand","\"I'll leave it at the door\" \\u2014 then photograph and close the delivery","\"I need to speak with an adult before I can leave this. Can you wake your mom or is there another adult home?\" Wait. If no adult comes, do not leave the medication. Follow the undeliverable protocol.","Give the medication to the child with simple storage instructions"],"correct":2,"feedback":"Medication is never left with a minor. The child cannot verify identity, cannot consent to receive medication, and cannot be accountable for storage. Ask for an adult. If no adult is available, the package is not left. Contact the pharmacy and follow the undeliverable protocol."},{"q":"Walking back to your vehicle, the patient calls after you: \"Can you tell me if it's okay to take this with my blood pressure medication?\" What is your response?","opts":["Answer based on what you know \\u2014 you handle this medication regularly","\"That's a great question for your pharmacist \\u2014 they have your complete medication list and can check for interactions. Their number should be on the label.\"","Tell them you do not know and walk away","Call the pharmacy on their behalf"],"correct":1,"feedback":"No clinical information is provided by Corevian employees. Drug interactions require a pharmacist's review of the complete medication list. Redirect warmly and specifically: pharmacist, number on label."},{"q":"A patient asks: \"Is this the right dose? The bottle looks different than last time.\" What do you do?","opts":["Look at the label and confirm whether the dose matches what is printed","\"You should call your pharmacy \\u2014 if something looks different than you expected, they will want to know about it. Their number is on the label.\" Do not inspect the medication or offer any assessment.","Open the package to show them the medication looks correct","Tell them it is probably fine and that packaging changes happen"],"correct":1,"feedback":"A patient noticing a difference from what they expected is a situation for the pharmacy \\u2014 not the driver. You do not inspect, assess, compare, or reassure about medication content. Redirect to the pharmacy warmly and specifically."}]},{"id":10,"num":"10","title":"Professional Communication","sections":[{"h":"The Corevian Communication Standard","body":"Every interaction — phone call, email, in-person conversation, text message — is a representation of Corevian. Professional communication is not a personality — it is a skill that can be learned and practiced."},{"h":"Phone Calls — The Standard Answer","body":""},{"h":"Email Communication","body":""},{"h":"Talking to Hospital and Lab Decision-Makers","body":"When speaking to a hospital supply chain director, a laboratory director, or a pharmacy director, you are speaking to someone who has been doing their job for years and has been disappointed by logistics vendors before. They have heard promises. What they respond to is specificity, expertise, and the ability to name the specific problem they have been living with — then answer it with proof.\n\nWhat NOT to Say\n\n'We are the best courier in New Orleans' — This is a claim. They have heard it before. Don't lead with it.\n\n'We're HIPAA compliant' without being able to explain what that means in practice\n\n'I'll find out and get back to you' on basic compliance or service questions — you should know the answers\n\n'We're small but we work hard' — undersells the company. Size is irrelevant. Standard is everything.\n\n'We can handle that' without naming the specific documentation, temperature standard, or protocol that backs it up\n\nWhat TO Say — Name the Problem First, Then the Answer\n\nThe difference between a capability statement and a problem-solver statement is whether the other person feels like you understand what they have been dealing with. Lead with the problem their environment faces. Then answer it with a specific.\n\n'The documentation gap most couriers leave in your audit file — our chain-of-custody eliminates it. Seven documented chain-of-custody events on every run — GPS-verified, timestamped, and retrievable within 24 hours of any client or audit request. CAP and JCAHO auditors ask for those records. We produce them automatically.'\n\n'The temperature question that hangs over every cold chain run — our LogTag PDF answers it. Readings every 5 minutes from pack-out to delivery, emailed to your team the same day.'\n\n'The STAT call that goes to voicemail at 2am — that is the problem our STAT response system is built to solve. A live person answers, every time, including on holidays. Our Tier 1 target is 15 minutes from request to driver dispatched.'\n\n'The vendor credentialing stack that takes weeks at other companies — we have a BAA ready to execute. COI, W-9, HIPAA policy, COC SOP, and capability statement are ready before this conversation ends.'\n\n'Corevian is actively pursuing WOSB and MBE certification, which will directly support your supplier diversity requirements as our certifications are confirmed.'\n\n'We provide monthly KPI reports to every contracted client — on-time rate, temperature compliance, incident log, documentation rate.'"},{"h":"Handling a Client Complaint — The Recovery Script","body":""},{"h":"Communicating Internally","body":"All Operations-approved communication system messages about active runs should include run ID, client name, and status\n\nIncident reports go to the Operations-approved communication system incident channel with a subject, time, and brief description within 5 minutes — full report in 24 hours\n\nSTAT escalations are announced in the STAT channel the moment they are received\n\nPerformance feedback to drivers is specific and documented"}],"questions":["Explain the Corevian communication standard and why professional communication is treated as an operational skill.","State the standard Corevian phone greeting taught in this unit.","What is the maximum client-email response time taught in v1.1?","When speaking with a hospital, laboratory, pharmacy, or other decision-maker, why should the employee lead with the operational problem and then explain the specific Corevian control?","Name four statements or approaches the unit says should not be used with healthcare decision-makers and explain why.","Using the cold-chain example in the unit, explain how to answer a client's documentation question with specific operational evidence rather than a generic claim.","Walk through the five-step complaint-recovery process taught in this unit.","What information should be included in internal communications about an active run?","What is the internal communication expectation when an incident or STAT escalation occurs?","Scenario: a client reports that a specimen was delivered to the wrong location. Demonstrate the communication response without speculating, assigning blame, or making unsupported promises."],"quiz":[{"q":"A hospital supply chain director says: \"We already have a courier. Why would we switch to Corevian?\" Give the complete problem-first answer.","opts":["\"Corevian offers competitive rates and same-day service.\"","\"We are smaller which means more personalized service.\"","\"The documentation gap your current courier likely leaves in your audit file. When a CAP inspector asks your lab director for chain-of-custody records from 90 days ago, can your current courier produce GPS-verified timestamps, the delivery recipient's name and title, and a temperature PDF \\u2014 all within 24 hours? Corevian can. Every run. Without a special request.\"","\"We have better technology and more experienced drivers.\""],"correct":2,"feedback":"Lead with the problem \\u2014 not a Corevian feature. Name the specific gap: audit documentation. Then answer with specific proof: seven COC scan events, GPS-verified, 24-hour retrieval, temperature PDF same day."},{"q":"A client calls frustrated: \"Your driver was 22 minutes late and my morning processing run is delayed.\" Walk through the complete 5-step complaint resolution protocol.","opts":["Step 1: Apologize. Step 2: Explain the traffic situation. Step 3: Offer a discount. Step 4: Promise it won't happen again. Step 5: Send a follow-up email.","Step 1: Listen without interrupting. Step 2: \"I completely understand why that is a concern, and I want to address it directly.\" Step 3: \"That should not have happened. I am sorry it did.\" Step 4: \"I am pulling the run record now. I will call you back within two hours with a complete account of what occurred and what we are changing.\" Step 5: Follow through within the committed timeframe.","Step 1: Pull the Onfleet record before responding. Step 2: Present the evidence. Step 3: Determine fault. Step 4: Offer resolution. Step 5: Issue a credit.","Step 1: Acknowledge. Step 2: Ask for a written complaint. Step 3: Review internally. Step 4: Respond within 5 days. Step 5: Document resolution."],"correct":1,"feedback":"The 5-step protocol: (1) listen completely without interrupting. (2) Acknowledge without qualification \\u2014 no \"but,\" no traffic. (3) Take responsibility. (4) Commit to a specific action with a specific timeframe. (5) Follow through within that timeframe."},{"q":"What are the four things you must NEVER say to a hospital or laboratory decision-maker, and what is specifically wrong with each?","opts":["\"We are cheap,\" \"we are fast,\" \"we are local,\" and \"we are small\" \\u2014 all four undermine the compliance message","\"We are still small but growing,\" \"we are working on getting that certification,\" \"our prices are competitive,\" and \"I will check and get back to you\" (without a specific time) \\u2014 each signals a gap in readiness, standards, value, or professionalism","\"We don't serve all areas yet,\" \"we've only been operating for one year,\" \"our technology is new,\" and \"we have a small team\" \\u2014 all four are size-related weaknesses","\"We can't match hospital rates,\" \"we are a startup,\" \"we are locally owned,\" and \"we use independent contractors\" \\u2014 all four reduce confidence"],"correct":1,"feedback":"\"Still small but growing\" \\u2014 signals not ready for their volume. \"Working on getting that certification\" \\u2014 signals current non-compliance. \"Our prices are competitive\" \\u2014 positions Corevian as a commodity. \"I'll check and get back to you\" without a time \\u2014 signals disorganization. Each costs credibility."},{"q":"A clinical staff member approaches you and asks: \"Was that the Smithson family's oncology medication you just delivered to 4B?\" How do you respond?","opts":["Confirm or deny to maintain a professional relationship","\\\"I'm not in a position to discuss delivery contents or recipients. For questions about specific shipments, the sending pharmacy would be the right contact.\\\" Close the conversation professionally.","Confirm since they are clinical staff with patient access","Say you are unsure and walk away"],"correct":1,"feedback":"Confirming delivery contents, patient names, or delivery locations to anyone \\u2014 including clinical staff \\u2014 is a PHI disclosure. Clinical staff at the receiving facility do not automatically have rights to information about materials transported between other facilities."},{"q":"You receive a call at 11:43 PM about a delivery at 2:15 PM. The client believes abnormal results may indicate a transport issue. What do you do?","opts":["Explain that you are not available for after-hours calls","Pull the Onfleet record while on the call and provide GPS timestamps, temperature data, and delivery confirmation. Offer to email the complete COC record within 24 hours. Escalate to Raquia if the client believes an incident occurred.","Tell them the delivery was completed correctly and they should look elsewhere","Ask them to submit the concern through your formal complaint channel"],"correct":1,"feedback":"24/7/365 operations means this call is fully within operational hours. Pull the record immediately. Provide specific data. Offer the complete COC record within 24 hours. Escalate to Raquia if there is any possibility of an incident."}]},{"id":11,"num":"11","title":"STAT & Incident Response","sections":[{"h":"The STAT Dispatch System","body":"STAT is a medical abbreviation from the Latin 'statim' — meaning immediately. Corevian provides 24/7/365 STAT coverage with no holiday exceptions and no after-hours voicemail. When a clinical organization has a STAT logistics need at 2am, they reach a live person."},{"h":"The STAT Response Sequence — Every Step","body":""},{"h":"Incident Types and Triggers","body":""},{"h":"The Escalation Sequence — Every Incident","body":""}],"questions":["Define STAT as taught in the program and explain why STAT work still requires complete documentation.","Name the three STAT tiers taught in Unit 11 and explain how urgency changes the operational response.","State the Tier 1 dispatch target and the tracking-link timing taught in this unit.","Walk through all eight steps of the STAT response sequence in order.","Name the incident types/triggers taught in the unit that require escalation.","Walk through all seven steps of the incident-escalation sequence in order.","What is the hard internal rule for how quickly a driver must notify Operations after recognizing an incident?","Which Corevian form is used for the full incident report, and when must that report be completed?","How often are client updates provided during an unresolved incident under the sequence taught in the program?","Scenario: a Tier 1 STAT run experiences a delay. Explain how you preserve safety, COC documentation, Operations escalation, and proactive client communication at the same time."],"quiz":[{"q":"A Tier 1 STAT for a blood product is dispatched at 11:14 PM. Walk through the complete 8-step STAT response sequence with specific time targets.","opts":["Step 1: acknowledge within 3 min. Step 2: dispatch within 5 min. Step 3: send GPS link within 5 min of dispatch. Step 4: verify temperature equipment. Step 5: complete pre-acceptance inspection at pickup. Step 6: drive direct, no other stops. Step 7: deliver to authorized personnel, full name and title. Step 8: post-run notification within 10 min of delivery.","Step 1: receive and log. Step 2: call driver. Step 3: driver departs within 30 min. Step 4: send GPS link at arrival. Step 5: inspect. Step 6: deliver. Step 7: signature. Step 8: close task.","Step 1: receive. Step 2: acknowledge within 15 min. Step 3: dispatch within 30 min. Step 4: track. Step 5: deliver. Step 6: confirm. Step 7: notify. Step 8: report.","Step 1: receive. Step 2: call driver. Step 3: driver picks up when available. Step 4: deliver directly. Step 5: signature. Step 6: close task. Steps 7-8: not required for STAT runs."],"correct":0,"feedback":"Complete sequence: (1) Acknowledge to client within 3 min. (2) Dispatch nearest qualified driver within 5 min. (3) GPS link sent to both sending and receiving facility within 5 min of dispatch. (4) Verify temperature equipment. (5) Full 8-point pre-acceptance at pickup \\u2014 urgency does not waive inspection. (6) Direct route, no stops. (7) Authorized personnel only, full name and title. (8) Post-run notification within 10 min of confirmed delivery."},{"q":"You discover a flat tire at 2:27 PM with a time-sensitive specimen that has a 3:00 PM processing cutoff. Walk through the incident escalation protocol with exact timing.","opts":["Try to change the tire quickly. If that fails, call operations.","Call a rideshare to transfer the specimen independently.","Document the incident in Onfleet with the exact time: 2:27 PM. Call operations within five minutes \\u2014 by 2:32 PM. State: location, materials, cutoff time, vehicle condition. Operations contacts the client and coordinates backup transport. Do not make independent decisions about specimen transfer.","Wait for a passing motorist. Call operations if no help arrives within 10 minutes."],"correct":2,"feedback":"Step 1: document exact time. Step 2: call operations within five minutes \\u2014 2:32 PM. State all relevant facts including the 3:00 PM cutoff. Operations contacts the client and coordinates the response. The driver does not independently transfer specimens to another vehicle \\u2014 that requires operations authorization and COC documentation."},{"q":"What specifically is a CAPA and what does sending one to the affected client accomplish that a verbal apology does not?","opts":["CAPA stands for Client Acknowledgment and Performance Assessment \\u2014 it documents the client's satisfaction","CAPA stands for Corrective and Preventive Action \\u2014 it documents what caused the incident and what structural change prevents recurrence. Sending it provides evidence of systemic improvement \\u2014 not a promise, not an apology. A client who sees a documented process change has evidence the system was fixed.","CAPA is a legal document that limits Corevian liability for future incidents","CAPA is a formal complaint form submitted to regulatory bodies"],"correct":1,"feedback":"A CAPA gives the client evidence \\u2014 not assurance. It names the root cause and documents the specific operational change made to prevent recurrence. This is the difference between a company that apologizes and a company that demonstrates it fixed the problem."},{"q":"Under what three circumstances must you contact operations immediately \\u2014 not at end of shift, immediately?","opts":["When running more than 5 minutes late, when a package is heavier than expected, and when the client is not present","When you detect or suspect a temperature excursion, when any incident occurs, and when a recipient is unavailable and the undeliverable protocol is triggered","When your phone battery drops below 30%, when traffic delays are anticipated, and when a client requests a route change","When a package fails inspection, when you complete a delivery, and when you begin your shift"],"correct":1,"feedback":"Three immediate contact triggers: (1) temperature excursion detected or suspected \\u2014 within two minutes. (2) Any incident \\u2014 vehicle breakdown, biological spill, delivery failure, tamper seal breach, accident \\u2014 within five minutes. (3) Recipient unavailable and undeliverable protocol triggered. All three require operations contact before any independent action."},{"q":"A client calls at 8:15 AM reporting a delivery you completed yesterday appears to have gone to the wrong location. Your Onfleet record shows the correct address was scanned and the delivery photograph confirms the correct facility. What do you do?","opts":["Tell the client the records show delivery was correct and they should check internally","Contact operations immediately with the specific details: client name, run date, delivery time, and what the Onfleet record shows. Do not resolve the dispute independently \\u2014 operations reviews the complete COC record and coordinates with the client.","Pull the record and send it to the client directly with your explanation","Escalate to Raquia only if the client escalates further"],"correct":1,"feedback":"Contact operations immediately \\u2014 this is potentially an incident even if records appear correct. Operations reviews the complete COC record including GPS coordinates, delivery photograph metadata, and recipient information. Client disputes are resolved through the documented record, not driver-to-client conversation."}]},{"id":12,"num":"12","title":"Daily Operations & Dispatch","sections":[{"h":"The Daily Driver Pre-Shift Checklist","body":"Vehicle Check\n\nVehicle exterior is clean — no visible dirt, grime, or damage not previously documented\n\nVehicle interior cargo area is clean and clear — no food, personal items, or debris\n\nSamsara dashcam is powered on and recording — confirm green indicator light\n\nFuel level is sufficient for the day's routes — never begin a route below quarter tank\n\nVehicle temperature control (heat/AC) is functioning — critical for cold chain management\n\nEquipment Check\n\nValidated coolers are present — clean, dry, no visible damage or odor\n\nSpill kit is complete: nitrile gloves (multiple pairs), face shield, absorbent pads, biohazard bags, approved EPA-registered disinfectant solution or equivalent disinfectant, absorbent powder\n\nLogTag loggers: sufficient quantity for day's cold chain runs, batteries verified, calibration dates current\n\nPhase-change materials or ice packs pre-cooled to target temperature if cold chain runs are scheduled\n\nDry ice is available if frozen transport is scheduled — check quantity against run duration\n\nTechnology Check\n\nOnfleet app is open, logged in, and routes are visible\n\nPhone is fully charged — minimum 80% at shift start, car charger available\n\nClient contact numbers are accessible for all scheduled clients\n\nProfessional Appearance\n\nFull uniform — company shirt, appropriate pants, clean and presentable\n\nCompany ID badge is attached and visible on chest\n\nPersonal appearance is professional — appropriate for clinical facility access"},{"h":"Route Management","body":""},{"h":"Monthly KPI Reporting","body":"Every contracted client receives a monthly KPI report. This is one of the most powerful differentiators in our market. The report goes out by the 5th of the following month, every month, without exception."}],"questions":["Walk through the complete pre-shift vehicle check taught in Unit 12.","Walk through the complete equipment check, including validated coolers, spill kit, LogTag loggers, phase-change materials/ice packs, and dry ice when applicable.","Walk through the technology check and explain the different operational purpose of Onfleet and Samsara.","Describe the professional-appearance requirements, including uniform and visible company ID badge.","What fuel-level standard is taught for the start of a route?","Who has authority to change a contracted route sequence during an active shift, and what should a driver do when an unplanned change appears necessary?","Explain the end-of-shift/documentation responsibilities taught in the daily operations section.","Describe the monthly KPI report taught in this unit and the purpose of its seven sections.","By what date does the unit state the monthly KPI report is delivered to contracted clients?","Which Corevian incident form number must appear in operational/KPI incident documentation?","Using the Field Reference Cards, explain when you would use the pre-acceptance, temperature, COC, incident, spill, STAT, and HIPAA cards.","Scenario: before shift you discover an expired logger calibration and an incomplete spill kit. Explain why you should not simply begin the route and what must happen next."],"quiz":[{"q":"Walk through the complete daily pre-shift checklist in the correct order \\u2014 every item.","opts":["Fuel check, phone charge, uniform, badge, Onfleet login","Vehicle exterior, cargo area, spill kit, LogTag loggers, coolers, Onfleet, phone charge, uniform, ID badge \\u2014 in that order, every shift, without abbreviation","Phone charge, Onfleet login, pick up route, vehicle check at first stop","Badge, uniform, phone, fuel \\u2014 standard morning check"],"correct":1,"feedback":"Complete checklist in order: Vehicle exterior (damage, signage, tires). Cargo area (clean, clear, no personal items). Spill kit (complete \\u2014 gloves, face shield, pads, biohazard bags, bleach). LogTag loggers (charged above 80%, calibration current). Coolers (clean, pre-conditioned if cold chain scheduled). Onfleet (open, logged in, route reviewed). Phone (charged to 80% minimum, car charger present). Professional appearance (full uniform, ID badge visible)."},{"q":"A client on your contracted route contacts you directly at 9:22 AM and asks you to add a pickup from a new location. The pickup would add 18 minutes to your route. What do you do?","opts":["Add the stop \\u2014 it's an 18-minute addition and the client is making a reasonable request","Add it and document it in Onfleet so operations knows after the fact","Contact operations with the client's request. Route modifications require operations authorization regardless of additional time involved. Operations determines whether the modification fits the contracted schedule and whether other client SLAs are affected.","Tell the client no and proceed with the contracted route"],"correct":2,"feedback":"Route modifications require operations authorization \\u2014 not driver judgment. The contracted route sequence protects every other client on the route. Operations determines whether the addition affects downstream pickup windows and SLA commitments before authorizing any change."},{"q":"The monthly KPI report shows an on-time delivery rate of 94.2% against a contracted SLA of 98%. What must the report include and when must it be delivered?","opts":["The report is sent as-is \\u2014 the client can see the number and draw their own conclusions","94.2% is close enough to 98% that no additional action is required","The report must include the on-time data, an open corrective action identifying the runs that fell outside the SLA window, and a CAPA describing what operational change will bring performance back to 98%. The report must be delivered by the 5th.","Wait until performance improves before sending the report"],"correct":2,"feedback":"Transparency is the standard \\u2014 not reporting only good months. The KPI report includes the data AND the open corrective action when performance falls below SLA. This is delivered by the 5th regardless of what the data shows. Clients who receive this level of reporting become long-term partners."},{"q":"What is the specific consequence to a laboratory client when Corevian misses the laboratory processing cutoff?","opts":["The laboratory must reorder specimen collection supplies","The laboratory processes specimens on the next available run with no meaningful impact","The patient's results are delayed until the next processing window \\u2014 which may be 24 hours. For a patient awaiting time-sensitive results such as a cardiac enzyme or culture, this delay has direct clinical significance.","The laboratory submits a complaint to Corevian operations team"],"correct":2,"feedback":"Missing the processing cutoff means the patient's results are delayed until the next window \\u2014 often 24 hours. A patient waiting on a cardiac enzyme for a potential myocardial infarction experiences that delay directly. This is why pickup time windows are clinically \\u2014 not just contractually \\u2014 significant."},{"q":"Name all seven sections of the monthly KPI report and the specific data source for each.","opts":["Section 1: delivery count (Onfleet). Section 2: on-time rate (Onfleet vs SLA windows). Section 3: STAT response time (Onfleet). Section 4: temperature compliance (LogTag excursion log). Section 5: incidents (CHL-F-001 log). Section 6: COC completion rate (Onfleet). Section 7: note from Raquia Compass.","Section 1: revenue. Section 2: run count. Section 3: driver performance. Section 4: vehicle mileage. Section 5: fuel costs. Section 6: client satisfaction. Section 7: operations notes.","Section 1: delivery count. Section 2: complaints received. Section 3: incidents. Section 4: response times. Section 5: temperature logs. Section 6: billing summary. Section 7: summary.","Sections 1\\u20137: all generated from Onfleet monthly export."],"correct":0,"feedback":"Each section has a specific source: (1) Total runs \\u2014 Onfleet export. (2) On-time rate \\u2014 Onfleet vs contracted windows. (3) STAT response time \\u2014 Onfleet task creation to driver arrival. (4) Temperature compliance \\u2014 LogTag excursion log. (5) Incidents \\u2014 CHL-F-001 log. (6) COC completion rate \\u2014 Onfleet task completion records. (7) Personal note from Raquia."}]}];

const LU_UNITS = [{"id":13,"num":"13","role":"leadership","title":"Sales & Client Relations","subtitle":"Who to call, how to pitch, how to handle objections, vendor credentialing, proposals, and client onboarding.","quote":"Lead with their problem. Answer it with your proof.","sections":[{"h":"13.1 Who to Call and What They Care About","body":"Every opening line should name the specific problem that organization faces before offering the Corevian answer. A supply chain director who has been burned by a courier who could not produce COC documentation recognizes immediately that you understand their reality.\n\nHospital systems: reach the Supply Chain Director or VP Operations. Primary concern: compliance docs, vendor credentialing, supplier diversity, KPI reporting. Open with the documentation gap that shows up in audits.\n\nReference labs: reach the Laboratory Director or Operations Manager. Primary concern: specimen cutoff times, COC documentation, CAP compliance. Open with the CAP inspection gap.\n\nSpecialty pharmacies: reach the Director of Pharmacy or Operations Director. Primary concern: HIPAA, patient-facing professionalism, cold chain reliability. Open with the two problems specialty pharmacies name most.\n\nBlood banks: reach the Blood Bank Director or Medical Director. Primary concern: AABB compliance, temperature validation, emergency response. Open with the temperature deviation documentation problem.\n\nSurgery centers: reach the Administrator or OR Manager. Primary concern: on-time delivery linked to OR schedule, reverse logistics. Open with the surgical tray delay problem."},{"h":"13.2 The Full Corevian Pitch","body":"Practice this until you can deliver it naturally and conversationally. It should not sound like a script. It should sound like an expert who understands exactly what their client has been dealing with and has already built the answer.\n\nTHE PITCH: 'The healthcare organizations we work with all had the same experience before us — a courier who disappeared in an audit, a temperature excursion nobody documented, a STAT call that went to voicemail at 2am. Those are not transportation failures. They are documentation failures, accountability failures, and communication failures. Corevian was built to eliminate all three. Every run generates a complete chain-of-custody record with seven GPS-verified documentation events and recipient signature — the records CAP and JCAHO auditors ask for, produced automatically. Every cold chain run gets an automated temperature log PDF emailed to your team the same day. Every contracted client gets a monthly KPI report. Would you like to schedule 20 minutes to talk about your current transport setup and see if there is a fit?'"},{"h":"13.3 Objection Handling — Word for Word","body":"Objection: 'We already have a courier.'\nResponse: 'That is great — most facilities do. I would just ask: does your current courier provide a BAA, chain-of-custody documentation with GPS verification on every run, automated temperature log PDFs for cold chain shipments, and monthly KPI reports? If they do, we may not be a better fit. If they do not, your next compliance audit is going to reveal that gap before we do.'\n\nObjection: 'You are too expensive.'\nResponse: 'The temperature monitoring, chain-of-custody documentation, HIPAA training, monthly KPI reporting, and 24/7 STAT coverage we provide cost money to operate correctly. Generic couriers who provide none of that can charge less. When your next CAP audit asks for transport documentation, which answer do you want to have?'\n\nObjection: 'We need to go through vendor credentialing.'\nResponse: 'Absolutely — and we are ready for it today. We are set up in GHX Vendormate and symplr. We have our COI available, HIPAA training certificates on file, and SOP documentation ready for review. Which platform does your facility use? We can have our credentials submitted within 48 hours.'\n\nObjection: 'We are a small clinic.'\nResponse: 'Do you have STAT coverage for urgent specimens right now? Do you get a chain-of-custody record on every pickup? If something goes wrong with a specimen, do you have documentation to protect yourself? Even smaller facilities deserve a courier who operates at a professional standard — and our pricing scales with volume.'"},{"h":"13.4 Vendor Credentialing — Step by Step","body":"GHX Vendormate — used by many hospital systems nationwide. Documents required: COI, HIPAA training certs, W-9, company profile, SOP summary. Timeline: 2–4 weeks.\n\nsymplr (Vendor Check) — hospital networks and large health systems. Same documents as GHX plus background check for each driver. Timeline: 3–6 weeks.\n\nRepTrax — some hospital systems, particularly OR-based. COI, training certs, product documentation if applicable. Timeline: 2–4 weeks.\n\nInovalon / Aris360 — specialty pharmacy and clinical networks. Company profile, HIPAA compliance documentation. Timeline: 2–3 weeks.\n\nStatus rule: Do not pursue a hospital account without first confirming which platform they use and beginning the credentialing process. Credentialing takes time — start it the moment you identify a target facility."},{"h":"13.5 Proposals, Onboarding & the 30-Day Check-In","body":"Proposal structure: Executive Summary (1 page, who we are and why we are the right partner). Proposed Services (specific services, route structure, frequency, coverage hours). Compliance Credentials (HIPAA/BAA, UN3373, OSHA, temperature monitoring, COC system, SOPs). Technology Platform (Onfleet, LogTag, Samsara — what each produces for the client). Pricing (itemized, transparent, with rationale for each tier). SLA Framework (on-time targets, documentation standards, incident response times, reporting schedule). References. Next Steps (specific call to action).\n\nClient onboarding — 7 steps: (1) Discovery Call — Week 1 — map every pickup location, delivery destination, time window, and temperature requirement. (2) BAA and MSA Execution — within 24 hours of discovery call — no service begins until both are signed. (3) Route Design — Week 1-2 — build in Onfleet. (4) Pilot Run — Week 2 — supervised. Review every COC record immediately after. (5) Pilot Review Call — share the COC record and temperature log as examples of what they will receive. (6) Go Live Authorization — Week 3 — Operations signs off. (7) 30-Day Check-In — deliver the first monthly KPI report. Ask: Is there anything we could be doing better?\n\nThe 30-day check-in sets the relationship tone for every month that follows. Clients who receive a KPI report and a check-in call in their first month become long-term partners."}],"quiz":[{"q":"A hospital supply chain director says 'We already have a courier.' Give the complete word-for-word Corevian response.","opts":["'We offer competitive pricing that may be better than your current courier.'","'That is great — most facilities do. I would ask: does your current courier provide a BAA, chain-of-custody documentation with GPS verification on every run, automated temperature log PDFs, and monthly KPI reports? If they do not, your next compliance audit will reveal that gap before we do.'","'We are a newer company but we are growing fast and would love the opportunity.'","'We have WOSB and MBE certifications which may benefit your supplier diversity goals.'"],"correct":1,"feedback":"Lead with the documentation gap, not with Corevian. The question forces the director to evaluate their current courier against specific compliance criteria — criteria most couriers cannot meet."},{"q":"What are the three primary vendor credentialing platforms Corevian uses and what documents does each require?","opts":["GHX, symplr, RepTrax — all require only COI and W-9","GHX Vendormate (COI, HIPAA certs, W-9, SOP summary), symplr (same plus per-driver background checks), RepTrax (COI, training certs, product docs) — timelines 2-6 weeks each","symplr, Inovalon, and Aris360 — all require only a company profile","GHX and symplr only — RepTrax is not used by Corevian"],"correct":1,"feedback":"GHX Vendormate: COI, HIPAA training certs, W-9, company profile, SOP summary — 2-4 weeks. symplr: same plus per-driver background check — 3-6 weeks. RepTrax: COI, training certs, product docs — 2-4 weeks. Inovalon/Aris360: company profile, HIPAA compliance docs — 2-3 weeks. Begin credentialing the moment you identify a target hospital."},{"q":"Walk through all seven steps of the Corevian client onboarding process from signed contract to first live run.","opts":["Sign contract, start deliveries, report monthly","Discovery call, BAA/MSA execution, route design, pilot run, pilot review call, go-live authorization, 30-day check-in — in that order, with specific timing for each","BAA execution, route design, first delivery, monthly reporting","Discovery call, pricing agreement, first run, 90-day review"],"correct":1,"feedback":"Step 1: Discovery Call Week 1. Step 2: BAA and MSA via DocuSign within 24 hours — no service begins without both signed. Step 3: Route design in Onfleet Weeks 1-2. Step 4: Supervised pilot run Week 2. Step 5: Pilot review call — share COC record and temperature log. Step 6: Go Live Authorization Week 3. Step 7: 30-Day check-in with first monthly KPI report. The 30-day check-in is what converts a new client into a long-term partner."},{"q":"A specialty pharmacy director says your pricing is too high. What is the complete Corevian response?","opts":["Offer a discount to close the deal","Explain that pricing is fixed and cannot be negotiated","Explain why the pricing reflects real operational costs: temperature monitoring, COC documentation, HIPAA training, monthly KPI reporting, and 24/7 STAT coverage all cost money. Generic couriers who provide none of that charge less. Ask: when your next CAP audit asks for transport documentation, which answer do you want to have?","Offer a free trial month to demonstrate the value"],"correct":2,"feedback":"Never apologize for the pricing. Explain what it funds. Then turn the conversation to the audit risk they face with a cheaper, less-documented courier. The question about the CAP audit puts the real cost of the cheaper option in front of them."},{"q":"What is the rule about pursuing a hospital account and vendor credentialing?","opts":["Begin outreach and credentialing simultaneously — they take the same time","Close the contract first, then begin credentialing — credentialing is administrative","Do not pursue a hospital account without first confirming which platform they use and beginning the credentialing process — credentialing takes 3-6 weeks and must run in parallel with the sales process","Credentialing is the hospital's responsibility — Corevian only needs a COI"],"correct":2,"feedback":"Credentialing is a physical access requirement, not an administrative courtesy. Many hospital facilities require credentialing platform verification before any vendor can enter the building. A contract without completed credentialing means the driver may be turned away at the door. Start credentialing the moment you identify a target facility."}]},{"id":14,"num":"14","role":"leadership","title":"Leadership, HR & Finance","subtitle":"How to hire right, how to terminate correctly, Louisiana employment law, and how to read your own financial statements.","quote":"Every business failure that was not a market failure was a people failure or a financial failure. Learn both.","sections":[{"h":"14.1 How to Hire the Right Driver","body":"In healthcare logistics, one bad hire does not just cost you money — it can cost you a client, a compliance certification, or a patient safety event. The hiring process must be structured, documented, and consistent.\n\nWhat you are looking for: Reliability record — ask: 'Tell me about a time you had a commitment and something came up. What did you do?' Professional appearance — if they show up to the interview unprofessional, they will show up to a hospital unprofessional. Communication — can they speak clearly, listen carefully, respond professionally? Healthcare experience (preferred, not required). Clean driving record — pull the MVR before any offer. Reckless driving, DUI, or multiple accidents are disqualifying.\n\nThe hiring decision checklist: Application or resume reviewed. Phone screen completed. In-person interview completed — structured questions, consistent across all candidates. MVR pulled and reviewed. Background check completed. Drug screen completed. References called — at least two professional references. Offer letter issued — includes position, pay rate, start date, at-will statement."},{"h":"14.2 Illegal Interview Questions","body":"These questions are illegal in employment interviews. Asking them exposes Corevian to discrimination claims. Train every person who interviews candidates on this list.\n\nNEVER ASK: Age, date of birth, or graduation year (implies age discrimination). Marital status, number of children, or childcare arrangements. National origin, citizenship, or country of birth — you CAN ask: 'Are you authorized to work in the United States?' Religious affiliation or practice. Disability or medical history — you CAN ask: 'Can you perform the essential functions of this job?' Arrest record — you CAN ask about convictions with care; the background check handles this formally.\n\nThe rule is simple: stick to questions about the job, the schedule, the requirements, and the candidate's demonstrated abilities. Any question that could reveal a protected characteristic is a question you do not ask."},{"h":"14.3 How to Terminate an Employee — Correctly","body":"Louisiana is an at-will employment state — either party can end employment at any time for any legal reason. At-will status does not mean fire with no documentation. Documentation is what protects Corevian if the termination is ever challenged.\n\nDocumentation required before termination: Written documentation of the performance issue or policy violation — dated and signed by the employee. For performance issues: documented coaching conversations, written performance improvement plan, clear timeline and expectations. For policy violations: written incident reports, witness statements, the specific policy violated. For immediate termination offenses (theft, workplace violence, positive drug test, HIPAA violation): document the incident thoroughly the same day.\n\nThe termination conversation — 5 steps: (1) Have a witness present — always. (2) State the decision directly: 'I need to let you know that we are ending your employment with Corevian, effective today.' (3) State the reason briefly — one or two sentences. (4) Cover logistics — return of company property, Louisiana Final Paycheck Law: wages due on next regular payday or within 15 days — failure triggers penalty wages up to 90 days of pay under Louisiana RS 23:632. (5) End professionally — 'I wish you well.' Do not allow the conversation to become an argument."},{"h":"14.4 Louisiana Employment Law Essentials","body":"At-will employment: Either party can end the relationship at any time for any legal reason — Louisiana is a strong at-will state. Still requires documentation.\n\nMinimum wage: Louisiana has no state minimum wage — federal minimum of $7.25/hr applies. Most Corevian drivers earn more.\n\nOvertime: Any employee working over 40 hours in a workweek earns 1.5x their regular rate — federal FLSA. Not optional.\n\nFinal paycheck: Louisiana RS 23:631 — wages due on next regular payday or within 15 days of termination. Failure triggers penalty wages up to 90 days under RS 23:632.\n\nWorkers compensation: Louisiana requires workers comp for any employer with ONE or more employees — stricter than many states. Get coverage before your first hire, not your third.\n\nIndependent contractor vs employee: A driver you control — when, where, and how they work — is legally an employee under the IRS economic realities test. Misclassifying employees as contractors triggers IRS penalties, back taxes, and Louisiana LWCC penalties.\n\nAnti-discrimination: Cannot discriminate based on race, color, sex, religion, national origin, age (40+), disability, or — in Louisiana — pregnancy (Louisiana Employment Discrimination Law). Every hire, every firing, every performance action must be based on documented, legitimate business reasons."},{"h":"14.5 Reading Your Financial Statements","body":"The number one reason small businesses fail is not lack of clients — it is lack of financial visibility. You must be able to read your own financial statements and know exactly where the business stands at any moment.\n\nThe Profit & Loss Statement (P&L): Revenue = total income from all services. Cost of Goods Sold (COGS) = direct costs of providing the service (driver pay, fuel, equipment, LogTag costs). Gross Profit = Revenue minus COGS. Operating Expenses = indirect costs (insurance, software, phone, admin, marketing). Net Income = Gross Profit minus Operating Expenses.\n\nCash Flow — different from profit: You can be profitable on paper and still run out of cash. A client who owes you $5,000 but has not paid yet means that money is not in your account — even though it shows as revenue. Monitor both profitability AND cash on hand every week.\n\nTHE CASH RULE: Never let your business bank account fall below 3 months of operating expenses. This is your operational cushion. A large unexpected expense, a client who pays late, or a slow month should not threaten your ability to make payroll and pay insurance."}],"quiz":[{"q":"What documentation must exist BEFORE you can terminate an employee for performance issues in Louisiana?","opts":["No documentation required — Louisiana is at-will and you can terminate at any time","Only a verbal warning is needed, documented in a supervisor's notes","Written documentation of the performance issue dated and signed by the employee; documented coaching conversations; a written performance improvement plan with clear timeline and expectations; and the specific policy violated","A letter from HR and approval from the state labor board"],"correct":2,"feedback":"At-will means you can terminate for legal reasons — it does not mean undocumented. Without written, signed documentation of each incident, a terminated employee can successfully challenge the termination. Documentation is Corevian's protection."},{"q":"A candidate mentions during an interview that they have three young children. You want to ask about weekend availability. What is the legally correct question?","opts":["'With three kids, are you able to work weekends?'","'Can your family schedule accommodate weekend work?'","'This role requires weekend availability on a rotating basis — are you able to meet that requirement?'","'I understand you have family commitments — how flexible is your schedule?'"],"correct":2,"feedback":"Reference only the job requirement — never personal information the candidate volunteered. Connecting the question to 'three kids' or 'family commitments' references family status, a protected characteristic under Title VII and Louisiana law. Ask only about the job requirement."},{"q":"What is Louisiana's final paycheck law and what happens if it is violated?","opts":["Final paycheck must be issued within 30 days — violation results in a $500 fine","Final paycheck must be issued on the next regular payday or within 15 days of termination — failure triggers penalty wages of up to 90 days of pay under Louisiana RS 23:632","Louisiana has no specific final paycheck law — the federal standard applies","Final paycheck must be issued immediately on termination day — violation results in a $1,000 fine"],"correct":1,"feedback":"Louisiana RS 23:631: wages due on next regular payday or within 15 days of termination — whichever comes first. Failure triggers penalty wages of up to 90 days of pay under RS 23:632. This is one of the most expensive mistakes a Louisiana employer can make."},{"q":"A profitable month shows net income of $5,400 but the bank balance dropped by $2,100 during that same period. How is this possible?","opts":["The accounting must be wrong — a profitable month always increases the bank balance","Profit and cash flow are the same thing — this situation cannot exist","Profit is a paper measurement based on when revenue is earned. Cash flow reflects actual money in the account. If $6,000 was invoiced but only $3,900 was collected that month, the business was profitable on paper but cash actually decreased. Accounts receivable, loan payments, and large one-time expenses all affect cash without affecting reported profit.","Operating expenses were understated in the P&L"],"correct":2,"feedback":"This is the most dangerous blind spot for small business owners. You can be profitable every month and still run out of cash — if clients pay late, if a large expense hits, or if receivables build up. Monitor both P&L AND cash on hand weekly. The cash rule: never let the account fall below 3 months of operating expenses."},{"q":"At what employee count does Louisiana workers compensation become legally required, and what is the consequence of operating without it?","opts":["5 employees — consequence is a fine up to $1,000","3 employees — consequence is license suspension for 30 days","1 or more W-2 employees — consequence is personal liability for all workplace injury costs plus civil penalties. The owner pays out of pocket for medical bills, lost wages, and rehabilitation for any injured employee.","10 employees — consequence is a state audit and mandatory compliance plan"],"correct":2,"feedback":"Louisiana is stricter than most states — workers comp is required from employee number one. Operating without it means the owner is personally liable for every workplace injury cost. Get coverage before the first hire, not the third."}]},{"id":15,"num":"15","role":"leadership","title":"Compliance & Audit Readiness","subtitle":"What auditors look for, how to respond when they arrive, what records to keep and for how long, and how to build a compliance file that holds up to any inspection.","quote":"An audit is not a surprise if you have been doing your job correctly every day. Our documentation is our defense.","sections":[{"h":"15.1 Documents That Must Always Be Current and On File","body":"Business Associate Agreements (BAA): 6 years from execution or last effective date (HIPAA). Stored in Microsoft 365 — BAA folder. Maintained by Operations/Leadership.\n\nHIPAA Training Records (all staff): 6 years — must show who was trained and when. Stored in Microsoft 365 — Training Records folder. Maintained by Operations.\n\nDriver Training Certificates (HIPAA, UN3373, COC): Duration of employment + 3 years. Stored in Microsoft 365 — Personnel folder per driver. Maintained by Operations.\n\nLogTag Calibration Certificates: As long as logger is in service + 3 years. Stored in Microsoft 365 — Equipment folder. Maintained by Operations.\n\nIncident Reports (CHL-F-001): 5 years minimum. Stored in Microsoft 365 — Incident Log. Maintained by Operations.\n\nCAPAs: 5 years minimum. Stored in Microsoft 365 — CAPA Log. Maintained by Operations.\n\nChain-of-Custody Records: 3 years minimum (HIPAA-related COC: 6 years). Onfleet archive + Microsoft 365 backup. Automatic / Operations.\n\nTemperature Log PDFs: 3 years minimum. Microsoft 365 — Temperature Logs by month. Automatic / Operations.\n\nCertificate of Insurance (COI): Current certificate on file at all times. Microsoft 365 — Compliance folder. Operations.\n\nEmployee Personnel Files: Duration of employment + 7 years. Microsoft 365 — HR (restricted access). Leadership only.\n\nVehicle Maintenance Records: 3 years or life of vehicle, whichever is longer. Microsoft 365 — Fleet folder. Operations."},{"h":"15.2 What Auditors Look For — By Type","body":"HIPAA Audit (HHS Office for Civil Rights): Signed BAA with every client who generates PHI-adjacent work. Documented HIPAA training for every workforce member — dates, scores, trainer confirmation. Breach notification procedures in writing. Minimum necessary policies — documented policy stating drivers access only the PHI required for the delivery.\n\nCAP Inspection (Laboratory Clients): Pre-acceptance inspection documentation — evidence that drivers are trained and performing the 8-point inspection. Chain-of-custody records for specimen transport — complete scan events, timestamps, recipient signatures. Temperature documentation for cold chain specimens — LogTag PDF reports. Driver training records — UN3373, biological specimen handling, HIPAA.\n\nJCAHO Survey (Hospital Clients): Vendor credentialing records — proof that Corevian is credentialed in GHX/symplr. Driver background checks and training documentation. Emergency/STAT response capability documentation. Incident reporting and CAPA evidence.\n\nOSHA Inspection: Bloodborne pathogen training records for all drivers handling biological specimens. Spill kits present in all vehicles — inspectors may ask drivers to show their kit. PPE availability documentation. Exposure incident records if any have occurred."},{"h":"15.3 What to Do When an Auditor Arrives","body":"Step 1 — Remain calm. An audit is not an accusation. If the documentation is in order — and ours is — there is nothing to fear.\n\nStep 2 — Request credentials and written scope. Ask for the auditor's official identification and the written scope of the audit before allowing access to any records.\n\nStep 3 — Notify Raquia immediately. Call before the audit formally begins if possible. No document is produced until leadership is on the phone or physically present.\n\nStep 4 — Answer honestly and specifically. Answer only what is asked. If you do not know something: 'I will need to retrieve that information and get back to you.' Never guess.\n\nStep 5 — Retrieve documents promptly. The Microsoft 365 folder structure means any document can be found in under 2 minutes. An auditor who waits more than 5 minutes for a document begins to wonder why.\n\nStep 6 — Never argue or obstruct. If an auditor identifies a finding, acknowledge it professionally: 'Understood. We will address that.' Never dispute during the active audit.\n\nStep 7 — Document the audit visit. After the auditor leaves: who came, their organization, date and duration, what they reviewed, any findings. This becomes part of the CAPA record."},{"h":"15.4 WOSB & MBE Certification Maintenance","body":"WOSB (SBA self-certification): Maintained at certify.sba.gov. Annual recertification required — confirm ownership and control thresholds are still met. Disqualifiers: ownership drops below 51% female, or operational control shifts to a non-woman.\n\nMBE (NMSDC): Maintained through NMSDC Louisiana affiliate. Annual renewal — documentation of ownership, financials, and control. Disqualifiers: ownership below 51% minority, revenue above threshold, loss of control.\n\nBoth certifications require annual action. Lapsed certifications close supplier diversity procurement channels and may void existing contracts awarded on the basis of certification status. Calendar the renewal dates — do not let them lapse. The value of these certifications is in keeping them current, not just obtaining them."}],"quiz":[{"q":"A CAP inspector arrives unannounced asking to see your specimen transport documentation. What are the first three things you do — in order?","opts":["Pull all records immediately and answer every question fully","Ask them to leave and schedule a formal audit in advance","Remain calm. Request their official credentials and the written scope of the audit. Call Raquia immediately before the audit formally begins — no documents are produced until leadership is on the phone.","Direct them to the driver who did the last specimen run"],"correct":2,"feedback":"The sequence matters. Remain calm. Request credentials and written scope — you are entitled to know who this is and what they are authorized to review. Call Raquia before the audit formally begins. No document is produced without leadership present. The documentation is in order — the protocol is about controlling the process."},{"q":"How long must HIPAA-related records — BAAs, training records, and HIPAA-related COC records — be retained?","opts":["3 years from the date of creation","5 years from the date of creation","6 years from the date of creation or the date the record was last in effect — whichever is later","10 years for BAAs, 3 years for training records"],"correct":2,"feedback":"HIPAA requires 6 years from date of creation OR date last in effect — whichever is later. A BAA signed in 2024 that remains active through 2028 must be retained until at least 2034. Active record management matters — not just initial documentation. Corevian-specific: HIPAA-related COC records are retained 6 years; general COC records minimum 3 years."},{"q":"An OSHA inspector asks a driver to show their vehicle spill kit. What must the kit contain?","opts":["A first aid kit and fire extinguisher","Latex gloves and hand sanitizer","Nitrile gloves, face shield or splash goggles, absorbent pads, biohazard waste bags, bleach solution (10% concentration), and instructions for decontamination — all present and unexpired","Any cleaning supplies available in the vehicle"],"correct":2,"feedback":"OSHA bloodborne pathogen inspectors will physically check spill kit contents. An incomplete kit is an immediate citation. Every Corevian vehicle carries a complete spill kit at all times — this is part of the daily pre-shift checklist. If the kit has been used, it must be restocked before the next shift."},{"q":"WOSB certification lapses because the annual recertification was missed. What are the consequences?","opts":["No immediate consequence — there is a 90-day grace period","A $500 fine from the SBA","Loss of access to supplier diversity procurement channels, and existing contracts awarded on the basis of WOSB status may be voided. The certification must be reinstated before any WOSB-designated contracts can be pursued.","The certification automatically renews — no lapse is possible"],"correct":2,"feedback":"WOSB and MBE certifications are the keys to supplier diversity procurement channels. A lapsed certification closes those channels immediately. Existing contracts awarded based on certification status may be voided if certification lapses during contract performance. Calendar the renewal dates — this is not optional maintenance."},{"q":"During an audit a finding is identified. The auditor states that a driver training record from eight months ago is missing the trainer signature. What is the correct in-room response?","opts":["Dispute the finding — point out that the driver did complete the training","Offer to produce a retroactive signed record","'Understood. We will address that.' Document the finding after the auditor departs, initiate a corrective action to investigate the gap and prevent recurrence, and send the client a CAPA summary.","Explain that the trainer was unavailable that day"],"correct":2,"feedback":"Never dispute during an active audit. 'Understood. We will address that.' is the complete in-room response. After the auditor departs: document the finding, investigate root cause, implement corrective action, send CAPA summary to the affected client. This sequence turns an audit finding into evidence of a quality management system — not evidence of negligence."}]},{"id":16,"num":"16","role":"leadership","title":"Growing Corevian","subtitle":"Revenue model, route building, subcontracting network, the 90-day growth plan, and how to build a company that scales beyond its founders.","quote":"We are not building a job. We are building a company. Those require completely different thinking.","sections":[{"h":"16.1 The Revenue Model","body":"Contracted daily specimen route: Monthly flat retainer, $1,500–$8,000/month. Win it through direct outreach to lab directors — 2-week onboarding.\n\nContracted pharmacy delivery route: Monthly flat retainer, $1,000–$5,000/month. Win through pharmacy director relationships — compounding and specialty pharmacies.\n\nHospital outreach program: Monthly flat retainer, $8,000–$30,000/month. Win through vendor credentialing and supply chain relationship — 6–12 weeks to close.\n\nOn-demand per-run delivery: $35–$150 per run. Comes after trust is established in the relationship.\n\nSTAT surcharge: $35–$75 added to any STAT run. Included in contracted client agreements.\n\nCold chain surcharge: $15–$30 per cold chain run. Included in contracts with temperature-sensitive clients.\n\nSubcontracting platform runs: $25–$80 per run. Apply to Lab Logistics Group, Courier Exchange, and similar platforms for immediate cash flow while direct contracts build.\n\nThe target model: monthly retainer contracts from multiple clients. Predictable recurring revenue is operationally and financially more stable than per-run billing — you can plan staffing, equipment, and cash flow around it."},{"h":"16.2 Revenue Milestones","body":"$5,000/month: 2–3 contracted lab routes running daily. Key milestone: first contracted hospital or lab client.\n\n$15,000/month: 5–7 contracted routes plus regular on-demand volume. Mix of lab, pharmacy, and hospital clients.\n\n$30,000/month: 8–12 contracted clients plus first subcontractor partner. First hospital outreach program contract.\n\n$60,000/month: 15–20 clients, structured subcontractor network, consistent KPI reporting across all clients. Second operations hire needed.\n\n$100,000/month: 25+ clients, 3–5 trained subcontractor partners, 2–3 staff. Regional recognition — Corevian is the standard in New Orleans and Louisiana."},{"h":"16.3 The 90-Day Growth Plan","body":"Phase 1 — Foundation (Weeks 1-4): Goal: first 2 signed contracted clients. Actions: complete vendor credentialing on 3 platforms; begin direct outreach to 20 target facilities in New Orleans metro; finalize all compliance documentation; apply to 3 subcontracting platforms.\n\nPhase 2 — Route Build (Weeks 5-8): Goal: 4–6 contracted routes running daily. Actions: deliver perfect service to first clients; request referrals; pursue first hospital system contact; begin WOSB renewal process; hire first driver if volume requires.\n\nPhase 3 — Expansion (Weeks 9-12): Goal: $15,000+/month contracted revenue. Actions: formalize first subcontractor relationship; pursue hospital outreach program contract; deliver first set of monthly KPI reports; schedule first quarterly reviews with contracted clients."},{"h":"16.4 Subcontracting Network Management","body":"As Corevian grows beyond our direct driver capacity, we extend through a qualified subcontracting network. Subcontractors operate under Corevian's SOPs, documentation requirements, and professional standards — not their own.\n\nEvery subcontractor executes a subcontractor services agreement before their first run — this agreement binds them to Corevian's operational standards.\n\nEvery subcontractor driver completes Corevian's HIPAA training and receives COC documentation requirements in writing before their first run.\n\nSubcontractor vehicles are inspected before being authorized for Corevian runs.\n\nSubcontractors use Onfleet on every run — same documentation standard, same GPS tracking, same COC events as direct drivers.\n\nSubcontractor performance is reviewed monthly in the same KPI framework as direct drivers.\n\nThe standard extends to every person who runs under the Corevian name. A subcontractor who creates a compliance failure is still a Corevian compliance failure. The qualification requirement is not optional."}],"quiz":[{"q":"What is the Phase 3 revenue target in the 90-day growth plan and what are the three key actions that drive it?","opts":["$5,000/month — sign first contracted client, complete vendor credentialing, apply to subcontracting platforms","$30,000/month — hire operations staff, open a second location, expand service area","$15,000+/month contracted revenue — formalize first subcontractor relationship; pursue hospital outreach program contract; deliver first monthly KPI reports and schedule quarterly client reviews","$60,000/month — structured subcontractor network, 15-20 clients, second operations hire"],"correct":2,"feedback":"Phase 3 (Weeks 9-12): $15,000+/month. Three key actions: (1) Formalize first subcontractor relationship. (2) Pursue hospital outreach program contract. (3) Deliver first set of monthly KPI reports and schedule first quarterly reviews. These three actions — subcontracting, hospital, and client retention — are what move Corevian from the route-build phase to a scaled operation."},{"q":"What must a subcontractor complete before their first Corevian run?","opts":["A phone interview and a background check","Nothing — subcontractors operate under their own standards","Execute a subcontractor services agreement; complete Corevian HIPAA training; receive COC documentation requirements in writing; have their vehicle inspected; and be set up on Onfleet with the same documentation standard as direct drivers","Submit their insurance certificate and driver's license copy"],"correct":2,"feedback":"A subcontractor who runs under the Corevian name is held to Corevian's standard — not a lower one. The qualification requirement is not negotiable. A subcontractor compliance failure is a Corevian compliance failure. Every requirement — agreement, HIPAA training, COC documentation, vehicle inspection, Onfleet — must be completed before the first run."},{"q":"Why is a monthly retainer contract the target revenue model rather than per-run billing?","opts":["Retainer contracts are always more expensive for clients and generate more revenue per run","Per-run billing is actually preferred because it matches revenue to actual volume delivered","Monthly retainers create predictable recurring cash flow — the most operationally and financially stable revenue model. Predictable revenue allows planning for staffing, equipment, and cash flow. Per-run billing is useful for new relationships before conversion but creates variable revenue that is harder to plan around.","Retainer contracts require less documentation and are easier to manage"],"correct":2,"feedback":"Predictable recurring revenue is the foundation of a stable operating business. Three retainer clients generating $5,000/month each is more operationally valuable than the same total in variable per-run billing — because you can plan around it. The growth strategy converts per-run relationships to retainer contracts as trust is established."},{"q":"At what monthly revenue level does Corevian typically require its first additional operations hire and what does the business look like at that stage?","opts":["$15,000/month — first contracted client and one route","$30,000/month — 8-12 clients and first subcontractor partner","$60,000/month — 15-20 clients, structured subcontractor network, consistent KPI reporting across all clients","$100,000/month — 25+ clients and regional recognition"],"correct":2,"feedback":"$60,000/month: 15-20 clients, structured subcontractor network, consistent KPI reporting across all clients. This is the operational complexity level where a second operations hire becomes necessary — the volume of route management, documentation, subcontractor oversight, and client communication exceeds what one operations leader can manage alone."},{"q":"Corevian receives an offer to join a subcontracting platform at $35 per run for standard specimen routes. You have no direct hospital contracts yet. Should you accept? Explain the complete reasoning.","opts":["No — subcontracting undermines the Corevian brand and should be avoided","Yes, but only temporarily and without using Corevian branding","Yes — with full Corevian operational standards applied on every run. Subcontracting provides immediate cash flow while direct contracts build. Every run is an opportunity to build operational history, documentation quality, and hospital relationships that may become direct accounts. The standard does not change because it is a platform run.","Only if the per-run rate exceeds $75 — otherwise the margin is too thin"],"correct":2,"feedback":"Subcontracting is Phase 1 strategy — immediate cash flow while the direct contract pipeline builds. The key condition: full Corevian standards on every run. The same pre-acceptance inspection, same COC documentation, same professional appearance. Subcontracted runs build operational history and hospital relationships that are directly transferable to direct contract conversations."}]},{"id":17,"num":"17","role":"leadership","title":"Training New Employees","subtitle":"The 5-day program, skill verification, what to look for, how to authorize — and how to handle someone who is not ready.","quote":"Every person you authorize to represent Corevian carries our standards into every facility they enter. Train them like it matters — because it does.","sections":[{"h":"17.1 The 5-Day Training Program","body":"Day 1 — Company overview, mission, values, HIPAA (full Unit 3), and the Corevian why. Method: classroom reading and verbal discussion. Pass requirement: score 80%+ on HIPAA written assessment (20 questions).\n\nDay 2 — UN3373 and biological safety, triple packaging, pre-acceptance inspection, spill response, COC system overview. Method: classroom plus hands-on packaging and spill kit practice. Pass requirement: complete UN3373 competency certificate — physical packaging demonstration observed by trainer.\n\nDay 3 — Onfleet full certification (all 18 steps), cold chain equipment operation (LogTag full sequence), vehicle and equipment standards. Method: hands-on app walkthrough, equipment operation, vehicle inspection. Pass requirement: complete all Onfleet training scenarios without errors.\n\nDay 4 — Supervised ride-along. New hire observes and assists. Experienced driver operates. Method: field, full supervised route. Pass requirement: supervisor sign-off on all observed COC events and interactions.\n\nDay 5 — Solo supervised run. New hire operates. Trainer observes in passenger seat. Trainer does not intervene unless safety issue. Method: field, supervised solo. Pass requirement: trainer authorization for independent operation — both sign the authorization form."},{"h":"17.2 The Three Non-Negotiable Skills","body":"Never authorize a driver who cannot perform all three of these correctly and independently:\n\n1. The pre-acceptance inspection — all 8 points, in sequence, without prompting. A driver who would accept a non-compliant package creates regulatory exposure for Corevian and the client. There is no passing 7 out of 8 points.\n\n2. The Onfleet delivery sequence — all required steps, all documentation fields, without errors. A driver who skips COC events or fails to collect a recipient signature creates an incomplete legal record on every run they do.\n\n3. The incident escalation procedure — all steps, from memory, including the 5-minute operations contact rule. A driver who improvises during an incident creates liability. The procedure exists because it was designed to protect everyone.\n\nIf any of these three skills cannot be demonstrated independently, the Day 5 authorization does not happen. Extend the supervised period, address the specific gap, and reassess."},{"h":"17.3 The Skill Verification Framework","body":"HIPAA Verification: Can name at least 10 PHI identifiers without looking. Can explain the minimum necessary rule in their own words. Knows the 1-hour breach reporting rule. Can describe what a BAA is and why it exists. Scored 80%+ on the written HIPAA assessment.\n\nUN3373 Verification: Can perform all 8 points of the pre-acceptance inspection correctly and in sequence. Can correctly identify a non-compliant package and state the correct response. Can describe all three layers of the triple packaging system from memory. Has physically packed a specimen in a triple-packaging system under trainer observation. Knows all steps of spill response.\n\nCold Chain Verification: Can activate and start a LogTag logger correctly without prompting. Knows the temperature range for each product type. Knows what to do if an excursion is detected. Can set up a validated cooler for a 2–8°C run. Knows the difference between ambient, refrigerated, and frozen transport.\n\nOnfleet Verification: Can complete all 18 steps of the Onfleet delivery sequence without prompting. Knows what to do if the recipient is unavailable. Knows how to send a GPS tracking link for a STAT run. Has completed all Onfleet training scenarios without errors.\n\nIncident Response Verification: Can state the incident escalation sequence — all steps — from memory. Knows the 5-minute rule for contacting operations after any incident. Can set up and use the spill kit correctly under observation."},{"h":"17.4 What to Do When Someone Is Not Ready","body":"Do not pass someone who is not ready. The authorization form your signature goes on is a professional representation that this person is capable of independently operating at Corevian's standard. That representation has legal and operational weight.\n\nThe conversation: 'You have made real progress this week, and I want to be honest with you about where you stand. You are not quite ready for solo authorization yet. The specific areas we need to strengthen are [X and Y]. Here is what we are going to do — we are going to spend [time] working on those specific skills, and then we will reassess. This is about making sure you are set up for success, not about doubting your capability.'\n\nDocument the specific gaps in the personnel file. Set a reassessment date. Address the specific skills with additional practice — do not repeat the entire 5-day program if only one skill needs work. When the reassessment occurs, evaluate only the skills that were identified as gaps. Sign when all three non-negotiables are demonstrated."}],"quiz":[{"q":"On Day 5, the trainee completes the Onfleet sequence and pre-acceptance inspection correctly but describes only 5 steps of the incident escalation sequence when asked. What do you do?","opts":["Sign the authorization — they demonstrated the two most critical operational skills","Sign conditional authorization and monitor for 30 days","Do not sign the authorization. All three non-negotiable skills must be demonstrated completely and without errors. The incident escalation procedure is non-negotiable. Extend the supervised period, drill the specific gap, and reassess.","The trainee passed 2 of 3 non-negotiables — a 67% pass rate on non-negotiables is sufficient"],"correct":2,"feedback":"All three non-negotiable skills must be demonstrated completely. A driver who does not know the incident escalation sequence will improvise in an actual incident — creating liability for Corevian and the client. The authorization does not proceed. The standard protects everyone."},{"q":"What happens on Day 4 of the 5-day training program and what is the pass requirement?","opts":["The trainee operates the full route solo while the trainer observes from a distance","The trainee takes a written exam covering all units","Day 4 is a supervised ride-along — an experienced driver operates while the new hire observes and assists. Pass requirement: supervisor sign-off on all observed COC events and all client and patient interactions.","Day 4 is a classroom review of all prior days' material before the Day 5 field test"],"correct":2,"feedback":"Day 4 is observation and assistance — the experienced driver operates, the new hire learns in a real environment. This is different from Day 5 where the new hire operates and the trainer only observes. Day 4 verifies understanding in context. Day 5 verifies independent execution. Both are required because comprehension and competency are different things."},{"q":"Three weeks into solo operation, you observe that a driver is consistently skipping COC Event 5 — the facility arrival scan — when parking is difficult. What is the correct response?","opts":["Mention it at their 30-day review as a performance note","Let it continue — the facility arrival scan is the least critical of the seven events","Address it immediately and specifically. Provide a written corrective action naming the specific runs affected. Document in the personnel file. Monitor on the next supervised run. This is a compliance gap, not a preference issue.","Remove them from routes with difficult parking until the habit is established"],"correct":2,"feedback":"Missing COC Event 5 on multiple runs is a documented performance gap — not a reminder item. Without Event 5, the COC record is incomplete. If a client or auditor requests a record and Event 5 is missing, Corevian cannot explain the gap. Written corrective action, documentation in personnel file, monitored follow-up. A correct past authorization does not excuse ongoing non-compliance."},{"q":"A new hire passes Days 1 through 4 with strong scores. On Day 5 they make consistent errors collecting the recipient's job title in Onfleet — they collect the name but skip the title. Do you authorize?","opts":["Yes — they collected the name which is the more important field","Yes — this is a minor documentation error that can be corrected through reminders","No. The recipient's full name AND job title are both required fields in the COC record. A driver who consistently skips the title creates incomplete COC records on every run. Address the specific gap before authorizing.","Yes, with a note to improve the title field — probationary authorization is acceptable"],"correct":2,"feedback":"The COC record requires recipient full name AND job title — both constitute legal proof of who received the package. A driver who consistently skips the title creates a gap in every COC record they produce. This is not a minor error — it is a systematic documentation failure. Address the specific gap. Authorize when both fields are collected correctly and consistently."},{"q":"What is the correct way to conduct a reassessment after a trainee was not authorized on Day 5?","opts":["Repeat the entire 5-day training program before reassessing","Schedule a reassessment immediately and evaluate all skills again","Document the specific gaps in the personnel file, set a reassessment date, address only the specific skills that were identified as gaps with additional practice, and during reassessment evaluate only those specific skills.","Allow the trainee to self-study and request a reassessment when they feel ready"],"correct":2,"feedback":"A targeted reassessment addresses the specific gap — not the entire program. Repeating training the driver already demonstrated competency in wastes time and signals unclear standards. Document the gap, practice the gap, evaluate the gap. When the specific non-negotiable skill is demonstrated correctly and independently, sign the authorization."}]}];

// STATE
// ═══════════════════════════════════════════════════════════════════
let currentUser = null;
let currentRole = null;
let unitProgress = {}; // {unitId: persisted learner progress state}
let currentView = 'dashboard';
let selectedRole = null;
let demoMode = false; // shared global state used by navigation, dashboard, exam, and controller

// Production staff mode is database-backed. The legacy arrays above remain ONLY for Demo Mode.
let productionUnits = [];
let currentEnrollment = null;
let currentCurriculumVersionId = null;
let currentFinalAssessment = null;
let finalAssessmentStatus = { passed:false, bestScorePercent:null, attemptCount:0, assessmentType:null };
let currentProfileRole = null;
let currentLeadershipQualificationTraining = null;
let leadershipQualificationTrainingAttempts = [];
let leadershipQualificationTrainingStartedAt = null;
let competencyDefinitions = [];
let competencyCandidates = [];
let selectedCompetencyUserId = null;
let fieldTrainingCandidates = [];
let selectedFieldTrainingUserId = null;
let endorsementCandidates = [];
let selectedEndorsementUserId = null;
let authorizationCandidates = [];
let selectedAuthorizationUserId = null;
let recertificationCandidates = [];
let selectedRecertificationUserId = null;
let selectedRecertificationItemCode = null;

// ═══════════════════════════════════════════════════════════════════
// ROLE HELPERS
// ═══════════════════════════════════════════════════════════════════
function getUnitsForRole(role) {
  // Staff Mode MUST use the controlled Supabase curriculum loaded for the user's enrollment.
  if (!demoMode && productionUnits.length) return productionUnits;

  // Demo fallback hierarchy only.
  if (role === 'admin' || role === 'leadership') {
    return [...EMP_UNITS, ...LU_UNITS];
  }

  if (role === 'trainer') {
    return [
      ...EMP_UNITS,
      ...LU_UNITS.filter(u => u.id !== 16)
    ];
  }

  return EMP_UNITS;
}

function canAccessUnit(uid, role) {
  const units = getUnitsForRole(role);
  const idx = units.findIndex(u => u.id === uid);
  if (idx < 0) return false;

  // EXECUTIVE DEMO: presentation access is intentionally unlocked.
  // This bypass applies only while demoMode === true and does not
  // change real staff progression or create real training records.
  if (demoMode) return true;

  if (idx === 0) return true;
  // STAFF MODE: must pass the previous unit before advancing.
  const prev = units[idx - 1];
  if (!prev) return true;
  return unitProgress[prev.id] && unitProgress[prev.id].passed;
}

function allUnitsComplete(role) {
  const units = getUnitsForRole(role);
  return units.every(u => unitProgress[u.id] && unitProgress[u.id].passed);
}

function canEvaluateCompetencies(){
  return !demoMode && ['trainer','leadership','admin'].includes(currentProfileRole);
}
function canAccessLeadershipQualificationTraining(){
  return !demoMode && ['leadership','admin'].includes(currentProfileRole);
}
function canApproveEndorsements(){
  return !demoMode && ['leadership','admin'].includes(currentProfileRole);
}
function canIssueOperationalAuthorization(){
  return !demoMode && ['leadership','admin'].includes(currentProfileRole);
}
function canManageRecertification(){ return !demoMode && ['leadership','admin'].includes(currentProfileRole); }
// Single authoritative implementation of the leadership department-visibility rule.
// Visibility only — existing approval, qualification, and self-approval controls remain unchanged.
async function scopeProfilesToLeadershipDepartment(client, profiles) {
  // Administrator / executive: enterprise-wide visibility.
  if (currentProfileRole === 'admin') {
    return profiles;
  }

  const userResult = await client.auth.getUser();
  if (userResult.error) throw userResult.error;

  const myUserId = userResult.data?.user?.id;

  if (!myUserId) {
    throw new Error(
      'Authenticated workforce management user could not be identified.'
    );
  }

  // Trainer / manager: only personnel directly assigned to them.
  if (currentProfileRole === 'trainer') {
    return profiles.filter(
      p => String(p?.reports_to_user_id || '') === String(myUserId)
    );
  }

  // Leadership: preserve existing department-scoped leadership visibility.
  if (currentProfileRole === 'leadership') {
    const myProfile = profiles.find(
      p => String(p?.user_id) === String(myUserId)
    );

    const myDepartment = String(
      myProfile?.department || ''
    ).trim();

    if (!myDepartment) {
      return [];
    }

    return profiles.filter(p => {
      const sameDepartment =
        String(p?.department || '').trim() === myDepartment;

      const isSelf =
        String(p?.user_id) === String(myUserId);

      const isExecutiveOrLeadership =
        p?.role === 'admin' ||
        p?.role === 'leadership';

      return (
        sameDepartment &&
        !isSelf &&
        !isExecutiveOrLeadership
      );
    });
  }

  return [];
}

window.scopeProfilesToLeadershipDepartment =
  scopeProfilesToLeadershipDepartment;

async function loadLeadershipQualificationTraining(){
  if(!canAccessLeadershipQualificationTraining()){
    throw new Error('Leadership or Administrator role required.');
  }

  const c = getSupabaseClient();

  const trainingResult = await c
  .from('leadership_qualification_trainings')
  .select(`
    id,
    training_code,
    title,
    version_label,
    passing_percent,
    question_count,
    content_json,
    active,
    effective_date
  `)
  .eq('training_code','CVN-LSRAT-001')
  .eq('active',true)
  .order('effective_date',{ascending:false})
  .limit(1)
  .maybeSingle();

  if(trainingResult.error) throw trainingResult.error;

  if(!trainingResult.data){
    throw new Error(
      'CVN Leadership Solo Route Authorization Training v1.0 was not found.'
    );
  }

  const userResult = await c.auth.getUser();

  if(userResult.error) throw userResult.error;

  const userId = userResult.data?.user?.id;

  if(!userId){
    throw new Error('Authenticated user could not be identified.');
  }

  const attemptsResult = await c
    .from('leadership_qualification_training_attempts')
    .select(`
      id,
      attempt_number,
      score_percent,
      passed,
      started_at,
      completed_at,
      created_at
    `)
    .eq('training_id',trainingResult.data.id)
    .eq('user_id',userId)
    .order('attempt_number',{ascending:false});

  if(attemptsResult.error) throw attemptsResult.error;

  currentLeadershipQualificationTraining = trainingResult.data;
  leadershipQualificationTrainingAttempts = attemptsResult.data || [];

  if(!leadershipQualificationTrainingStartedAt){
    leadershipQualificationTrainingStartedAt =
      new Date().toISOString();
  }

  return currentLeadershipQualificationTraining;
}


// ═══════════════════════════════════════════════════════════════════
// AUTH
// ═══════════════════════════════════════════════════════════════════
// Production authentication is implemented in the controller at the end of this file.
// Roles are read from public.profiles after Supabase Auth succeeds; the browser does not choose its own role.

// ═══════════════════════════════════════════════════════════════════
// NAV
// ═══════════════════════════════════════════════════════════════════
function buildNav() {
  const empNav = document.getElementById('empNav');
  const leaderNav = document.getElementById('leaderNav');
  const leaderSection = document.getElementById('leaderSection');
  const units = getUnitsForRole(currentRole);
  const employeeUnits = units.filter(u => u.role !== 'leadership');
  const leadershipUnits = units.filter(u => u.role === 'leadership');

  empNav.innerHTML = employeeUnits.map(u => navBtn(u, false)).join('');
  if (leadershipUnits.length) {
    leaderSection.style.display = 'block';
    leaderNav.innerHTML = leadershipUnits.map(u => navBtn(u, true)).join('');
  } else {
    leaderSection.style.display = 'none';
    leaderNav.innerHTML = '';
  }
const qualNavBlock = document.getElementById('qualNavBlock');

if (qualNavBlock) {
  qualNavBlock.style.display =
    demoMode
      ? (['trainer', 'leadership', 'admin'].includes(currentRole) ? 'block' : 'none')
      : (canEvaluateCompetencies() ? 'block' : 'none');
}

const leadershipQualTrainingBlock =
  document.getElementById('leadershipQualTrainingBlock');

if (leadershipQualTrainingBlock) {
  leadershipQualTrainingBlock.style.display =
    demoMode
      ? (['leadership', 'admin'].includes(currentRole) ? 'block' : 'none')
      : (canAccessLeadershipQualificationTraining() ? 'block' : 'none');
}
const navWorkforceReadiness = document.getElementById('navWorkforceReadiness');

if (navWorkforceReadiness) {
  navWorkforceReadiness.style.display =
    ['trainer', 'leadership', 'admin'].includes(currentRole)
      ? 'flex'
      : 'none';

  const navSub = navWorkforceReadiness.querySelector('.nav-sub');

  if (navSub) {
    navSub.textContent =
      currentRole === 'trainer'
        ? 'Leadership workforce oversight'
        : 'Executive qualification oversight';
  
}
}
  const navEndorsements = document.getElementById('navEndorsements');
if (navEndorsements) {
  navEndorsements.style.display =
    demoMode
      ? (['leadership', 'admin'].includes(currentRole) ? 'flex' : 'none')
      : (canApproveEndorsements() ? 'flex' : 'none');
}

const navAuthorization = document.getElementById('navAuthorization');
if (navAuthorization) {
  navAuthorization.style.display =
    demoMode
      ? (['leadership', 'admin'].includes(currentRole) ? 'flex' : 'none')
      : (canIssueOperationalAuthorization() ? 'flex' : 'none');
}

const navRecertification = document.getElementById('navRecertification');
if (navRecertification) {
  navRecertification.style.display =
    demoMode
      ? (['leadership', 'admin'].includes(currentRole) ? 'flex' : 'none')
      : (canManageRecertification() ? 'flex' : 'none');
}
  
  updateProgress();
}
async function launchExecutiveDemo(){
  try{
    if(demoMode){
      return;
    }

    const client = getSupabaseClient();

    if(!client){
      throw new Error('Supabase client is unavailable.');
    }

    const userResult = await client.auth.getUser();

    if(userResult.error){
      throw userResult.error;
    }

    const userId = userResult.data?.user?.id;

    if(!userId){
      throw new Error('Authenticated user could not be identified.');
    }

    const profileResult = await client
      .from('profiles')
      .select('role,active')
      .eq('user_id', userId)
      .single();

    if(profileResult.error){
      throw profileResult.error;
    }

    const role = profileResult.data?.role;
    const active = profileResult.data?.active === true;

    if(!active){
      throw new Error('This Corevian account is not active.');
    }

    if(role !== 'leadership' && role !== 'admin'){
      throw new Error(
        'Executive Demo access is restricted to Leadership and Administrator roles.'
      );
    }

    demoMode = true;

    const demoBanner = document.getElementById('demoBanner');
    const demoBannerSpacer = document.getElementById('demoBannerSpacer');

    if(demoBanner){
      demoBanner.style.display = 'flex';
    }

    if(demoBannerSpacer){
      demoBannerSpacer.style.display = 'block';
    }

   if(typeof enterDemo === 'function'){
  enterDemo();
}else{
  throw new Error(
    'Executive Demo initialization function was not found.'
  );
}
  }catch(err){
    console.error(err);

    alert(
      err?.message ||
      'Unable to launch the Executive Demo.'
    );
  }
}

window.launchExecutiveDemo = launchExecutiveDemo;

function navBtn(u, isLeader) { 
  const p = unitProgress[u.id] || {};
  const accessible = canAccessUnit(u.id, currentRole);
  const done = p.passed;
  const cls = done ? 'complete' : (accessible ? '' : 'locked');
  const check = done ? '<span class="check">✓</span>' : '';
  const tag = isLeader ? '<span class="nav-leader-tag">LEADERSHIP</span>' : '';
  return `<button class="nav-btn ${cls}" onclick="navToUnit(${u.id},this)" id="navU${u.id}">
    <div class="num">${u.num}</div>
    <div class="nav-text">
      <span class="nav-title">${u.title}</span>
      <span class="nav-sub">${u.subtitle ? u.subtitle.slice(0,40)+'...' : ''}${tag}</span>
    </div>
    ${check}
  </button>`;
}

function navToUnit(uid, el) {
  if (!canAccessUnit(uid, currentRole)) {
    alert('Complete the previous unit quiz before unlocking this unit.');
    return;
  }
  const btn = el || document.getElementById('navU'+uid);
  showView('unit:'+uid, btn);
}

function updateProgress() {
  const units = getUnitsForRole(currentRole);
  const done = units.filter(u => unitProgress[u.id] && unitProgress[u.id].passed).length;
  const pct = Math.round((done / units.length) * 100);
  document.getElementById('progFill').style.width = pct + '%';
  document.getElementById('progLabel').textContent = pct + '% Complete (' + done + '/' + units.length + ' units)';

  // Refresh nav buttons
  units.forEach(u => {
    const btn = document.getElementById('navU'+u.id);
    if (!btn) return;
    const p = unitProgress[u.id] || {};
    btn.className = 'nav-btn' + (p.passed ? ' complete' : '') + (canAccessUnit(u.id, currentRole) ? '' : ' locked');
    const check = btn.querySelector('.check');
    if (p.passed && !check) {
      const span = document.createElement('span');
      span.className = 'check'; span.textContent = '✓';
      btn.appendChild(span);
    }
  });

  // Exam button
  const examSub = document.getElementById('examSub');
  if (demoMode) {
    examSub.textContent = 'Demo preview available';
  } else if (finalAssessmentStatus && finalAssessmentStatus.passed) {
    examSub.textContent = 'Passed — final assessment complete';
  } else if (allUnitsComplete(currentRole)) {
    examSub.textContent = 'Ready — take final assessment';
  } else {
    const remaining = units.filter(u => !unitProgress[u.id] || !unitProgress[u.id].passed).length;
    examSub.textContent = remaining + ' unit' + (remaining>1?'s':'') + ' remaining';
  }
}

// ═══════════════════════════════════════════════════════════════════
// VIEWS
// ═══════════════════════════════════════════════════════════════════
function goDashboard(event) {
  if (event) {
    try { event.preventDefault(); event.stopPropagation(); } catch (_) {}
  }
  currentView = 'dashboard';
  document.querySelectorAll('.nav-btn').forEach(b => b.classList.remove('active'));
  const dash = document.getElementById('navDash');
  if (dash) dash.classList.add('active');
  renderDashboard();
  try { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); } catch (_) { window.scrollTo(0,0); }
  return false;
}
window.goDashboard = goDashboard;

document.addEventListener('click', function(e){
  const target = e.target && e.target.closest ? e.target.closest('#navDash,[data-go-dashboard]') : null;
  if (!target) return;
  goDashboard(e);
});

function showView(view, el) {
  currentView = view;
  // Update sidebar active
  document.querySelectorAll('.nav-btn').forEach(b => b.classList.remove('active'));
  if (el) el.classList.add('active');

  if (view === 'dashboard') {
    goDashboard();
  } else if (view.startsWith('unit:')) {
    const uid = parseInt(view.split(':')[1]);
    renderUnit(uid);
  } else if (view === 'exam') {
    document.getElementById('navExam').classList.add('active');
    renderExam();
    } else if (view === 'soloroutetraining') {
  const btn = document.getElementById('navSoloRouteTraining');
  if (btn) btn.classList.add('active');
  renderSoloRouteAuthorizationTraining();
  } else if (view === 'competency') {
    const btn=document.getElementById('navCompetency');
    if(btn) btn.classList.add('active');
    renderCompetencyEvaluation();
  } else if (view === 'fieldtraining') {
    const btn=document.getElementById('navFieldTraining');
    if(btn) btn.classList.add('active');
    renderFieldTrainingReadiness();
  } else if (view === 'endorsements') {
    const btn=document.getElementById('navEndorsements');
    if(btn) btn.classList.add('active');
    renderOperationalEndorsements();
  } else if (view === 'authorization') {
    const btn=document.getElementById('navAuthorization');
    if(btn) btn.classList.add('active');
    renderOperationalAuthorization();
    } else if (view === 'workforce') {
  const btn = document.getElementById('navWorkforceReadiness');
  if (btn) btn.classList.add('active');
  renderWorkforceReadiness();
  } else if (view === 'recertification') {
    const btn=document.getElementById('navRecertification'); if(btn) btn.classList.add('active'); renderRecertificationTracking();
  }
}
async function renderWorkforceReadiness() {
    // Executive Demo must use simulated workforce data only.
  // Never load live employee qualification records while Demo Mode is active.
  if (demoMode && typeof renderDemoWorkforce === 'function') {
    renderDemoWorkforce();
    return;
  }
  const root = document.getElementById('contentInner');

  if (!root) return;

   if (
  !demoMode &&
  !['trainer', 'leadership', 'admin'].includes(currentProfileRole)
   ) {
    root.innerHTML =
      '<div class="comp-error">Workforce Readiness is available only to Leadership and Administrator roles.</div>';
    return;
  }
    const workforceReadinessEyebrow =
  currentProfileRole === 'trainer'
    ? 'LEADERSHIP WORKFORCE OVERSIGHT'
    : 'EXECUTIVE QUALIFICATION OVERSIGHT';
    const workforceReadinessSubtitle =
  currentProfileRole === 'trainer'
    ? 'Assigned workforce view of qualification readiness, service eligibility, training status, and renewal requirements.'
    : 'Enterprise view of workforce qualification, operational authorization, service scope, renewals, and controlled-document readiness.';
  root.innerHTML = `
    <div class="unit-header">
      <div class="unit-eyebrow">${workforceReadinessEyebrow}</div>
      <div class="unit-title">Workforce Readiness Command Center</div>
      <div class="unit-subtitle">
        ${workforceReadinessSubtitle}
      </div>
    </div>

    <div class="comp-notice">
      Loading controlled workforce qualification records…
    </div>
  `;

  try {
    const rows = await loadWorkforceReadinessWorkspace();

    const activeWorkforce = rows.length;

    const readyForAssignment = rows.filter(
      row => row.overall_status === 'ready'
    ).length;

    const actionRequired = rows.filter(row =>
  row.overall_status === 'action_required' ||
  Number(row.renewal_counts?.pending || 0) > 0
).length;

    const notAuthorized = rows.filter(
      row => row.overall_status === 'not_authorized'
    ).length;

        const renewalAttention = rows.reduce(
      (total, row) =>
        total +
        Number(row.renewal_counts?.due_soon || 0) +
        Number(row.renewal_counts?.expired || 0),
      0
    );

    // ═══════════════════════════════════════════════════════════════
    // WORKFORCE MANAGEMENT INDEX
    // Management navigation only.
    // Uses the existing production Workforce Readiness rows.
    // Does NOT create or recalculate qualification decisions.
    // ═══════════════════════════════════════════════════════════════

    const workforceIndexRows = rows.length
      ? rows.map(row => {
          const renewalCounts = row.renewal_counts || {};
          const documentCounts = row.document_counts || {};

          let statusLabel = 'NOT AUTHORIZED';
          let statusColor = '#FCA5A5';

          if (row.overall_status === 'ready') {
            statusLabel = 'READY';
            statusColor = '#4ADE80';
          } else if (row.overall_status === 'action_required') {
            statusLabel = 'ACTION REQUIRED';
            statusColor = '#FBBF24';
          }

          const issues = [];

          if (Number(documentCounts.overdue || 0) > 0) {
            issues.push('Documents Overdue');
          } else if (Number(documentCounts.outstanding || 0) > 0) {
            issues.push('Documents');
          }

          if (Number(renewalCounts.expired || 0) > 0) {
            issues.push('Renewal Expired');
          } else if (Number(renewalCounts.pending || 0) > 0) {
            issues.push('Renewal Pending');
          } else if (Number(renewalCounts.due_soon || 0) > 0) {
            issues.push('Renewal Due Soon');
          }

          if (
            !row.latest_authorization ||
            row.latest_authorization.status !== 'authorized'
          ) {
            issues.push('Authorization');
          }

          const issueText = issues.length
            ? issues.join(' · ')
            : 'No current exceptions';

          return `
            <div
              class="workforce-index-row"
              data-workforce-name="${esc(
                String(row.full_name || '').toLowerCase()
              )}"
              data-workforce-role="${esc(
                String(row.job_title || '').toLowerCase()
              )}"
              data-workforce-status="${esc(row.overall_status || '')}"
              data-workforce-documents="${
                Number(documentCounts.outstanding || 0) > 0 ||
                Number(documentCounts.overdue || 0) > 0
                  ? 'true'
                  : 'false'
              }"
              data-workforce-renewals="${
                Number(renewalCounts.pending || 0) > 0 ||
                Number(renewalCounts.due_soon || 0) > 0 ||
                Number(renewalCounts.expired || 0) > 0
                  ? 'true'
                  : 'false'
              }"
              data-workforce-authorization="${
                !row.latest_authorization ||
                row.latest_authorization.status !== 'authorized'
                  ? 'true'
                  : 'false'
              }"
              style="
                display:grid;
                grid-template-columns:minmax(180px,1.5fr) minmax(150px,1fr) minmax(145px,.8fr) minmax(180px,1.4fr) auto;
                gap:14px;
                align-items:center;
                padding:14px 16px;
                border-top:1px solid rgba(255,255,255,.08);
              "
            >
              <div>
                <div style="font-weight:800;color:var(--white)">
                  ${esc(row.full_name || 'Unnamed Employee')}
                </div>

                <div class="comp-history" style="margin-top:3px">
                  Employee Record
                </div>
              </div>

              <div class="comp-history">
                ${esc(row.job_title || 'Employee')}
              </div>

              <div>
                <span style="
                  display:inline-block;
                  border:1px solid ${statusColor};
                  color:${statusColor};
                  border-radius:999px;
                  padding:5px 9px;
                  font-size:10px;
                  font-weight:800;
                  letter-spacing:.05em;
                  white-space:nowrap;
                ">
                  ${statusLabel}
                </span>
              </div>

              <div class="comp-history">
                ${esc(issueText)}
              </div>

                            <button
                type="button"
                onclick="selectWorkforceEmployee('${esc(
                  String(row.user_id)
                )}')"
                style="
                  min-width:82px;
                  padding:9px 14px;
                  border:1px solid rgba(125,227,223,.45);
                  border-radius:8px;
                  background:rgba(125,227,223,.08);
                  color:#7DE3DF;
                  font:inherit;
                  font-size:12px;
                  font-weight:800;
                  cursor:pointer;
                  white-space:nowrap;
                  transition:
                    background .2s ease,
                    border-color .2s ease,
                    box-shadow .2s ease;
                "
                onmouseover="
                  this.style.background='rgba(125,227,223,.14)';
                  this.style.borderColor='#7DE3DF';
                  this.style.boxShadow='0 0 14px rgba(125,227,223,.18)';
                "
                onmouseout="
                  this.style.background='rgba(125,227,223,.08)';
                  this.style.borderColor='rgba(125,227,223,.45)';
                  this.style.boxShadow='none';
                "
              >
                Manage
              </button>
            </div>
          `;
        }).join('')
      : `
          <div class="comp-notice">
            No active employee workforce records were found.
          </div>
        `;

    const workforceManagementIndex = `
      <div
        class="comp-card workforce-management-index"
        style="padding:20px;margin-top:18px;margin-bottom:18px"
      >
        <div style="
          display:flex;
          justify-content:space-between;
          gap:16px;
          align-items:flex-start;
          flex-wrap:wrap;
          margin-bottom:16px;
        ">
          <div>
            <div class="comp-code">
              WORKFORCE MANAGEMENT INDEX
            </div>

            <div style="
              margin-top:5px;
              font-size:18px;
              font-weight:800;
              color:var(--white);
            ">
              Find & Manage Personnel
            </div>

            <div class="comp-history" style="margin-top:5px">
              Search and filter the active workforce, then open the
              employee's existing Qualification Passport.
            </div>
          </div>

          <div
            id="workforceIndexCount"
            class="comp-history"
            style="white-space:nowrap"
          >
            Showing ${rows.length} of ${rows.length}
          </div>
        </div>

        <div style="
          display:grid;
          grid-template-columns:minmax(220px,2fr) minmax(160px,1fr) minmax(160px,1fr);
          gap:10px;
          margin-bottom:16px;
        ">
          <input
            id="workforceSearchInput"
            type="search"
            placeholder="Search employee name or role..."
            oninput="filterWorkforceManagementIndex()"
            style="
              width:100%;
              padding:11px 12px;
              border-radius:8px;
              border:1px solid rgba(255,255,255,.14);
              background:rgba(255,255,255,.04);
              color:var(--white);
              font:inherit;
            "
          >

          <select
            id="workforceStatusFilter"
            onchange="filterWorkforceManagementIndex()"
            style="
              width:100%;
              padding:11px 12px;
              border-radius:8px;
              border:1px solid rgba(255,255,255,.14);
              background:#111827;
              color:var(--white);
              font:inherit;
            "
          >
            <option value="all">All Statuses</option>
            <option value="ready">Ready</option>
            <option value="action_required">Action Required</option>
            <option value="not_authorized">Not Authorized</option>
          </select>

          <select
            id="workforceIssueFilter"
            onchange="filterWorkforceManagementIndex()"
            style="
              width:100%;
              padding:11px 12px;
              border-radius:8px;
              border:1px solid rgba(255,255,255,.14);
              background:#111827;
              color:var(--white);
              font:inherit;
            "
          >
            <option value="all">All Issues</option>
            <option value="documents">Controlled Documents</option>
            <option value="renewals">Renewals</option>
            <option value="authorization">Authorization</option>
          </select>
        </div>

        <div style="overflow-x:auto">
          <div style="min-width:850px">
            <div style="
              display:grid;
              grid-template-columns:minmax(180px,1.5fr) minmax(150px,1fr) minmax(145px,.8fr) minmax(180px,1.4fr) auto;
              gap:14px;
              padding:0 16px 10px;
              color:var(--muted);
              font-size:10px;
              font-weight:800;
              letter-spacing:.08em;
              text-transform:uppercase;
            ">
              <div>Employee</div>
              <div>Role</div>
              <div>Status</div>
              <div>Primary Issue</div>
              <div>Record</div>
            </div>

            <div id="workforceIndexRows">
              ${workforceIndexRows}
            </div>

            <div
              id="workforceIndexEmpty"
              class="comp-notice"
              style="display:none;margin-top:12px"
            >
              No employees match the selected filters.
            </div>
          </div>
        </div>
      </div>
    `;

     const registerCards = rows.length
  ? rows.map(row => {
      const authStatus =
        row.latest_authorization?.status || 'not_authorized';

      let endorsementNames = 'None currently authorized';

if (
  row.authorized_endorsements_current &&
  row.authorized_endorsements_current.length
) {
  endorsementNames = row.authorized_endorsements_current
    .map(x => x.endorsement_name)
    .join(', ');
}

const renewalCounts = row.renewal_counts || {};

const renewalIssueCount =
  Number(renewalCounts.due_soon || 0) +
  Number(renewalCounts.expired || 0) +
  Number(renewalCounts.pending || 0);
  const documentCounts = row.document_counts || {};

let documentStatusLabel = 'NOT ASSIGNED';

if (row.document_readiness === 'current') {
  documentStatusLabel = 'CURRENT';
} else if (row.document_readiness === 'overdue') {
  documentStatusLabel = 'OVERDUE';
} else if (row.document_readiness === 'action_required') {
  documentStatusLabel = 'ACTION REQUIRED';
}

let overallLabel = 'NOT AUTHORIZED';
let overallColor = '#FCA5A5';

if (row.overall_status === 'ready') {
  overallLabel = 'READY FOR ASSIGNMENT';
  overallColor = '#4ADE80';
} else if (row.overall_status === 'action_required') {
  overallLabel = 'ACTION REQUIRED';
  overallColor = '#FBBF24';
}
       const actionReasons = [];

if (Number(renewalCounts.pending || 0) > 0) {
  actionReasons.push('Pending recertification requirement');
}

if (Number(renewalCounts.due_soon || 0) > 0) {
  actionReasons.push('Renewal due soon');
}

if (Number(renewalCounts.expired || 0) > 0) {
  actionReasons.push('Expired renewal requirement');
}

if (Number(documentCounts.overdue || 0) > 0) {
  actionReasons.push('Overdue controlled document');
} else if (Number(documentCounts.outstanding || 0) > 0) {
  actionReasons.push('Outstanding controlled document');
}

const actionReasonText = actionReasons.join(' · ');
          return `
        <div
          class="comp-card workforce-detail-card"
          data-workforce-detail-user="${esc(String(row.user_id))}"
          style="padding:20px;margin-top:14px;display:none"
        >
              <div style="
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:12px;
  margin-bottom:16px;
">
  <button
    type="button"
    onclick="backToWorkforceIndex()"
    style="
      background:transparent;
      border:1px solid rgba(125,227,223,.35);
      color:#7DE3DF;
      border-radius:8px;
      padding:8px 12px;
      font:inherit;
      font-size:12px;
      font-weight:800;
      cursor:pointer;
    "
  >
    ← Back to Workforce Readiness
  </button>

  <button
    type="button"
    onclick="closeWorkforceEmployeeCard()"
    style="
      background:transparent;
      border:1px solid rgba(255,255,255,.18);
      color:var(--white);
      border-radius:8px;
      padding:8px 12px;
      font:inherit;
      font-size:12px;
      font-weight:800;
      cursor:pointer;
    "
  >
    Close
  </button>
</div>
          <div style="
            display:flex;
            justify-content:space-between;
            align-items:flex-start;
            gap:18px;
            flex-wrap:wrap;
            padding-bottom:16px;
            border-bottom:1px solid rgba(255,255,255,.08);
          ">
            <div>
              <div style="
                font-size:18px;
                font-weight:800;
                color:var(--white);
              ">
                ${esc(row.full_name || 'Unnamed Employee')}
              </div>

              <div class="comp-history" style="margin-top:4px">
                ${esc(row.job_title || 'Employee')}
              </div>
              ${
               actionReasonText
                ? `
               <div class="comp-history" style="margin-top:8px;color:#FBBF24">
                Reason for Action: ${esc(actionReasonText)}
               </div>
                `
               : ''
               }
            </div>
              
            <div style="
              border:1px solid ${overallColor};
              border-radius:999px;
              padding:7px 12px;
              color:${overallColor};
              font-size:11px;
              font-weight:800;
              letter-spacing:.06em;
              white-space:nowrap;
            ">
              ${overallLabel}
            </div>
   <div style="
  display:grid;
  grid-template-columns:repeat(3,minmax(220px,1fr));
  gap:12px;
  margin-left:auto;
  width:min(100%,1050px);
">
          
<button
  type="button"
  onclick="openQualificationPassport('${row.user_id}')"
  style="
    border:1px solid rgba(91,211,207,.45);
    background:rgba(91,211,207,.08);
    color:#7DE3DF;
    border-radius:8px;
    padding:9px 14px;
    font-size:11px;
    font-weight:800;
    letter-spacing:.04em;
    cursor:pointer;
    white-space:nowrap;
  "
>
   View Authorization Details
</button>
<button
  class="secondary-btn"
  style="margin-left:10px"
  onclick="openEmployeeCompetencyEvaluation('${row.user_id}')"
>
  Competency Evaluation
</button>
 ${canManageRecertification() ? `
<button
  class="secondary-btn"
  style="margin-left:10px"
  onclick="openEmployeeRecertification('${row.user_id}')"
>
  Manage Renewals
</button>
` : ''}
${canIssueOperationalAuthorization() ? `
<button
  class="secondary-btn"
  style="margin-left:10px"
  onclick="openEmployeeOperationalAuthorization('${row.user_id}')"
>
  Operational Authorization
</button>
` : ''}

${canApproveEndorsements() ? `
<button
  class="secondary-btn"
  style="margin-left:10px"
  onclick="openEmployeeOperationalEndorsements('${row.user_id}')"
>
  Operational Endorsements
</button>
` : ''}
 <button
  class="secondary-btn"
  style="margin-left:10px"
  onclick="openEmployeeAssignmentEligibility('${row.user_id}')"
>
  Check Assignment Eligibility
</button>
          </div>

          </div>

          <div style="
            display:grid;
            grid-template-columns:repeat(auto-fit,minmax(170px,1fr));
            gap:12px;
            margin-top:16px;
          ">

            <div style="
              padding:14px;
              border:1px solid rgba(255,255,255,.07);
              border-radius:8px;
            ">
              <div class="comp-code">TRAINING FOUNDATION</div>

              <div style="margin-top:10px">
                <div class="comp-history">Final Assessment</div>
                <div style="font-weight:800;color:var(--white)">
                  ${row.final_passed ? 'PASS' : 'HOLD'}
                </div>
              </div>

               <div style="margin-top:10px">
           <div class="comp-history">Required Competencies</div>

        <div style="font-weight:800;color:var(--white)">
         ${Number(row.competent_count || 0)} / ${Number(row.required_count || 0)}
         </div>

         <button
          type="button"
        class="secondary-btn"
         style="margin-top:8px;width:100%"
         onclick="openPersonnelCompetencyRecord('${esc(String(row.user_id))}')"
        >
        View Competency Record
        </button>
        </div>

           <div style="margin-top:10px">
  <div class="comp-history">Field Readiness</div>
  <div style="font-weight:800;color:var(--white)">
    ${row.field_ready ? 'READY' : 'HOLD'}
  </div>

  ${canViewPersonnelReadinessRecord() ? `
    <button
      type="button"
      class="secondary-btn"
      style="margin-top:8px;width:100%"
      onclick="openPersonnelFieldTrainingRecord('${esc(String(row.user_id))}')"
    >
      View Field Training Record
    </button>
  ` : ''}
</div>
              <button
  type="button"
  class="secondary-btn"
  style="
    margin-top:14px;
    width:100%;
  "
  onclick="openEmployeeTrainingFoundation('${row.user_id}')"
>
  View Training Foundation
</button>
            </div>

            <div style="
              padding:14px;
              border:1px solid rgba(255,255,255,.07);
              border-radius:8px;
            ">
              <div class="comp-code">AUTHORIZED SERVICE SCOPE</div>

              <div style="
                margin-top:10px;
                font-weight:700;
                color:var(--white);
                line-height:1.5;
              ">
                ${esc(endorsementNames)}
              </div>
            </div>

            <div style="
              padding:14px;
              border:1px solid rgba(255,255,255,.07);
              border-radius:8px;
            ">
              <div class="comp-code">OPERATIONAL AUTHORIZATION</div>

              <div style="
                margin-top:10px;
                font-weight:800;
                color:var(--white);
              ">
                ${esc(
                  String(authStatus)
                    .replaceAll('_', ' ')
                    .toUpperCase()
                )}
              </div>

              <div class="comp-history" style="margin-top:8px">
                Final independent-operation status
              </div>
            </div>

            <div style="
              padding:14px;
              border:1px solid rgba(255,255,255,.07);
              border-radius:8px;
            ">
              <div class="comp-code">ONGOING CONTROLS</div>

              <div style="margin-top:10px">
  <div class="comp-history">Recertification & Renewals</div>
  <div style="font-weight:800;color:var(--white)">
    ${
      renewalIssueCount > 0
        ? renewalIssueCount +
          ' ACTION ITEM' +
          (renewalIssueCount === 1 ? '' : 'S')
        : 'CURRENT'
    }
  </div>
  <div class="comp-history" style="margin-top:6px">
  Pending ${Number(renewalCounts.pending || 0)}
  · Due Soon ${Number(renewalCounts.due_soon || 0)}
  · Expired ${Number(renewalCounts.expired || 0)}
</div>

  <div style="
    margin-top:18px;
    padding-top:16px;
    border-top:1px solid rgba(255,255,255,.08);
  ">
    <div class="comp-history">Controlled Documents</div>

    <div style="font-weight:800;color:var(--white);margin-top:4px">
      ${documentStatusLabel}
    </div>

    <div class="comp-history" style="margin-top:5px">
      ${Number(documentCounts.completed || 0)} /
      ${Number(documentCounts.total || 0)} completed
      ${
        Number(documentCounts.overdue || 0) > 0
          ? ' · ' + Number(documentCounts.overdue || 0) + ' overdue'
          : ''
      }
    </div>
    ${canViewPersonnelReadinessRecord() ? `
  <button
    type="button"
    class="secondary-btn"
    style="margin-top:10px;width:100%"
    onclick="openPersonnelDocumentRecord('${esc(String(row.user_id))}')"
  >
    View Document Record
  </button>
` : ''}
  </div>
</div>

          </div>
        </div>
        </div>
      `;
    }).join('')
  : `
      <div class="comp-notice" style="margin-top:14px">
        No active employee workforce records were found.
      </div>
    `;

    root.innerHTML = `
      <div class="unit-header">
        <div class="unit-eyebrow">${workforceReadinessEyebrow}</div>
        <div class="unit-title">Workforce Readiness Command Center</div>
        <div class="unit-subtitle">
          ${workforceReadinessSubtitle}
        </div>
      </div>

      <div class="stat-grid">
        <div class="stat-card">
          <div class="stat-num">${activeWorkforce}</div>
          <div class="stat-label">Active Workforce</div>
        </div>

        <div class="stat-card">
          <div class="stat-num">${readyForAssignment}</div>
          <div class="stat-label">Ready for Assignment</div>
        </div>

        <div class="stat-card">
          <div class="stat-num">${actionRequired}</div>
          <div class="stat-label">Action Required</div>
        </div>

        <div class="stat-card">
          <div class="stat-num">${notAuthorized}</div>
          <div class="stat-label">Not Authorized</div>
        </div>

        <div class="stat-card">
          <div class="stat-num">${renewalAttention}</div>
          <div class="stat-label">Renewals Due / Expired</div>
        </div>
      </div>
            </div>
                  ${workforceManagementIndex}
                   
                  ${registerCards}

       <div class="comp-notice" style="margin-top:18px">
        Workforce metrics are calculated from current production qualification,
authorization, endorsement, recertification, and controlled-document records.
      </div>
    `;

  } catch (error) {
    console.error('Workforce Readiness error:', error);

    root.innerHTML = `
      <div class="unit-header">
        <div class="unit-eyebrow">EXECUTIVE QUALIFICATION OVERSIGHT</div>
        <div class="unit-title">Workforce Readiness Command Center</div>
      </div>

      <div class="comp-error">
        ${esc(error?.message || 'Unable to load Workforce Readiness records.')}
      </div>
    `;
  }
}

 function filterWorkforceManagementIndex() {
  const searchInput =
    document.getElementById('workforceSearchInput');

  const statusFilter =
    document.getElementById('workforceStatusFilter');

  const issueFilter =
    document.getElementById('workforceIssueFilter');

  const rows =
    Array.from(
      document.querySelectorAll('.workforce-index-row')
    );

  const search =
    String(searchInput?.value || '')
      .trim()
      .toLowerCase();

  const status =
    statusFilter?.value || 'all';

  const issue =
    issueFilter?.value || 'all';

  let visibleCount = 0;

  rows.forEach(row => {
    const name =
      row.dataset.workforceName || '';

    const role =
      row.dataset.workforceRole || '';

    const rowStatus =
      row.dataset.workforceStatus || '';

    const matchesSearch =
      !search ||
      name.includes(search) ||
      role.includes(search);

    const matchesStatus =
      status === 'all' ||
      rowStatus === status;

    let matchesIssue = true;

    if (issue === 'documents') {
      matchesIssue =
        row.dataset.workforceDocuments === 'true';
    }

    if (issue === 'renewals') {
      matchesIssue =
        row.dataset.workforceRenewals === 'true';
    }

    if (issue === 'authorization') {
      matchesIssue =
        row.dataset.workforceAuthorization === 'true';
    }

    const visible =
      matchesSearch &&
      matchesStatus &&
      matchesIssue;

    row.style.display =
      visible ? 'grid' : 'none';

    if (visible) {
      visibleCount++;
    }
  });

  const count =
    document.getElementById('workforceIndexCount');

  if (count) {
    count.textContent =
      'Showing ' +
      visibleCount +
      ' of ' +
      rows.length;
  }

  const empty =
    document.getElementById('workforceIndexEmpty');

  if (empty) {
    empty.style.display =
      rows.length > 0 && visibleCount === 0
        ? 'block'
        : 'none';
  }
}
function canViewPersonnelReadinessRecord() {
  return (
    !demoMode &&
    ['leadership', 'admin'].includes(currentProfileRole)
  );
}

window.canViewPersonnelReadinessRecord =
  canViewPersonnelReadinessRecord;
   async function openPersonnelDocumentRecord(userId) {
  if (!canViewPersonnelReadinessRecord()) {
    alert('Personnel Readiness document records are available only to Leadership and Administrator roles.');
    return;
  }

  const employee = workforceReadinessRows.find(
    row => String(row.user_id) === String(userId)
  );

  if (!employee) {
    alert('Unable to locate this employee readiness record.');
    return;
  }

  const evidence = Array.isArray(employee.document_evidence)
    ? employee.document_evidence
    : [];

  const root = document.getElementById('contentInner');
  if (!root) return;

  // Presentation-only categorization. Reuses the document_code naming
  // convention already in use elsewhere in this system (e.g. the
  // Executive Demo's isOccupationalHealthRecordTemplate() BBP check).
  // No new data source, no new status calculation.
  function categorizeDocumentCode(code) {
    const c = String(code || '');
    if (/^CHL-POL-/.test(c)) return 'POLICIES';
    if (/^CHL-SOP-/.test(c)) return 'SOPS';
    if (/^CHL-BBP-/.test(c) || /^CHL-ECP-/.test(c)) return 'BBP / OCCUPATIONAL EXPOSURE DOCUMENTS';
    return 'OTHER CONTROLLED DOCUMENTS';
  }

  const categoryOrder = [
    'POLICIES',
    'SOPS',
    'BBP / OCCUPATIONAL EXPOSURE DOCUMENTS',
    'OTHER CONTROLLED DOCUMENTS'
  ];

  const grouped = {
    'POLICIES': [],
    'SOPS': [],
    'BBP / OCCUPATIONAL EXPOSURE DOCUMENTS': [],
    'OTHER CONTROLLED DOCUMENTS': []
  };

  evidence.forEach(item => {
    grouped[categorizeDocumentCode(item.document_code)].push(item);
  });

  // Unchanged per-document status calculation, exactly as it existed
  // in the prior single-list version — not modified, just reused
  // per item inside each category's render.
  function renderDocumentItem(item) {
    const status =
      item.acknowledged ||
      item.status === 'completed' ||
      item.completed_at
        ? 'COMPLETED'
        : (
            item.due_at &&
            new Date(item.due_at).getTime() < Date.now()
          )
          ? 'OVERDUE'
          : 'ACTION REQUIRED';

    const assignedAt = item.assigned_at
      ? new Date(item.assigned_at).toLocaleString()
      : '—';

    const dueAt = item.due_at
      ? new Date(item.due_at).toLocaleString()
      : '—';

    const acknowledgedAt = item.acknowledged_at
      ? new Date(item.acknowledged_at).toLocaleString()
      : (
          item.completed_at
            ? new Date(item.completed_at).toLocaleString()
            : '—'
        );

    return `
      <div class="comp-card" style="margin-top:12px">
        <div class="comp-code">
          ${esc(item.document_code || 'Unknown')}
          · VERSION ${esc(String(item.version_number || '—'))}
        </div>

        <div class="comp-name" style="margin-top:5px">
          ${esc(item.title || 'Controlled Document')}
        </div>

        <div class="comp-history" style="margin-top:8px">
          Status: ${esc(status)}
        </div>

        <div class="comp-history">
          Assigned: ${esc(assignedAt)}
        </div>

        <div class="comp-history">
          Due: ${esc(dueAt)}
        </div>

        <div class="comp-history">
          Acknowledged / Completed: ${esc(acknowledgedAt)}
        </div>

        <div class="comp-history">
          Effective Date:
          ${
            item.effective_date
              ? esc(new Date(item.effective_date).toLocaleDateString())
              : '—'
          }
        </div>
      </div>
    `;
  }

  function renderCategorySection(categoryName, items) {
    if (!items.length) return '';

    const acknowledgedCount = items.filter(item =>
      item.acknowledged || item.status === 'completed' || item.completed_at
    ).length;

    const outstandingCount = items.length - acknowledgedCount;

    return `
      <div class="comp-card" style="margin-top:16px;padding:16px">
        <div class="comp-code">${esc(categoryName)} · ${items.length}</div>
        <div class="comp-history" style="margin-top:6px">
          Assigned ${items.length} · Acknowledged ${acknowledgedCount} · Outstanding ${outstandingCount}
        </div>
        ${items.map(renderDocumentItem).join('')}
      </div>
    `;
  }

  const groupedSectionsHtml = categoryOrder
    .map(category => renderCategorySection(category, grouped[category]))
    .join('');

  const documentRows = evidence.length
    ? groupedSectionsHtml
    : `
        <div class="comp-notice" style="margin-top:14px">
          No controlled documents are currently assigned to this employee.
        </div>
      `;

  root.innerHTML = `
    <div class="unit-header">
      <div class="unit-eyebrow">
        PERSONNEL READINESS RECORD · CONTROLLED DOCUMENTS
      </div>

      <div class="unit-title">
        ${esc(employee.full_name || 'Employee')}
      </div>

      <div class="unit-subtitle">
        Document-by-document assignment and acknowledgment evidence from the current Workforce Readiness record.
      </div>
    </div>

    <div class="comp-card" style="margin-top:16px">
      <div class="comp-code">DOCUMENT READINESS SUMMARY</div>

      <div class="comp-history" style="margin-top:8px">
        ${Number(employee.document_counts?.completed || 0)}
        /
        ${Number(employee.document_counts?.total || 0)}
        completed
        ${
          Number(employee.document_counts?.overdue || 0) > 0
            ? ' · ' +
              Number(employee.document_counts.overdue || 0) +
              ' overdue'
            : ''
        }
      </div>
    </div>

    ${documentRows}

    <div style="margin-top:18px">
      <button
        type="button"
        class="secondary-btn"
        onclick="returnToPersonnelRecord('${esc(String(userId))}')"
      >
        ← Back to Employee Record
      </button>
    </div>
  `;
}

window.openPersonnelDocumentRecord =
  openPersonnelDocumentRecord;

  async function openPersonnelCompetencyRecord(userId) {
  if (!canViewPersonnelReadinessRecord()) {
    alert(
      'Personnel Readiness competency records are available only to Leadership and Administrator roles.'
    );
    return;
  }

  const employee = workforceReadinessRows.find(
    row => String(row.user_id) === String(userId)
  );

  if (!employee) {
    alert('Unable to locate this employee readiness record.');
    return;
  }

  const records = Array.isArray(employee.competency_records)
    ? employee.competency_records
    : [];

  const root = document.getElementById('contentInner');
  if (!root) return;

  const competencyRows = records.length
    ? records.map(item => {
        const record = item?.record || null;

        const result = String(
          record?.result || 'not_evaluated'
        ).toUpperCase();

        const evaluatedAt = record?.evaluated_at
          ? new Date(record.evaluated_at).toLocaleString()
          : '—';

        return `
          <div class="comp-card" style="margin-top:12px">
            <div class="comp-code">
              ${esc(String(item?.competency_code || 'Unknown'))}
            </div>

            <div class="comp-history" style="margin-top:8px">
              Result: ${esc(result)}
            </div>

            <div class="comp-history" style="margin-top:5px">
              Evaluated: ${esc(evaluatedAt)}
            </div>
          </div>
        `;
      }).join('')
    : `
        <div class="comp-notice" style="margin-top:14px">
          No required competency records were found for this employee.
        </div>
      `;

  root.innerHTML = `
    <div class="unit-header">
      <div class="unit-eyebrow">PERSONNEL READINESS RECORD</div>
      <div class="unit-title">Competency Record</div>
      <div class="unit-subtitle">
        ${esc(String(employee.full_name || 'Employee'))}
      </div>
    </div>

    <div class="comp-card" style="margin-top:14px">
      <div class="comp-code">REQUIRED COMPETENCIES</div>

      <div class="comp-history" style="margin-top:8px">
        ${Number(employee.competent_count || 0)}
        /
        ${Number(employee.required_count || 0)}
        competent
      </div>
    </div>

    ${competencyRows}

    <div style="margin-top:18px">
      <button
        type="button"
        class="secondary-btn"
        onclick="returnToPersonnelRecord('${esc(String(userId))}')"
      >
        ← Back to Employee Record
      </button>
    </div>
  `;
}

window.openPersonnelCompetencyRecord =
  openPersonnelCompetencyRecord;
  async function openPersonnelFieldTrainingRecord(userId) {
  if (!canViewPersonnelReadinessRecord()) {
    alert('Personnel Readiness field-training records are available only to Leadership and Administrator roles.');
    return;
  }

  const employee = workforceReadinessRows.find(
    row => String(row.user_id) === String(userId)
  );

  if (!employee) {
    alert('Unable to locate this employee readiness record.');
    return;
  }

  const root = document.getElementById('contentInner');
  if (!root) return;

  root.innerHTML = '<div class="comp-notice">Loading field-training record…</div>';

  try {
    const client = getSupabaseClient();

    const result = await client
      .from('field_training_records')
      .select('id,user_id,trainer_id,route_or_assignment,readiness_result,observed_at,evidence_reference,notes,created_at')
      .eq('user_id', userId)
      .order('observed_at', { ascending: false });

    if (result.error) throw result.error;

    const records = result.data || [];
    const latest = records[0] || null;

    function renderRecordFields(record) {
      return `
        <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:14px;margin-top:12px">
          <div>
            <div class="comp-history">Readiness Result</div>
            <div style="font-weight:800;color:var(--white);margin-top:4px">
              ${esc(String(record.readiness_result || '—').replaceAll('_',' ').toUpperCase())}
            </div>
          </div>
          <div>
            <div class="comp-history">Observed Date</div>
            <div style="font-weight:800;color:var(--white);margin-top:4px">
              ${record.observed_at ? esc(new Date(record.observed_at).toLocaleString()) : '—'}
            </div>
          </div>
          <div>
            <div class="comp-history">Route / Assignment</div>
            <div style="font-weight:800;color:var(--white);margin-top:4px">
              ${esc(record.route_or_assignment || '—')}
            </div>
          </div>
          <div>
            <div class="comp-history">Evidence Reference</div>
            <div style="font-weight:800;color:var(--white);margin-top:4px">
              ${esc(record.evidence_reference || '—')}
            </div>
          </div>
        </div>
        ${record.notes ? `
          <div style="margin-top:12px">
            <div class="comp-history">Observer Notes</div>
            <div style="margin-top:4px;color:var(--white)">${esc(record.notes)}</div>
          </div>` : ''}
      `;
    }

    const latestSectionHtml = latest
      ? `
        <div class="comp-card" style="margin-top:16px;padding:18px">
          <div class="comp-code">LATEST FIELD TRAINING RECORD</div>
          ${renderRecordFields(latest)}
        </div>`
      : `
        <div class="comp-card" style="margin-top:16px;padding:18px;border-left:3px solid #FCA5A5">
          <div class="comp-code" style="color:#FCA5A5">NO FIELD TRAINING RECORD</div>
          <div class="comp-history" style="margin-top:8px">
            No field-training observation has been recorded for this employee.
          </div>
        </div>`;

    const historyRowsHtml = records.length
      ? records.map((record, idx) => `
          <div class="comp-card" style="margin-top:12px;padding:16px">
            <div class="comp-code">${idx === 0 ? 'MOST RECENT' : 'RECORD'} · ${record.observed_at ? esc(new Date(record.observed_at).toLocaleDateString()) : 'Undated'}</div>
            ${renderRecordFields(record)}
          </div>
        `).join('')
      : '';

    root.innerHTML = `
      <div class="unit-header">
        <div class="unit-eyebrow">
          PERSONNEL READINESS RECORD · FIELD TRAINING
        </div>
        <div class="unit-title">
          ${esc(employee.full_name || 'Employee')}
        </div>
        <div class="unit-subtitle">
          Field-training observation evidence from the existing production record. Evidence review only — no evaluation action is taken from this screen.
        </div>
      </div>

      ${latestSectionHtml}

      ${records.length ? `
        <div class="comp-card" style="margin-top:18px;padding:18px">
          <div class="comp-code">FIELD TRAINING HISTORY · ${records.length} RECORD${records.length === 1 ? '' : 'S'}</div>
          ${historyRowsHtml}
        </div>` : ''}

      <div style="margin-top:18px">
        <button
          type="button"
          class="secondary-btn"
          onclick="returnToPersonnelRecord('${esc(String(userId))}')"
        >
          ← Back to Employee Record
        </button>
      </div>
    `;
    window.scrollTo({ top: 0, behavior: 'smooth' });

  } catch (err) {
    root.innerHTML = `
      <div class="comp-error">${esc(err?.message || 'Unable to load field-training record.')}</div>
      <div style="margin-top:18px">
        <button type="button" class="secondary-btn" onclick="returnToPersonnelRecord('${esc(String(userId))}')">← Back to Employee Record</button>
      </div>
    `;
  }
}

window.openPersonnelFieldTrainingRecord = openPersonnelFieldTrainingRecord;
 function selectWorkforceEmployee(userId) {
  const targetId = String(userId).trim();

  document
    .querySelectorAll('.workforce-detail-card')
    .forEach(card => {
      const cardId = String(
        card.getAttribute('data-workforce-detail-user') || ''
      ).trim();

      card.style.display =
        cardId === targetId ? 'block' : 'none';
    });
}
function closeWorkforceEmployeeCard() {
  document
    .querySelectorAll('.workforce-detail-card')
    .forEach(card => {
      card.style.display = 'none';
    });
}

function backToWorkforceIndex() {
  closeWorkforceEmployeeCard();

  const index =
    document.querySelector('.workforce-management-index');

  if (index) {
    index.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
}
function openQualificationPassport(userId) {
  const employee = (workforceReadinessRows || []).find(
    row => String(row.user_id) === String(userId)
  );

  if (!employee) {
    alert('Qualification Passport record could not be found.');
    return;
  }
   const qualificationProgressSteps =
  employee.qualification_progress_steps || [];

 const nextAction =
  employee.next_action || null;
  const existing = document.getElementById('qualificationPassportOverlay');
  if (existing) existing.remove();

  const authStatus =
    employee.latest_authorization &&
    employee.latest_authorization.status
      ? employee.latest_authorization.status
      : 'not_authorized';

  const serviceScope =
    employee.authorized_endorsements_current &&
    employee.authorized_endorsements_current.length
      ? employee.authorized_endorsements_current
          .map(item => item.endorsement_name)
          .join(', ')
          : 'None currently authorized';
          const documentEvidence =
  employee.document_evidence || [];

const documentCounts =
  employee.document_counts || {
    total: 0,
    completed: 0,
    outstanding: 0,
    overdue: 0,
    waived: 0
  };
  const recertificationItems =
  employee.recertification_items || [];

const renewalCounts =
  employee.renewal_counts || {
    current: 0,
    due_soon: 0,
    expired: 0,
    pending: 0,
    not_required: 0
  };
    let passportStatusLabel = 'NOT AUTHORIZED';
let passportStatusColor = '#FCA5A5';

if (employee.overall_status === 'ready') {
  passportStatusLabel = 'READY FOR ASSIGNMENT';
  passportStatusColor = '#4ADE80';
} else if (employee.overall_status === 'action_required') {
  passportStatusLabel = 'ACTION REQUIRED';
  passportStatusColor = '#FBBF24';
}
const authorizationRecord = employee.latest_authorization || null;

let authorizationRecordedAt = 'Not recorded';
let qualificationFileReference = 'Not recorded';
let qualificationFileAudit = 'Not confirmed';

if (authorizationRecord) {
  const recordDate =
    authorizationRecord.authorized_at ||
    authorizationRecord.created_at;

  if (recordDate) {
    authorizationRecordedAt =
      new Date(recordDate).toLocaleString();
  }

  if (authorizationRecord.qualification_file_reference) {
    qualificationFileReference =
      authorizationRecord.qualification_file_reference;
  }

  if (authorizationRecord.qualification_file_audited === true) {
    qualificationFileAudit = 'Confirmed';
  }
  }
  const overlay = document.createElement('div');

  overlay.id = 'qualificationPassportOverlay';

  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:99999',
    'background:rgba(3,10,20,.88)',
    'display:flex',
    'align-items:center',
    'justify-content:center',
    'padding:24px'
  ].join(';');

  overlay.innerHTML = `
    <div style="
      width:min(900px,100%);
      max-height:90vh;
      overflow:auto;
      background:#0F1D31;
      color:#F7FAFC;
      border:1px solid rgba(91,211,207,.28);
      border-radius:14px;
      padding:26px;
      box-shadow:0 24px 70px rgba(0,0,0,.45);
    ">

      <div style="
        display:flex;
        justify-content:space-between;
        align-items:flex-start;
        gap:20px;
        padding-bottom:18px;
        border-bottom:1px solid rgba(255,255,255,.08);
      ">
        <div>
          <div class="unit-eyebrow">
            CONTROLLED QUALIFICATION RECORD
          </div>

          <div style="
            font-size:26px;
            font-weight:800;
            color:var(--white);
            margin-top:6px;
          ">
            ${esc(employee.full_name || 'Unnamed Employee')}
          </div>

          <div class="comp-history" style="margin-top:4px">
            ${esc(employee.job_title || 'Employee')}
          </div>
          <div style="
  display:inline-block;
  margin-top:12px;
  padding:7px 12px;
  border:1px solid ${passportStatusColor};
  border-radius:999px;
  color:${passportStatusColor};
  font-size:11px;
  font-weight:800;
  letter-spacing:.06em;
  white-space:nowrap;
">
  ${passportStatusLabel}
</div>

</div>
      
        <button
          type="button"
          onclick="closeQualificationPassport()"
          style="
            background:transparent;
            border:1px solid rgba(255,255,255,.18);
            color:var(--white);
            border-radius:8px;
            padding:8px 12px;
            cursor:pointer;
          "
        >
          Close
        </button>
      </div>

      <div style="
        display:grid;
      grid-template-columns:.9fr .9fr 1fr 1.15fr 1.15fr;
        gap:14px;
        margin-top:20px;
      ">

        <div class="comp-card">
          <div class="comp-code">FINAL ASSESSMENT</div>
          <div style="font-size:22px;font-weight:800;margin-top:8px">
            ${employee.final_passed ? 'PASS' : 'HOLD'}
          </div>
        </div>

        <div class="comp-card">
          <div class="comp-code">COMPETENCIES</div>
          <div style="font-size:22px;font-weight:800;margin-top:8px">
            ${Number(employee.competent_count || 0)} /
            ${Number(employee.required_count || 0)}
          </div>
        </div>

        <div class="comp-card">
          <div class="comp-code">FIELD READINESS</div>
          <div style="font-size:22px;font-weight:800;margin-top:8px">
            ${employee.field_ready ? 'READY' : 'HOLD'}
          </div>
        </div>

        <div class="comp-card">
  <div class="comp-code">OPERATIONAL AUTHORIZATION</div>
  <div style="font-size:22px;font-weight:800;margin-top:8px">
    ${esc(String(authStatus).replaceAll('_',' ').toUpperCase())}
  </div>
</div>

<div class="comp-card">
  <div class="comp-code">CONTROLLED DOCUMENTS</div>

  <div style="
    font-size:18px;
    font-weight:800;
    margin-top:8px;
  ">
    ${
      employee.document_readiness === 'current'
        ? 'CURRENT'
        : employee.document_readiness === 'overdue'
          ? 'OVERDUE'
          : employee.document_readiness === 'action_required'
            ? 'ACTION REQUIRED'
            : 'UNASSIGNED'
    }
  </div>

  
</div>

</div>

      <div class="comp-card" style="margin-top:14px">
        <div class="comp-code">AUTHORIZED SERVICE SCOPE</div>

        <div style="
          font-size:18px;
          font-weight:700;
          color:var(--white);
          margin-top:8px;
        ">
          ${esc(serviceScope)}
        </div>
      </div>
      <div class="comp-card" style="margin-top:14px">
  <div class="comp-code">CONTROLLED DOCUMENT RECORD</div>

  <div style="
    display:grid;
    grid-template-columns:repeat(4,minmax(0,1fr));
    gap:14px;
    margin-top:14px;
  ">
    <div>
      <div class="comp-history">Assigned</div>
      <div style="margin-top:5px;font-weight:700;color:var(--white)">
        ${Number(documentCounts.total || 0)}
      </div>
    </div>

    <div>
      <div class="comp-history">Completed</div>
      <div style="margin-top:5px;font-weight:700;color:var(--white)">
        ${Number(documentCounts.completed || 0)}
      </div>
    </div>

    <div>
      <div class="comp-history">Outstanding</div>
      <div style="margin-top:5px;font-weight:700;color:var(--white)">
        ${Number(documentCounts.outstanding || 0)}
      </div>
    </div>

    <div>
      <div class="comp-history">Overdue</div>
      <div style="margin-top:5px;font-weight:700;color:var(--white)">
        ${Number(documentCounts.overdue || 0)}
      </div>
    </div>
  </div>

  ${
    documentEvidence.length
      ? documentEvidence.map(item => `
          <div style="
            margin-top:16px;
            padding-top:16px;
            border-top:1px solid rgba(255,255,255,.08);
          ">
            <div style="
              font-size:14px;
              font-weight:800;
              color:var(--white);
            ">
              ${esc(item.document_code)} · Version ${esc(item.version_number)}
            </div>

            <div class="comp-history" style="margin-top:5px">
              ${esc(item.title)}
            </div>

            <div class="comp-history" style="margin-top:8px">
              Status:
              ${
                item.acknowledged
                  ? 'Acknowledged'
                  : String(item.status || 'Assigned')
                      .replaceAll('_',' ')
              }
            </div>

            ${
              item.acknowledged_at
                ? `
                  <div class="comp-history" style="margin-top:4px">
                    Acknowledged ${esc(new Date(item.acknowledged_at).toLocaleString())}
                  </div>
                `
                : ''
            }
          </div>
        `).join('')
      : `
        <div class="comp-history" style="margin-top:16px">
          No controlled-document assignments are currently recorded for this employee.
        </div>
      `
  }
</div>
<div class="comp-card" style="margin-top:14px">
  <div class="comp-code">RECERTIFICATION & RENEWAL RECORD</div>

  <div style="
    display:grid;
    grid-template-columns:repeat(5,minmax(0,1fr));
    gap:14px;
    margin-top:14px;
  ">
    <div>
      <div class="comp-history">Current</div>
      <div style="margin-top:5px;font-weight:700;color:var(--white)">
        ${Number(renewalCounts.current || 0)}
      </div>
    </div>

    <div>
      <div class="comp-history">Due Soon</div>
      <div style="margin-top:5px;font-weight:700;color:var(--white)">
        ${Number(renewalCounts.due_soon || 0)}
      </div>
    </div>

    <div>
      <div class="comp-history">Expired</div>
      <div style="margin-top:5px;font-weight:700;color:var(--white)">
        ${Number(renewalCounts.expired || 0)}
      </div>
    </div>

    <div>
      <div class="comp-history">Pending</div>
      <div style="margin-top:5px;font-weight:700;color:var(--white)">
        ${Number(renewalCounts.pending || 0)}
      </div>
    </div>

    <div>
      <div class="comp-history">Not Required</div>
      <div style="margin-top:5px;font-weight:700;color:var(--white)">
        ${Number(renewalCounts.not_required || 0)}
      </div>
    </div>
  </div>

  ${
    recertificationItems.length
      ? recertificationItems.map(item => `
          <div style="
            margin-top:16px;
            padding-top:16px;
            border-top:1px solid rgba(255,255,255,.08);
          ">
            <div style="
              font-size:14px;
              font-weight:800;
              color:var(--white);
            ">
              ${esc(item.requirement_code || 'Requirement')}
            </div>

            <div class="comp-history" style="margin-top:5px">
              ${esc(item.requirement_name || 'Recertification Requirement')}
            </div>

            <div class="comp-history" style="margin-top:8px">
              Status:
              ${esc(
                String(item.status || 'pending')
                  .replaceAll('_',' ')
                  .toUpperCase()
              )}
            </div>

            ${
              item.completed_at
                ? `
                  <div class="comp-history" style="margin-top:4px">
                    Completed ${esc(new Date(item.completed_at).toLocaleDateString())}
                  </div>
                `
                : ''
            }

            ${
              item.due_at
                ? `
                  <div class="comp-history" style="margin-top:4px">
                    Due ${esc(new Date(item.due_at).toLocaleDateString())}
                  </div>
                `
                : ''
            }

            ${
              item.evidence_reference
                ? `
                  <div class="comp-history" style="margin-top:4px">
                    Evidence: ${esc(item.evidence_reference)}
                  </div>
                `
                : ''
            }
          </div>
        `).join('')
      : `
        <div class="comp-history" style="margin-top:16px">
          No recertification or renewal requirements are currently recorded for this employee.
        </div>
      `
  }
</div>
      <div class="comp-card" style="margin-top:14px">
  <div class="comp-code">OPERATIONAL AUTHORIZATION RECORD</div>

  <div style="
    display:grid;
    grid-template-columns:repeat(3,minmax(0,1fr));
    gap:14px;
    margin-top:14px;
  ">

    <div>
      <div class="comp-history">Decision Recorded</div>
      <div style="margin-top:5px;font-weight:700;color:var(--white)">
        ${esc(authorizationRecordedAt)}
      </div>
    </div>

    <div>
      <div class="comp-history">Qualification File Audit</div>
      <div style="margin-top:5px;font-weight:700;color:var(--white)">
        ${esc(qualificationFileAudit)}
      </div>
    </div>

    <div>
      <div class="comp-history">Qualification File Reference</div>
      <div style="margin-top:5px;font-weight:700;color:var(--white)">
        ${esc(qualificationFileReference)}
      </div>
    </div>

  </div>
   
</div>

    
 </div>
 
${(function(){
  // ADDITIVE — Authorization / Readiness Explanation.
  // Pure read-only consumption of window.explainQualificationStatus()
  // (corevian-university-authorization-explanation.js). This section
  // does not alter employee, authorizationRecord, or any other value
  // used above; it only reads the same already-loaded employee row.
  if (typeof window.explainQualificationStatus !== 'function') return '';

  const explanation = window.explainQualificationStatus(employee);
  const isBlocked = !explanation.authorized;

  const headingText = isBlocked
    ? 'WHY THIS EMPLOYEE IS NOT AUTHORIZED'
    : 'QUALIFICATION DETERMINATION';

  const headingColor = isBlocked ? '#FCA5A5' : '#4ADE80';

  function renderItem(item, color) {
    return `
      <div style="margin-top:14px;padding-top:14px;border-top:1px solid rgba(255,255,255,.08)">
        <div style="display:flex;justify-content:space-between;gap:12px;flex-wrap:wrap;align-items:baseline">
          <div style="font-size:14px;font-weight:800;color:var(--white)">${esc(item.control)}</div>
          <div style="font-size:12px;font-weight:800;color:${color}">${esc(item.status)}</div>
        </div>

        <div class="comp-history" style="margin-top:6px">
          ${esc(item.explanation)}
        </div>

        <div class="comp-history" style="margin-top:4px;color:var(--white)">
          <strong>Next Required Action:</strong> ${esc(item.nextAction)}
        </div>
      </div>`;
  }

  const blockersHtml = explanation.blockers
    .map(b => renderItem(b, '#FCA5A5'))
    .join('');

  const warningsHtml = explanation.warnings
    .map(w => renderItem(w, '#FBBF24'))
    .join('');

  const positiveBody = explanation.serviceScope.length
    ? `<div class="comp-history" style="margin-top:10px">Required controls are satisfied for: <strong style="color:var(--white)">${esc(explanation.serviceScope.join(', '))}</strong>.</div>`
    : `<div class="comp-history" style="margin-top:10px">Required controls are satisfied for the applicable authorized service scope.</div>`;

  return `
    <div class="comp-card" style="margin-top:14px;border-left:3px solid ${headingColor}">
      <div class="comp-code" style="color:${headingColor}">
        ${headingText}
      </div>

      <div style="margin-top:8px;font-weight:800;color:var(--white)">
        ${esc(explanation.summary)}
      </div>

      ${isBlocked ? blockersHtml : positiveBody}
      ${warningsHtml}
    </div>`;
})()}

      </div>
  `;

  overlay.addEventListener('click', function(event) {
    if (event.target === overlay) {
      closeQualificationPassport();
    }
  });

  document.body.appendChild(overlay);
}

function closeQualificationPassport() {
  const overlay = document.getElementById('qualificationPassportOverlay');

  if (overlay) {
    overlay.remove();
  }
}

window.openQualificationPassport = openQualificationPassport;
window.closeQualificationPassport = closeQualificationPassport;

let workforceReadinessRows = [];

async function loadWorkforceReadinessWorkspace() {
  const client = getSupabaseClient();

   const [
  ,
  ,
  documentAssignmentRes,
  documentAckRes,
  documentVersionRes,
  controlledDocumentRes
] = await Promise.all([
    loadOperationalAuthorizationWorkspace(),
    loadRecertificationWorkspace(),

    client
  .from('document_assignments')
  .select('id,user_id,document_version_id,assigned_at,due_at,status,completed_at')
  .order('assigned_at', { ascending: false }),

    client
  .from('document_acknowledgments')
  .select('id,user_id,document_version_id,acknowledged_at')
  .order('acknowledged_at', { ascending: false }),

client
  .from('controlled_document_versions')
  .select('id,document_id,version_number,status,effective_date')
  .eq('status', 'approved'),

client
  .from('controlled_documents')
  .select('id,document_code,title,active')
  .eq('active', true)
]);

  if (documentAssignmentRes.error) throw documentAssignmentRes.error;
  if (documentAckRes.error) throw documentAckRes.error;
  if (documentVersionRes.error) throw documentVersionRes.error;
  if (controlledDocumentRes.error) throw controlledDocumentRes.error;

  const documentAssignments =
    documentAssignmentRes.data || [];

  const documentAcknowledgments =
    documentAckRes.data || [];
   const documentVersions =
  documentVersionRes.data || [];

const controlledDocuments =
  controlledDocumentRes.data || [];

const documentVersionById = new Map(
  documentVersions.map(row => [row.id, row])
);

const controlledDocumentById = new Map(
  controlledDocuments.map(row => [row.id, row])
);
  const documentAckKeys = new Set(
    documentAcknowledgments.map(
      row => row.user_id + '|' + row.document_version_id
    )
  );
  const documentAckByKey = new Map();

documentAcknowledgments.forEach(row => {
  const key =
    row.user_id + '|' + row.document_version_id;

  if (!documentAckByKey.has(key)) {
    documentAckByKey.set(key, row);
  }
});

  const documentAssignmentsByUser = new Map();

  documentAssignments.forEach(row => {
    if (!documentAssignmentsByUser.has(row.user_id)) {
      documentAssignmentsByUser.set(row.user_id, []);
    }

    documentAssignmentsByUser
      .get(row.user_id)
      .push(row);
  });

  const recertByUser = new Map(
    (recertificationCandidates || []).map(row => [row.user_id, row])
  );

  workforceReadinessRows = (authorizationCandidates || []).map(employee => {
    const recert = recertByUser.get(employee.user_id);
    const recertItems = recert?.items || [];
    const employeeDocumentAssignments =
  documentAssignmentsByUser.get(employee.user_id) || [];
  const employeeDocumentEvidence = employeeDocumentAssignments.map(assignment => {
  const version =
    documentVersionById.get(assignment.document_version_id) || null;

  const documentRecord =
    version
      ? controlledDocumentById.get(version.document_id) || null
      : null;

  const acknowledged = documentAckKeys.has(
    employee.user_id + '|' + assignment.document_version_id
  );
  const acknowledgmentRecord = documentAckByKey.get(
  employee.user_id + '|' + assignment.document_version_id
) || null;

  return {
    assignment_id: assignment.id,
    document_version_id: assignment.document_version_id,
    document_code: documentRecord?.document_code || 'Unknown',
    title: documentRecord?.title || 'Controlled Document',
    version_number: version?.version_number || '—',
    effective_date: version?.effective_date || null,
    assigned_at: assignment.assigned_at || null,
    due_at: assignment.due_at || null,
    status: assignment.status || 'assigned',
    completed_at: assignment.completed_at || null,
acknowledged,
acknowledged_at: acknowledgmentRecord?.acknowledged_at || null
  };
});

const documentCounts = {
  total: employeeDocumentAssignments.length,
  completed: 0,
  outstanding: 0,
  overdue: 0,
  waived: 0
};

employeeDocumentAssignments.forEach(assignment => {
  const acknowledged = documentAckKeys.has(
    employee.user_id + '|' + assignment.document_version_id
  );

  const completed =
    assignment.status === 'completed' ||
    !!assignment.completed_at ||
    acknowledged;

  const waived =
    assignment.status === 'waived';

   const overdue =
  !completed &&
  !waived &&
  (
    assignment.status === 'overdue' ||
    (
      assignment.due_at &&
      new Date(assignment.due_at).getTime() < Date.now()
    )
  );

  if (completed) {
    documentCounts.completed++;
  } else if (waived) {
    documentCounts.waived++;
  } else {
    documentCounts.outstanding++;

    if (overdue) {
      documentCounts.overdue++;
    }
  }
});

let documentReadiness = 'current';

if (documentCounts.overdue > 0) {
  documentReadiness = 'overdue';
} else if (documentCounts.outstanding > 0) {
  documentReadiness = 'action_required';
} else if (documentCounts.total === 0) {
  documentReadiness = 'not_assigned';
}

    const renewalCounts = {
      current: 0,
      due_soon: 0,
      expired: 0,
      pending: 0,
      not_required: 0
    };

    recertItems.forEach(item => {
      if (item.status in renewalCounts) {
        renewalCounts[item.status]++;
      }
    });

    const latestEndorsementsByCode = new Map();

    (employee.endorsements || []).forEach(row => {
      if (!latestEndorsementsByCode.has(row.endorsement_code)) {
        latestEndorsementsByCode.set(row.endorsement_code, row);
      }
    });

    const currentEndorsements = Array.from(
      latestEndorsementsByCode.values()
    );

    const authorizedEndorsements = currentEndorsements.filter(
      row => row.status === 'authorized'
    );

       const latestAuthorization = employee.latest_authorization || null;
    // DEVELOPMENT ONLY — wiring to the tested shared Rules Engine.
    // computeOverallStatus() is defined in corevian-university-qualification-logic.js
    // (loaded before this file). Logic is unchanged — verified identical to the
    // inline version it replaces across 176 equivalence checks.
    const overallStatus = computeOverallStatus(
      latestAuthorization,
      authorizedEndorsements,
      renewalCounts,
      documentReadiness
    );
      const tier1Input = {
  ...employee,
  authorizationStatus: latestAuthorization?.status || null,
  renewalCounts: renewalCounts,
  documentCounts: documentCounts,
  documentReadiness: documentReadiness
};

const qualificationProgressSteps =
  window.computeQualificationProgressSteps
    ? window.computeQualificationProgressSteps(tier1Input)
    : [];

const nextAction =
  window.computeNextAction
    ? window.computeNextAction(tier1Input)
    : null;
    return {
  ...employee,
  current_endorsements: currentEndorsements,
  authorized_endorsements_current: authorizedEndorsements,
  recertification_items: recertItems,
  renewal_counts: renewalCounts,

  document_assignments: employeeDocumentAssignments,
  document_evidence: employeeDocumentEvidence,
  document_counts: documentCounts,
  document_readiness: documentReadiness,
  qualification_progress_steps: qualificationProgressSteps,
  next_action: nextAction,
  overall_status: overallStatus
};
  });

  return workforceReadinessRows;
}
  // DEVELOPMENT ONLY — wiring to the tested shared Rules Engine.
  // evaluateAssignmentEligibility() now comes from
  // corevian-university-qualification-logic.js (loaded before this file),
  // not a local definition. Logic is byte-identical to what was removed
  // here — moved, not changed. All call sites below are unchanged; they
  // resolve to the global function via normal scope lookup.

async function openEmployeeAssignmentEligibility(userId) {

  try {
  sessionStorage.setItem(
    'corevianCurrentWorkspace',
    JSON.stringify({
      type: 'assignmentEligibility',
      userId: userId
    })
  );
} catch (err) {
  console.warn(
    'Unable to save Assignment Eligibility workspace:',
    err
  );
}
  

 
  try {
    const rows = await loadWorkforceReadinessWorkspace();

    const row = rows.find(
      employee => employee.user_id === userId
    );

    if (!row) {
      alert('Employee readiness record could not be found.');
      return;
    }

    const options = ENDORSEMENT_MATRIX
  .map(service => {
    return `
      <option value="${esc(service.code)}">
        ${esc(service.name)}
      </option>
    `;
  })
  .join('');

    const app = document.getElementById('contentInner');

    app.innerHTML = `
       <div class="comp-card" style="padding:54px 24px 24px">

        <div style="margin-bottom:18px">
          <div style="
            font-size:12px;
            font-weight:800;
            letter-spacing:.08em;
            text-transform:uppercase;
            color:#7DE3DF;
          ">
            Operational Control
          </div>

          <h2 style="
            margin:6px 0 4px;
            color:#ffffff;
          ">
            Assignment Eligibility Control
          </h2>

          <div class="comp-history">
            Verify whether an employee is currently eligible
            for a specific authorized service category.
          </div>
        </div>

        <div class="comp-card" style="padding:18px;margin-top:16px">
          <div style="
            font-size:18px;
            font-weight:800;
            color:#ffffff;
          ">
            ${esc(row.full_name || 'Unnamed Employee')}
          </div>

          <div class="comp-history" style="margin-top:4px">
            ${esc(row.job_title || 'Employee')}
          </div>
        </div>

        <div style="margin-top:22px">
          <label style="
            display:block;
            margin-bottom:8px;
            font-size:12px;
            font-weight:800;
            color:#ffffff;
          ">
            Service Category
          </label>

          <select
            id="assignmentEligibilityService"
            style="
              width:100%;
              max-width:520px;
              padding:11px 12px;
              border-radius:8px;
              border:1px solid rgba(255,255,255,.16);
              background:#0B1426;
              color:#ffffff;
            "
          >
            <option value="">Select service category</option>
            ${options}
          </select>
        </div>

        <div style="margin-top:16px">
          <button
            class="secondary-btn"
            onclick="runAssignmentEligibilityCheck('${esc(userId)}')"
          >
            Run Eligibility Check
          </button>
        </div>

        <div
          id="assignmentEligibilityResult"
          style="margin-top:22px"
        ></div>

        <div style="margin-top:24px">
          <button
            class="secondary-btn"
            onclick="showView('workforce')"
          >
            Back to Workforce Readiness
          </button>
        </div>

      </div>
    `;

  } catch (error) {
    console.error(
      'Assignment Eligibility Control failed:',
      error
    );

    alert(
      'Unable to load Assignment Eligibility Control.'
    );
  }
  }
async function runAssignmentEligibilityCheck(userId) {
  try {

    const serviceSelect =
      document.getElementById('assignmentEligibilityService');

    const resultContainer =
      document.getElementById('assignmentEligibilityResult');

    if (!serviceSelect || !resultContainer) {
      return;
    }

    const endorsementCode = serviceSelect.value;

    if (!endorsementCode) {
      resultContainer.innerHTML = `
        <div class="comp-history" style="color:#FBBF24">
          Select a service category before running the eligibility check.
        </div>
      `;
      return;
    }

    const rows = await loadWorkforceReadinessWorkspace();

    const row = rows.find(
      employee => employee.user_id === userId
    );

    if (!row) {
      resultContainer.innerHTML = `
        <div class="comp-history" style="color:#FCA5A5">
          Employee readiness record could not be found.
        </div>
      `;
      return;
    }

    const result =
      evaluateAssignmentEligibility(
        row,
        endorsementCode
      );

    const statusColor =
      result.eligible
        ? '#4ADE80'
        : '#FCA5A5';

    const reasonHtml =
      result.reasons.length
        ? `
          <div style="margin-top:16px">
            <div style="
              font-size:12px;
              font-weight:800;
              color:#ffffff;
              margin-bottom:8px;
            ">
              Eligibility Findings
            </div>

            ${result.reasons
              .map(
                reason => `
                  <div
                    class="comp-history"
                    style="
                      margin-top:6px;
                      color:#FCA5A5;
                    "
                  >
                    ${esc(reason)}
                  </div>
                `
              )
              .join('')}
          </div>
        `
        : `
          <div
            class="comp-history"
            style="
              margin-top:12px;
              color:#4ADE80;
            "
          >
            All current assignment eligibility controls are satisfied
            for this service category.
          </div>
        `;

    resultContainer.innerHTML = `
      <div
        class="comp-card"
        style="
          padding:18px;
          border:1px solid ${statusColor};
        "
      >

        <div style="
          font-size:11px;
          font-weight:800;
          letter-spacing:.08em;
          color:${statusColor};
        ">
          ${esc(result.status_label)}
        </div>

        ${
          result.endorsement
            ? `
              <div style="
                margin-top:8px;
                font-size:16px;
                font-weight:800;
                color:#ffffff;
              ">
                ${esc(
                  result.endorsement.endorsement_name ||
                  result.endorsement.endorsement_code ||
                  endorsementCode
                )}
              </div>
            `
            : ''
        }

        ${reasonHtml}

      </div>
    `;
    window.scrollTo({
  top: 0,
  left: 0,
  behavior: 'auto'
  });

  } catch (error) {
    console.error(
      'Assignment eligibility check failed:',
      error
    );

    const resultContainer =
      document.getElementById('assignmentEligibilityResult');

    if (resultContainer) {
      resultContainer.innerHTML = `
        <div class="comp-history" style="color:#FCA5A5">
          Unable to complete assignment eligibility check.
        </div>
      `;
    }
  }
}
// ═══════════════════════════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════════════════════════
function renderDashboard() {
  const units = getUnitsForRole(currentRole);
  const done = units.filter(u => unitProgress[u.id] && unitProgress[u.id].passed).length;
  const pct = units.length ? Math.round((done / units.length) * 100) : 0;
  const leadershipUnits = units.filter(u => u.role === 'leadership');
  const employeeUnits = units.filter(u => u.role !== 'leadership');
  const isLeader = leadershipUnits.length > 0;

 const roleLabel =
  currentRole === 'admin' ? 'Executive / Administration' :
  currentRole === 'leadership' ? 'Executive Leadership' :
  currentRole === 'trainer' ? 'Leadership' :
  'Employee Operational Specialist';
    let html = `
  <div id="qualificationNextAction"></div>
  <div id="qualificationProgressSteps" style="margin-top:12px;margin-bottom:18px;"></div>

  <div class="dash-hero">
      <div class="dash-welcome">Welcome to Corevian University™</div>
      <div style="margin:10px 0 14px;padding:9px 12px;border:1px solid rgba(25,181,176,.35);border-radius:8px;color:var(--slate);font-size:11px">
        ${demoMode ? 'CONTROLLED PROTOTYPE v2.1 · Demo Mode · No production learner records are read or written.' : 'PRODUCTION DEVELOPMENT v3.11 · Supabase Auth · Controlled curriculum · Final assessment · Competency · Field training · Endorsements · Operational authorization · Recertification · Safe edit workflow.'}
      </div>
      <div class="dash-name">${esc(currentUser)}</div>
      <div class="dash-tagline">
        Enrolled as: <strong>${roleLabel}</strong> · 
        ${currentRole === 'admin'
  ? 'Executive / Administration Curriculum — '+units.length+' units'
  : currentRole === 'leadership'
    ? 'Executive Leadership Curriculum — '+units.length+' units'
    : currentRole === 'trainer'
      ? 'Leadership Curriculum — '+units.length+' units'
      : 'Employee Foundation Curriculum — '+units.length+' units'} ·
        Training completion does not by itself authorize independent field operation.
      </div>
      <div class="stat-grid">
        <div class="stat-card"><div class="stat-num">${done}</div><div class="stat-label">Units Passed</div></div>
        <div class="stat-card"><div class="stat-num">${units.length - done}</div><div class="stat-label">Units Remaining</div></div>
        <div class="stat-card"><div class="stat-num">${pct}%</div><div class="stat-label">Units Complete</div></div>
        <div class="stat-card"><div class="stat-num">${demoMode ? 'Demo' : (finalAssessmentStatus.passed ? 'Passed' : (allUnitsComplete(currentRole) ? 'Ready' : 'Locked'))}</div><div class="stat-label">Final Assessment</div></div>
      </div>
    </div>`;

  if (employeeUnits.length) {
html += `<div class="curriculum-divider curriculum-divider--employee"><span>Employee Foundation</span><span class="curriculum-divider__meta">Units 1–12</span></div>`;
    html += `<div class="unit-grid">` + employeeUnits.map(u => unitDashCard(u)).join('') + `</div>`;
  }
  if (leadershipUnits.length) {
    html += `<div class="curriculum-divider curriculum-divider--leadership"><span>Leadership Curriculum</span><span class="curriculum-divider__meta">Units 13–17</span></div>`;
    html += `<div class="unit-grid">` + leadershipUnits.map(u => unitDashCard(u)).join('') + `</div>`;
  }
  document.getElementById('contentInner').innerHTML = html;
  // TIER 2 UX — Role-based dashboard panels
const roleDashboardContainer = document.createElement('div');
roleDashboardContainer.id = 'roleDashboardPanels';

const dashboardContentInner = document.getElementById('contentInner');

if (
  dashboardContentInner &&
  window.CorevianUX &&
  typeof window.CorevianUX.renderRoleDashboardPanels === 'function'
) {
  const firstCurriculumDivider = dashboardContentInner.querySelector('.curriculum-divider');

if (firstCurriculumDivider) {
  dashboardContentInner.insertBefore(
    roleDashboardContainer,
    firstCurriculumDivider
  );
} else {
  dashboardContentInner.appendChild(roleDashboardContainer);
}

window.CorevianUX.renderRoleDashboardPanels(
  roleDashboardContainer,
  currentRole,
  {
    onAction: function(key) {
      if (key === 'workforce-readiness') {
        renderWorkforceReadiness();
        return;
      }

      if (key === 'operational-authorization') {
        renderOperationalAuthorization();
        return;
      }

      if (key === 'operational-endorsements') {
        renderOperationalEndorsements();
        return;
      }

      if (key === 'recertification') {
        renderRecertificationTracking();
        return;
      }

      if (key === 'competency-evaluation') {
        renderCompetencyEvaluation();
        return;
      }

      if (key === 'field-training') {
        renderFieldTrainingReadiness();
        return;
      }

      if (key === 'my-training') {
        const nextUnit = units.find(u => !(unitProgress[u.id] || {}).passed);
        if (nextUnit) {
          navToUnit(nextUnit.id);
        } else {
          showView('exam');
        }
        return;
      }
      if (key === 'controlled-knowledge') {
  if (typeof window.renderControlledDocuments === 'function') {
    window.renderControlledDocuments();
  }
  return;
}

     if (key === 'executive-functions') {
  if (typeof window.renderExecutiveFunctionsPanel === 'function') {
    window.renderExecutiveFunctionsPanel(
      document.getElementById('contentInner')
    );
  }
  return;
}

if (key === 'audit-compliance') {
  if (typeof window.renderAuditComplianceOversight === 'function') {
    window.renderAuditComplianceOversight(
      document.getElementById('contentInner')
    );
  }
  return;
}

if (key === 'exceptions-risk') {
  if (typeof window.renderExecutiveExceptionsRisk === 'function') {
    window.renderExecutiveExceptionsRisk(
      document.getElementById('contentInner')
    );
  }
  return;
}
      }
  }
);
  // ─────────────────────────────────────────────────────────────
// TIER 1 UX — Qualification guidance
// Presentation only. Does not grant, revoke, or change authorization.
// ─────────────────────────────────────────────────────────────
const nextActionContainer = document.getElementById('qualificationNextAction');
const progressContainer = document.getElementById('qualificationProgressSteps');

if (
  nextActionContainer &&
  progressContainer &&
  window.CorevianUX &&
  typeof window.computeNextAction === 'function' &&
  typeof window.computeQualificationProgressSteps === 'function'
) {
  const nextIncompleteUnit = units.find(
    u => !(unitProgress[u.id] || {}).passed
  );

  const tier1Input = {
    curriculumComplete: done === units.length,
    nextIncompleteUnitLabel: nextIncompleteUnit
      ? `Unit ${nextIncompleteUnit.num}`
      : null,

    finalPassed: !!finalAssessmentStatus?.passed,

    competenciesComplete: false,
    competentCount: 0,
    requiredCount: 0,
    fieldReady: false,
    endorsementsDocumented: false,
    authorizedEndorsementsCount: 0,
    authorizationStatus: null,

    renewalCounts: {
      expired: 0,
      due_soon: 0
    },

    soonestRenewalDueInDays: null
  };

  if (currentRole === 'employee') {
  window.CorevianUX.renderNextActionPanelAuto(
    nextActionContainer,
    tier1Input
  );

  window.CorevianUX.renderProgressStepsAuto(
    progressContainer,
    tier1Input
  );
} else {
  nextActionContainer.innerHTML = '';
  progressContainer.innerHTML = '';

}
}
}
}
function unitDashCard(u) {
  const p = unitProgress[u.id] || {};
  const accessible = canAccessUnit(u.id, currentRole);
  const cls = p.passed ? 'done' : (!accessible ? 'locked' : '');
  const status = p.passed
    ? `✓ Passed · ${Math.round(p.score * 100)}%`
    : (demoMode ? 'Demo preview · Open freely' : (!accessible ? '🔒 Complete previous unit' : 'Not started'));
  const onclick = accessible ? `onclick="navToUnit(${u.id})"` : '';
  const isLeader = u.role === 'leadership';
  const borderStyle = isLeader ? 'border-color:rgba(124,58,237,.25)' : '';
  return `<div class="unit-card ${cls}" ${onclick} style="${borderStyle}">
    <div class="uc-num">UNIT ${u.num}</div>
    <div class="uc-title">${esc(u.title)}</div>
    <div class="uc-status">${status}</div>
  </div>`;
}


// ═══════════════════════════════════════════════════════════════════
// COMPETENCY EVALUATION — TRAINER / LEADERSHIP / ADMIN
// ═══════════════════════════════════════════════════════════════════
async function loadCompetencyWorkspace(){
  if(!canEvaluateCompetencies()) throw new Error('Trainer, leadership, or administrator access is required.');
  const client=getSupabaseClient();
  const defs=await client.from('competency_definitions')
    .select('id,competency_code,competency_name,description,display_order,required,active,curriculum_version_id')
    .eq('active',true).eq('curriculum_version_id',currentCurriculumVersionId).order('display_order',{ascending:true});
  if(defs.error) throw defs.error;
  competencyDefinitions=defs.data||[];
  const prof=await client.from('profiles').select('user_id,full_name,role,job_title,department,active').eq('active',true).order('full_name',{ascending:true});
  if(prof.error) throw prof.error;
  const employees = await scopeProfilesToLeadershipDepartment(client, prof.data||[]);
  const ids=employees.map(x=>x.user_id);
  let enrollments=[];
  if(ids.length){
    const er=await client.from('enrollments').select('id,user_id,curriculum_version_id,assigned_role,active').in('user_id',ids).eq('active',true);
    if(er.error) throw er.error;
    enrollments=er.data||[];
  }
  const enrollmentByUser=new Map(enrollments.filter(e=>e.curriculum_version_id===currentCurriculumVersionId).map(e=>[e.user_id,e]));
  const enrollmentIds=[...enrollmentByUser.values()].map(e=>e.id);
  let finals=[];
  if(enrollmentIds.length){
    const ar=await client.from('assessment_attempts').select('enrollment_id,assessment_type,score_percent,passed,submitted_at').in('enrollment_id',enrollmentIds).eq('assessment_type','employee_final').eq('passed',true).is('unit_id',null);
    if(ar.error) throw ar.error;
    finals=ar.data||[];
  }
  const passedEnrollments=new Set(finals.map(a=>a.enrollment_id));
  competencyCandidates=employees.map(p=>{
    const e=enrollmentByUser.get(p.user_id);
    return {...p,enrollment_id:e?e.id:null,final_passed:!!(e&&passedEnrollments.has(e.id))};
  });
}
// ═══════════════════════════════════════════════════════════════════
// LEADERSHIP QUALIFICATION TRAINING — SOLO ROUTE AUTHORIZATION
// CVN-LSRAT-001 v1.0
// ═══════════════════════════════════════════════════════════════════

async function renderSoloRouteAuthorizationTraining(){
  const root = document.getElementById('contentInner');

  if(!canAccessLeadershipQualificationTraining()){
    root.innerHTML =
      '<div class="comp-error">CVN Leadership Solo Route Authorization Training is available only to Leadership and Administrator roles.</div>';
    return;
  }

  root.innerHTML =
    '<div class="comp-notice">Loading controlled Leadership Qualification Training…</div>';

  try{
    await loadLeadershipQualificationTraining();

    const training = currentLeadershipQualificationTraining;
    const content = training?.content_json || {};
    const lessons = Array.isArray(content.lessons) ? content.lessons : [];
    const quiz = Array.isArray(content.quiz) ? content.quiz : [];
    const attempts = leadershipQualificationTrainingAttempts || [];

    const passedAttempt = attempts.find(a => a.passed === true);
    const latestAttempt = attempts.length ? attempts[0] : null;

    let statusHtml = '';

    if(passedAttempt){
      statusHtml = `
        <div class="comp-notice" style="border-color:rgba(74,222,128,.35)">
          <strong style="color:#4ADE80">Training Passed</strong><br>
          Best recorded completion includes a passing score of
          ${esc(String(passedAttempt.score_percent))}%.
          This training completion does not independently grant Operational Authorization authority.
        </div>
      `;
    } else if(latestAttempt){
      statusHtml = `
        <div class="comp-notice">
          <strong>Previous Attempt:</strong>
          ${esc(String(latestAttempt.score_percent))}% ·
          ${latestAttempt.passed ? 'Passed' : 'Not Passed'}.
          A score of ${esc(String(training.passing_percent))}% is required.
        </div>
      `;
    } else {
      statusHtml = `
        <div class="comp-notice">
          No completed attempt is currently recorded for this training.
          A score of ${esc(String(training.passing_percent))}% is required to pass.
        </div>
      `;
    }

    let lessonHtml = '';

lessons.forEach((lesson, index) => {
  const rawSections =
    Array.isArray(lesson.sections) ? lesson.sections : [];

  /*
   * Version 2.0:
   * Purpose is orientation, not reference material.
   * Pull it out of the accordion and keep it visible.
   */
  const purposeSection = rawSections.find(section =>
    String(section?.title || '')
      .trim()
      .toLowerCase() === 'purpose'
  );

  const referenceSections = rawSections.filter(section =>
    String(section?.title || '')
      .trim()
      .toLowerCase() !== 'purpose'
  );

  const hasStructuredSections = rawSections.length > 0;

  const lessonPurpose =
    purposeSection?.content
      ? String(purposeSection.content)
      : '';

  /*
   * Legacy fallback.
   * Keep Version 1.0 / non-structured content functional.
   */
  const paragraphs = String(lesson.body || '')
    .split(/\n\s*\n/)
    .filter(Boolean)
    .map(p => `<p>${esc(p)}</p>`)
    .join('');

  /*
   * Compact leadership reference sections.
   */
  const referenceSectionsHtml =
    referenceSections.length
      ? `
        <div
          class="cvu-reference-header"
          style="
            display:flex;
            align-items:center;
            justify-content:space-between;
            gap:18px;
            margin:22px 0 4px;
            padding:0 4px 10px;
            border-bottom:1px solid rgba(255,255,255,.08);
          "
        >
          <div
            style="
              font-size:10px;
              font-weight:800;
              letter-spacing:1.35px;
              text-transform:uppercase;
              color:var(--teal);
            "
          >
            Leadership Reference Sections
          </div>

          <div
            style="
              display:flex;
              align-items:center;
              gap:8px;
              flex-wrap:wrap;
            "
          >
            <button
              type="button"
              class="cvu-lesson-expand-all"
              style="
                appearance:none;
                border:0;
                background:transparent;
                padding:4px 2px;
                color:var(--slate);
                font:inherit;
                font-size:10px;
                font-weight:800;
                letter-spacing:.7px;
                text-transform:uppercase;
                cursor:pointer;
              "
            >
              Expand All
            </button>

            <span
              aria-hidden="true"
              style="
                color:rgba(255,255,255,.18);
                font-size:11px;
              "
            >
              /
            </span>

            <button
              type="button"
              class="cvu-lesson-collapse-all"
              style="
                appearance:none;
                border:0;
                background:transparent;
                padding:4px 2px;
                color:var(--slate);
                font:inherit;
                font-size:10px;
                font-weight:800;
                letter-spacing:.7px;
                text-transform:uppercase;
                cursor:pointer;
              "
            >
              Collapse All
            </button>
          </div>
        </div>

        <div class="cvu-lesson-reference">
          ${referenceSections.map((section, sectionIndex) => `
            <details
              class="cvu-lesson-accordion"
              style="
                margin:0;
                border-bottom:1px solid rgba(255,255,255,.08);
              "
            >
              <summary
                style="
                  display:flex;
                  align-items:center;
                  gap:14px;
                  min-height:54px;
                  padding:14px 4px;
                  cursor:pointer;
                  list-style:none;
                  user-select:none;
                "
              >
                <span
                  style="
                    flex:0 0 30px;
                    color:var(--teal);
                    font-size:10px;
                    font-weight:800;
                    letter-spacing:1px;
                  "
                >
                  ${String(sectionIndex + 1).padStart(2, '0')}
                </span>

                <span
                  style="
                    flex:1;
                    color:var(--white);
                    font-size:13px;
                    font-weight:800;
                    line-height:1.4;
                    letter-spacing:.55px;
                    text-transform:uppercase;
                  "
                >
                  ${esc(section.title || '')}
                </span>

                <span
                  class="cvu-accordion-arrow"
                  aria-hidden="true"
                  style="
                    flex:0 0 auto;
                    width:24px;
                    text-align:center;
                    color:var(--teal);
                    font-size:18px;
                    font-weight:400;
                    line-height:1;
                  "
                >
                  +
                </span>
              </summary>

              <div
                class="cvu-accordion-content"
                style="
                  max-width:960px;
                  padding:3px 42px 22px 48px;
                  color:var(--muted);
                  font-size:15px;
                  line-height:1.8;
                "
              >
                ${String(section.content || '')
                  .split(/\n\s*\n/)
                  .filter(Boolean)
                  .map(p => `
                    <p style="margin:0 0 14px;">
                      ${esc(p)}
                    </p>
                  `)
                  .join('')}
              </div>
            </details>
          `).join('')}
        </div>
      `
      : '';

  /*
   * Existing structured competency / field-training example.
   * Preserve the controlled data fields.
   */
  let trainingExampleHtml = '';

  if(lesson.training_example){
    const ex = lesson.training_example;

    const exampleRows = [
      ['Employee', ex.employee],
      ['Route Assignment', ex.route_assignment],
      ['Competency', ex.competency],
      ['Evidence Reference', ex.evidence_reference],
      ['Observation Context', ex.observation_context],
      ['Evaluator Notes', ex.evaluator_notes],
      ['Decision', ex.decision],
      ['Leadership Note', ex.leadership_note]
    ].filter(row =>
      row[1] !== undefined &&
      row[1] !== null &&
      String(row[1]).trim() !== ''
    );

    trainingExampleHtml = `
      <details
        class="cvu-example-accordion"
        style="
          margin-top:18px;
          border:1px solid rgba(25,181,176,.22);
          border-radius:10px;
          background:rgba(25,181,176,.035);
          overflow:hidden;
        "
      >
        <summary
          style="
            display:flex;
            align-items:center;
            gap:14px;
            min-height:56px;
            padding:14px 18px;
            cursor:pointer;
            list-style:none;
            user-select:none;
          "
        >
          <span
            style="
              flex:1;
              color:var(--teal);
              font-size:11px;
              font-weight:800;
              letter-spacing:1.15px;
              text-transform:uppercase;
            "
          >
            Controlled Training Example
          </span>

          <span
            class="cvu-accordion-arrow"
            aria-hidden="true"
            style="
              color:var(--teal);
              font-size:18px;
              line-height:1;
            "
          >
            +
          </span>
        </summary>

        <div style="padding:2px 18px 18px;">
          ${exampleRows.map(([label, value]) => `
            <div
              style="
                display:grid;
                grid-template-columns:minmax(145px,190px) 1fr;
                gap:16px;
                padding:10px 0;
                border-bottom:1px solid rgba(255,255,255,.06);
              "
            >
              <div
                style="
                  color:var(--slate);
                  font-size:10px;
                  font-weight:800;
                  letter-spacing:.8px;
                  text-transform:uppercase;
                "
              >
                ${esc(label)}
              </div>

              <div
                style="
                  color:var(--white);
                  font-size:14px;
                  line-height:1.65;
                "
              >
                ${esc(String(value))}
              </div>
            </div>
          `).join('')}

          ${ex.poor_example ? `
            <div
              style="
                margin-top:16px;
                padding:14px;
                border:1px solid rgba(220,38,38,.25);
                border-radius:8px;
                background:rgba(220,38,38,.05);
              "
            >
              <div
                style="
                  margin-bottom:7px;
                  color:#FCA5A5;
                  font-size:10px;
                  font-weight:800;
                  letter-spacing:1px;
                  text-transform:uppercase;
                "
              >
                Unacceptable Documentation
              </div>

              <div
                style="
                  color:var(--white);
                  font-size:14px;
                  font-weight:700;
                  line-height:1.6;
                "
              >
                “${esc(ex.poor_example)}”
              </div>

              ${ex.why_poor ? `
                <div
                  style="
                    margin-top:8px;
                    color:var(--slate);
                    font-size:13px;
                    line-height:1.65;
                  "
                >
                  ${esc(ex.why_poor)}
                </div>
              ` : ''}
            </div>
          ` : ''}

          ${ex.documentation_rule ? `
            <div
              style="
                margin-top:14px;
                color:#C4B5FD;
                font-size:13px;
                font-weight:800;
                line-height:1.6;
              "
            >
              ${esc(ex.documentation_rule)}
            </div>
          ` : ''}
        </div>
      </details>
    `;
  }

  /*
   * Preserve any authorization example without assuming its schema.
   * Render only fields actually present in the controlled JSON.
   */
  let authorizationExampleHtml = '';

  if(lesson.authorization_example){
    const ex = lesson.authorization_example;

    const authorizationRows = Object.entries(ex)
      .filter(([key, value]) =>
        key !== 'poor_example' &&
        key !== 'why_poor' &&
        key !== 'documentation_rule' &&
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ''
      );

    const formatLabel = key =>
      String(key)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());

    authorizationExampleHtml = `
      <details
        class="cvu-example-accordion"
        style="
          margin-top:18px;
          border:1px solid rgba(196,181,253,.24);
          border-radius:10px;
          background:rgba(124,58,237,.045);
          overflow:hidden;
        "
      >
        <summary
          style="
            display:flex;
            align-items:center;
            gap:14px;
            min-height:56px;
            padding:14px 18px;
            cursor:pointer;
            list-style:none;
            user-select:none;
          "
        >
          <span
            style="
              flex:1;
              color:#C4B5FD;
              font-size:11px;
              font-weight:800;
              letter-spacing:1.15px;
              text-transform:uppercase;
            "
          >
            Authorization Decision Example
          </span>

          <span
            class="cvu-accordion-arrow"
            aria-hidden="true"
            style="
              color:#C4B5FD;
              font-size:18px;
              line-height:1;
            "
          >
            +
          </span>
        </summary>

        <div style="padding:2px 18px 18px;">
          ${authorizationRows.map(([key, value]) => `
            <div
              style="
                display:grid;
                grid-template-columns:minmax(145px,190px) 1fr;
                gap:16px;
                padding:10px 0;
                border-bottom:1px solid rgba(255,255,255,.06);
              "
            >
              <div
                style="
                  color:var(--slate);
                  font-size:10px;
                  font-weight:800;
                  letter-spacing:.8px;
                  text-transform:uppercase;
                "
              >
                ${esc(formatLabel(key))}
              </div>

              <div
                style="
                  color:var(--white);
                  font-size:14px;
                  line-height:1.65;
                "
              >
                ${esc(String(value))}
              </div>
            </div>
          `).join('')}

          ${ex.poor_example ? `
            <div
              style="
                margin-top:16px;
                padding:14px;
                border:1px solid rgba(220,38,38,.25);
                border-radius:8px;
                background:rgba(220,38,38,.05);
              "
            >
              <div
                style="
                  margin-bottom:7px;
                  color:#FCA5A5;
                  font-size:10px;
                  font-weight:800;
                  letter-spacing:1px;
                  text-transform:uppercase;
                "
              >
                Unacceptable Authorization Documentation
              </div>

              <div
                style="
                  color:var(--white);
                  font-size:14px;
                  font-weight:700;
                  line-height:1.6;
                "
              >
                “${esc(ex.poor_example)}”
              </div>

              ${ex.why_poor ? `
                <div
                  style="
                    margin-top:8px;
                    color:var(--slate);
                    font-size:13px;
                    line-height:1.65;
                  "
                >
                  ${esc(ex.why_poor)}
                </div>
              ` : ''}
            </div>
          ` : ''}

          ${ex.documentation_rule ? `
            <div
              style="
                margin-top:14px;
                color:#C4B5FD;
                font-size:13px;
                font-weight:800;
                line-height:1.6;
              "
            >
              ${esc(ex.documentation_rule)}
            </div>
          ` : ''}
        </div>
      </details>
    `;
  }

  lessonHtml += `
    <div
      class="section-block cvu-leadership-lesson"
      data-cvu-lesson="${esc(
        String(lesson.lesson_number || index + 1)
      )}"
      style="
        margin-bottom:34px;
      "
    >
      <div class="unit-eyebrow">
        LESSON ${String(
          lesson.lesson_number || index + 1
        ).padStart(2,'0')}
      </div>

      <div
        class="section-heading"
        style="
          margin-bottom:${hasStructuredSections ? '0' : '14px'};
        "
      >
        ${esc(lesson.title || '')}
      </div>

      ${
        hasStructuredSections
          ? `
            ${
              lessonPurpose
                ? `
                  <div
                    class="cvu-lesson-purpose"
                    style="
                      max-width:1000px;
                      margin:14px 0 6px;
                      padding:14px 16px;
                      border-left:2px solid var(--teal);
                      background:rgba(25,181,176,.035);
                    "
                  >
                    <div
                      style="
                        margin-bottom:5px;
                        color:var(--teal);
                        font-size:9px;
                        font-weight:800;
                        letter-spacing:1.25px;
                        text-transform:uppercase;
                      "
                    >
                      Purpose
                    </div>

                    <div
                      style="
                        color:var(--white);
                        font-size:14px;
                        line-height:1.7;
                      "
                    >
                      ${esc(lessonPurpose)}
                    </div>
                  </div>
                `
                : ''
            }

            ${referenceSectionsHtml}
          `
          : `
            <div class="section-body">
              ${paragraphs}
            </div>
          `
      }

      ${trainingExampleHtml}
      ${authorizationExampleHtml}
    </div>
  `;
});

    let quizHtml = '';

    if(quiz.length){
      quizHtml = `
        <div class="quiz-container" id="soloRouteTrainingQuiz">

          <div class="unit-eyebrow">CONTROLLED ASSESSMENT</div>

          <div class="unit-title" style="font-size:24px;margin-bottom:6px">
            Solo Route Authorization Assessment
          </div>

          <div class="unit-subtitle" style="margin-bottom:22px">
            ${quiz.length} questions ·
            ${esc(String(training.passing_percent))}% required to pass ·
            Answer all questions before submitting.
          </div>

          ${quiz.map((q, qi) => `
            <div class="comp-card" style="margin-bottom:18px">

              <div class="unit-eyebrow">
                QUESTION ${qi + 1} OF ${quiz.length}
              </div>

              <div style="
                color:var(--white);
                font-weight:800;
                font-size:16px;
                line-height:1.45;
                margin:6px 0 14px;
              ">
                ${esc(q.q || '')}
              </div>

              ${(Array.isArray(q.opts) ? q.opts : []).map((opt, oi) => `
                <label style="
  display:flex;
  gap:10px;
  align-items:flex-start;
  padding:10px 12px;
  margin:6px 0;
  border:1px solid rgba(255,255,255,.10);
  border-radius:8px;
  cursor:pointer;
  font-size:14px;
  line-height:1.45;
  color:var(--white);
">
  <input
    type="radio"
    name="lsrat_q_${qi}"
    value="${oi}"
    style="
      margin-top:3px;
      width:16px;
      height:16px;
      flex:0 0 auto;
    "
  >

  <span style="
    font-size:14px;
    line-height:1.45;
    font-weight:500;
    letter-spacing:0;
  ">
    <strong style="
      display:inline-block;
      min-width:22px;
      font-size:13px;
      font-weight:800;
      color:var(--teal);
    ">
      ${String.fromCharCode(65 + oi)}.
    </strong>
    ${esc(opt)}
  </span>
</label>
              `).join('')}

            </div>
          `).join('')}

          <div class="comp-notice">
            Assessment submission will be recorded through the controlled
            Supabase training-attempt function.
          </div>

          <div class="comp-actions">
            <div class="comp-history" id="soloRouteTrainingSaveStatus"></div>

            <button
              type="button"
              class="comp-save"
              id="soloRouteTrainingSubmitButton"
              disabled
            >
              Submit Assessment
            </button>
          </div>

        </div>
      `;
    }

    root.innerHTML = `
      <div class="unit-header" style="border-color:rgba(196,181,253,.35)">

        <div class="unit-eyebrow" style="color:#C4B5FD">
          LEADERSHIP QUALIFICATION TRAINING
        </div>

        <div class="unit-title">
          ${esc(training.title)}
        </div>

        <div class="unit-subtitle">
          ${esc(training.training_code)} ·
          Version ${esc(training.version_label)} ·
          Required Leadership / Administrator Training
        </div>

        <div class="unit-quote"
             style="border-color:#C4B5FD;background:rgba(124,58,237,.08)">
          ${esc(content.core_rule || '')}
        </div>

      </div>

      ${statusHtml}

      <div class="comp-notice">
        <strong>Purpose:</strong>
        ${esc(content.purpose || '')}
      </div>

      ${lessonHtml}

      ${quizHtml}
    `;

    try{
      document
        .querySelector('#corevian-university-app .content')
        ?.scrollTo({top:0,behavior:'smooth'});
    }catch(_){}
    const soloRouteSubmitButton =
  document.getElementById('soloRouteTrainingSubmitButton');

const soloRouteSaveStatus =
  document.getElementById('soloRouteTrainingSaveStatus');

const soloRouteQuiz =
  Array.isArray(quiz) ? quiz : [];

function getSoloRouteAnswers(){
  return soloRouteQuiz.map((_, qi) => {
    const selected = document.querySelector(
      `input[name="lsrat_q_${qi}"]:checked`
    );

    return selected ? Number(selected.value) : null;
  });
}

function updateSoloRouteSubmitState(){
  if(!soloRouteSubmitButton) return;

  const answers = getSoloRouteAnswers();
  const allAnswered =
    answers.length === soloRouteQuiz.length &&
    answers.every(answer => Number.isInteger(answer));

  soloRouteSubmitButton.disabled = !allAnswered;
}

document
  .querySelectorAll('input[name^="lsrat_q_"]')
  .forEach(input => {
    input.addEventListener('change', updateSoloRouteSubmitState);
  });

if(soloRouteSubmitButton){
  soloRouteSubmitButton.addEventListener('click', async () => {
    const answers = getSoloRouteAnswers();

    if(
      answers.length !== soloRouteQuiz.length ||
      answers.some(answer => !Number.isInteger(answer))
    ){
      if(soloRouteSaveStatus){
        soloRouteSaveStatus.textContent =
          'Answer all assessment questions before submitting.';
      }
      return;
    }

    soloRouteSubmitButton.disabled = true;
    soloRouteSubmitButton.textContent = 'Submitting...';

    if(soloRouteSaveStatus){
      soloRouteSaveStatus.textContent =
        'Recording controlled assessment attempt...';
    }

    try{
      const trainingId =
        currentLeadershipQualificationTraining?.id;

      if(!trainingId){
        throw new Error(
          'Leadership qualification training record is not loaded.'
        );
      }

      const startedAt =
        leadershipQualificationTrainingStartedAt ||
        new Date().toISOString();

      const rpc = await getSupabaseClient().rpc(
        'record_leadership_qualification_training_attempt',
        {
          p_training_id: trainingId,

          // Compatibility parameter only.
          // Supabase now calculates the actual score.
          p_score_percent: 0,

          p_answer_payload: {
            answers: answers
          },

          p_started_at: startedAt
        }
      );

      if(rpc.error) throw rpc.error;

      const saved =
        Array.isArray(rpc.data)
          ? rpc.data[0]
          : rpc.data;

      const score =
        Number(saved?.score_percent ?? 0);

      const passed =
        saved?.passed === true;

      if(soloRouteSaveStatus){
        soloRouteSaveStatus.innerHTML = passed
          ? `Assessment passed — ${score.toFixed(0)}%. Attempt recorded in the controlled training history.`
          : `Assessment not passed — ${score.toFixed(0)}%. A score of 90% is required. Review the training and attempt the assessment again.`;
      }

     leadershipQualificationTrainingAttempts.unshift(saved);

const attemptNumber =
  Number(saved?.attempt_number ?? leadershipQualificationTrainingAttempts.length);

const correctCount =
  Number(saved?.answer_payload?.correct_count ?? 0);

const questionCount =
  Number(saved?.answer_payload?.question_count ?? soloRouteQuiz.length);

if(soloRouteSaveStatus){
  soloRouteSaveStatus.innerHTML = `
    <div style="
      margin-top:14px;
      padding:16px;
      border:1px solid ${passed ? 'rgba(74,222,128,.28)' : 'rgba(220,38,38,.28)'};
      border-radius:10px;
      background:${passed ? 'rgba(74,222,128,.05)' : 'rgba(220,38,38,.05)'};
    ">
      <div style="
        font-size:15px;
        font-weight:800;
        color:${passed ? '#4ADE80' : '#FCA5A5'};
        margin-bottom:6px;
      ">
        ${passed ? 'Assessment Passed' : 'Assessment Not Passed'} — ${score.toFixed(0)}%
      </div>

      <div style="
        font-size:13px;
        line-height:1.6;
        color:var(--white);
      ">
        ${correctCount} of ${questionCount} correct ·
        90% required to pass ·
        Attempt ${attemptNumber} recorded
      </div>
    </div>
  `;
}

soloRouteSubmitButton.textContent =
  passed ? 'Assessment Completed' : 'Retake Assessment';

soloRouteSubmitButton.disabled = passed;

    }catch(err){
      console.error(err);

      const message =
        err?.message ||
        'Unable to record the Solo Route Authorization assessment.';

      if(soloRouteSaveStatus){
        soloRouteSaveStatus.textContent =
          'Submission failed: ' + message;
      }

      soloRouteSubmitButton.disabled = false;
      soloRouteSubmitButton.textContent = 'Submit Assessment';
    }
  });
}

updateSoloRouteSubmitState();
  }catch(err){
    root.innerHTML = `
      <div class="comp-error">
        ${esc(
          err && err.message
            ? err.message
            : 'Unable to load CVN Leadership Solo Route Authorization Training.'
        )}
      </div>
    `;
  }
}
async function renderCompetencyEvaluation(){
  const root=document.getElementById('contentInner');
  if(!canEvaluateCompetencies()){
    root.innerHTML='<div class="comp-error">This control is available only to Trainer, Leadership, and Administrator roles.</div>';
    return;
  }

  root.innerHTML='<div class="comp-notice">Loading controlled competency workspace…</div>';

  try{
    await loadCompetencyWorkspace();

    const eligible=competencyCandidates.filter(x=>x.final_passed);
    if(!selectedCompetencyUserId && eligible.length) selectedCompetencyUserId=eligible[0].user_id;

    let options='<option value="">Select employee</option>';

    for (const p of competencyCandidates) {
      const label =
        esc(p.full_name) +
        (p.final_passed ? '' : ' — Final assessment not passed');

      options +=
        '<option value="' + p.user_id + '"' +
        (p.user_id === selectedCompetencyUserId ? ' selected' : '') +
        (p.final_passed ? '' : ' disabled') +
        '>' + label + '</option>';
    }

    root.innerHTML=`
      <div class="unit-header">
        <div class="unit-eyebrow">QUALIFICATION CONTROL · CVN-COMP-001</div>
        <div class="unit-title">Hands-On Competency Evaluation</div>
        <div class="unit-subtitle">Record observed practical competency after the employee has completed the required University pathway and passed the controlled final assessment. Competency evidence does not by itself grant operational authorization.</div>
      </div>
      <div class="comp-notice">Only employees with a passed Employee Final Assessment are selectable. Baseline competencies 01–08 are required for general qualification review. Service-specific competencies 09–13 support later endorsement decisions.</div>
      <div class="comp-toolbar">
        <div class="comp-field"><label>Employee</label><select id="competencyEmployee" onchange="changeCompetencyEmployee(this.value)">${options}</select></div>
        <button class="comp-save" onclick="refreshCompetencyWorkspace()">Refresh Records</button>
        <button type="button" class="secondary-btn" onclick="returnToPersonnelRecord(selectedCompetencyUserId)">← Back to Employee Record</button>
      </div>
      <div id="competencyCards"></div>`;
    await renderCompetencyCards();
  }catch(err){
    root.innerHTML=`<div class="comp-error">${esc(err&&err.message?err.message:'Unable to load competency workspace.')}</div>`;
  }
}

async function changeCompetencyEmployee(userId){
  selectedCompetencyUserId=userId||null;
  await renderCompetencyCards();
}
window.changeCompetencyEmployee=changeCompetencyEmployee;
function openEmployeeCompetencyEvaluation(userId) {
  selectedCompetencyUserId = userId || null;

  showView('competency');
}

window.openEmployeeCompetencyEvaluation = openEmployeeCompetencyEvaluation;
function openEmployeeFieldTraining(userId){
  selectedFieldTrainingUserId = userId || null;
  showView('fieldtraining');
}

window.openEmployeeFieldTraining = openEmployeeFieldTraining;
async function openEmployeeTrainingFoundation(userId){
  const app = document.getElementById('contentInner');

  if(!app){
    alert('Unable to open Training Foundation.');
    return;
  }

  app.innerHTML = '<div class="comp-notice">Loading Training & Assessments…</div>';

  try {
    const training = await loadPersonnelTrainingRecord(userId);

    let universityTrainingHtml;

    if (!training.hasEnrollment) {
      universityTrainingHtml = `
        <div class="comp-card" style="padding:18px;border-left:3px solid #FBBF24;margin-bottom:12px">
          <div class="comp-code" style="color:#FBBF24">COREVIAN UNIVERSITY TRAINING</div>
          <div class="comp-history" style="margin-top:8px">
            No active curriculum enrollment was found for this employee.
          </div>
        </div>`;
    } else {
      const fa = training.finalAssessmentStatus;
      const finalLabel = fa.passed
        ? `PASS — ${fa.bestScorePercent}%`
        : (fa.attemptCount > 0 ? `NOT PASSED — best ${fa.bestScorePercent}%` : 'LOCKED — no attempts recorded');

      const integrityWarning = training.missingProgressCount > 0
        ? `<div class="comp-history" style="margin-top:10px;color:#FCA5A5">
             Data Integrity Attention: ${training.missingProgressCount} assigned unit${training.missingProgressCount === 1 ? '' : 's'} missing a progress record.
           </div>`
        : '';

      universityTrainingHtml = `
        <div class="comp-card" style="padding:18px;border-left:3px solid #FBBF24;margin-bottom:12px">
          <div class="comp-code" style="color:#FBBF24">COREVIAN UNIVERSITY TRAINING</div>

          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(110px,1fr));gap:12px;margin-top:12px">
            <div><div class="comp-history">Assigned Role</div><div style="font-weight:800;color:var(--white)">${esc(String(training.assignedRole).toUpperCase())}</div></div>
            <div><div class="comp-history">Total Units</div><div style="font-weight:800;color:var(--white)">${training.totalCount}</div></div>
            <div><div class="comp-history">Passed</div><div style="font-weight:800;color:#4ADE80">${training.passedCount}</div></div>
            <div><div class="comp-history">Remaining</div><div style="font-weight:800;color:var(--white)">${training.remainingCount}</div></div>
            <div><div class="comp-history">Complete</div><div style="font-weight:800;color:var(--white)">${training.completionPercent}%</div></div>
          </div>

          <div class="comp-history" style="margin-top:12px">Final Assessment: <strong style="color:var(--white)">${esc(finalLabel)}</strong></div>

          ${integrityWarning}

          <button type="button" class="secondary-btn" style="margin-top:14px;width:100%"
            onclick="openPersonnelTrainingUnitDetail('${esc(userId)}')">
            View Unit-by-Unit Detail
          </button>
        </div>`;
    }

    app.innerHTML = `
      <div class="unit-header">
        <div class="unit-eyebrow">WORKFORCE READINESS DRILL-DOWN</div>
        <div class="unit-title">Training Foundation</div>
        <div class="unit-subtitle">
          Review the employee's controlled training, competency, and field-readiness records.
        </div>
      </div>

      ${universityTrainingHtml}

      <div class="comp-card" style="padding:20px;margin-top:18px">
        <button
          type="button"
          class="secondary-btn"
          style="width:100%;margin-bottom:12px"
          onclick="openEmployeeFinalAssessment('${esc(userId)}')"
        >
          View Final Assessment
        </button>

        <button
          type="button"
          class="secondary-btn"
          style="width:100%;margin-bottom:12px"
          onclick="openEmployeeCompetencyEvaluation('${esc(userId)}')"
        >
          View Required Competencies
        </button>

        <button
          type="button"
          class="secondary-btn"
          style="width:100%;margin-bottom:12px"
          onclick="openEmployeeFieldTraining('${esc(userId)}')"
        >
          View Field Training & Readiness
        </button>

        <button
          type="button"
          class="secondary-btn"
          style="width:100%"
          onclick="returnToPersonnelRecord('${esc(userId)}')"
        >
          ← Back to Employee Record
        </button>
      </div>
    `;

    window.scrollTo({ top: 0, behavior: 'smooth' });

  } catch (err) {
    console.error('Training Foundation failed to load:', err);
    app.innerHTML = `
      <div class="comp-error">
        ${esc(err?.message || 'Unable to load Training Foundation.')}
      </div>
      <div style="margin-top:18px">
        <button type="button" class="secondary-btn" onclick="returnToPersonnelRecord('${esc(userId)}')">← Back to Employee Record</button>
      </div>
    `;
  }
}

window.openEmployeeTrainingFoundation = openEmployeeTrainingFoundation;
async function openPersonnelTrainingUnitDetail(userId) {
  const app = document.getElementById('contentInner');
  if (!app) return;

  app.innerHTML = '<div class="comp-notice">Loading training unit detail…</div>';

  try {
    const training = await loadPersonnelTrainingRecord(userId);

    if (!training.hasEnrollment) {
      app.innerHTML = `
        <div class="comp-error">No active curriculum enrollment was found for this employee.</div>
        <div style="margin-top:18px">
          <button type="button" class="secondary-btn" onclick="openEmployeeTrainingFoundation('${esc(userId)}')">← Back to Training & Assessments</button>
        </div>`;
      return;
    }

    const statusColor = status => (
      status === 'passed' ? '#4ADE80' :
      status === 'in_progress' ? '#FBBF24' :
      status === 'failed' ? '#FCA5A5' :
      status === 'no_progress_record' ? '#FCA5A5' :
      '#94A3B8'
    );

    const unitRows = training.units.map(u => `
      <div style="display:flex;justify-content:space-between;gap:12px;padding:10px 0;border-top:1px solid rgba(255,255,255,.06)">
        <div>
          <div style="font-weight:700;color:var(--white)">${esc(u.num)} · ${esc(u.title)}</div>
          <div class="comp-history" style="margin-top:3px">
            ${u.score !== null ? 'Score: ' + u.score + '% · ' : ''}
            ${u.completedAt ? 'Completed ' + new Date(u.completedAt).toLocaleDateString() + ' · ' : ''}
            Attempts: ${u.attemptCount}
          </div>
        </div>
        <div style="font-weight:800;color:${statusColor(u.status)};white-space:nowrap">${esc(u.statusLabel)}</div>
      </div>`).join('');

    app.innerHTML = `
      <div class="unit-header">
        <div class="unit-eyebrow">PERSONNEL READINESS RECORD · TRAINING DETAIL</div>
        <div class="unit-title">Corevian University Training — Unit Detail</div>
        <div class="unit-subtitle">${training.passedCount} of ${training.totalCount} units passed · ${training.completionPercent}% complete</div>
      </div>

      ${training.missingProgressCount > 0 ? `
        <div class="comp-notice" style="color:#FCA5A5">
          Data Integrity Attention: ${training.missingProgressCount} assigned unit${training.missingProgressCount === 1 ? '' : 's'} missing a progress record.
        </div>` : ''}

      <div class="comp-card" style="padding:18px;margin-top:14px">
        ${unitRows}
      </div>

      <div style="margin-top:18px">
        <button type="button" class="secondary-btn" onclick="openEmployeeTrainingFoundation('${esc(userId)}')">
          ← Back to Training & Assessments
        </button>
      </div>
    `;
    window.scrollTo({ top: 0, behavior: 'smooth' });

  } catch (err) {
    app.innerHTML = `
      <div class="comp-error">${esc(err?.message || 'Unable to load training unit detail.')}</div>
      <div style="margin-top:18px">
        <button type="button" class="secondary-btn" onclick="openEmployeeTrainingFoundation('${esc(userId)}')">← Back to Training & Assessments</button>
      </div>
    `;
  }
}

window.openPersonnelTrainingUnitDetail = openPersonnelTrainingUnitDetail;
async function openEmployeeFinalAssessment(userId){
  const app = document.getElementById('contentInner');

  if(!app){
    alert('Unable to open Final Assessment record.');
    return;
  }

  app.innerHTML = `
    <div class="comp-notice">
      Loading employee Final Assessment record...
    </div>
  `;

  try{
    const client = getSupabaseClient();

    const [profileRes, enrollmentRes] = await Promise.all([
      client
        .from('profiles')
        .select('user_id,full_name,job_title')
        .eq('user_id', userId)
        .single(),

      client
        .from('enrollments')
        .select('id,user_id,assigned_role,active')
        .eq('user_id', userId)
        .eq('assigned_role', 'employee')
        .eq('active', true)
        .maybeSingle()
    ]);

    if(profileRes.error) throw profileRes.error;
    if(enrollmentRes.error) throw enrollmentRes.error;

    const profile = profileRes.data;
    const enrollment = enrollmentRes.data;

    if(!enrollment){
      throw new Error('No active employee enrollment was found.');
    }

    const assessmentRes = await client
      .from('assessment_attempts')
      .select('enrollment_id,assessment_type,passed,score_percent,submitted_at')
      .eq('enrollment_id', enrollment.id)
      .eq('assessment_type', 'employee_final')
      .is('unit_id', null)
      .order('submitted_at', { ascending:false })
      .limit(1)
      .maybeSingle();

    if(assessmentRes.error) throw assessmentRes.error;

    const assessment = assessmentRes.data;

    const status =
      assessment
        ? (assessment.passed ? 'PASS' : 'NOT PASSED')
        : 'NO RECORD';

    const score =
      assessment && assessment.score_percent != null
        ? Number(assessment.score_percent).toFixed(1) + '%'
        : '—';

    const completed =
      assessment?.submitted_at
        ? new Date(assessment.submitted_at).toLocaleString()
        : '—';

    app.innerHTML = `
      <div class="unit-header">
        <div class="unit-eyebrow">
          TRAINING FOUNDATION · EMPLOYEE RECORD
        </div>

        <div class="unit-title">
          Final Assessment Record
        </div>

        <div class="unit-subtitle">
          Controlled Final Assessment result for the selected employee.
        </div>
      </div>

      <div class="comp-card" style="padding:20px;margin-top:18px">

        <div style="
          font-size:18px;
          font-weight:800;
          color:var(--white);
        ">
          ${esc(profile?.full_name || 'Unnamed Employee')}
        </div>

        <div class="comp-history" style="margin-top:4px">
          ${esc(profile?.job_title || 'Employee')}
        </div>

        <div style="
          display:grid;
          grid-template-columns:repeat(auto-fit,minmax(180px,1fr));
          gap:12px;
          margin-top:20px;
        ">

          <div class="comp-card" style="padding:14px">
            <div class="comp-code">FINAL STATUS</div>
            <div style="margin-top:8px;font-weight:800;color:var(--white)">
              ${esc(status)}
            </div>
          </div>

          <div class="comp-card" style="padding:14px">
            <div class="comp-code">SCORE</div>
            <div style="margin-top:8px;font-weight:800;color:var(--white)">
              ${esc(score)}
            </div>
          </div>

          <div class="comp-card" style="padding:14px">
            <div class="comp-code">COMPLETED</div>
            <div style="margin-top:8px;font-weight:800;color:var(--white)">
              ${esc(completed)}
            </div>
          </div>

        </div>

        <button
          type="button"
          class="secondary-btn"
          style="width:100%;margin-top:18px"
          onclick="openEmployeeTrainingFoundation('${userId}')"
        >
          Back to Training Foundation
        </button>

      </div>
    `;

    window.scrollTo({
      top:0,
      behavior:'smooth'
    });

  }catch(error){
    console.error(
      'Employee Final Assessment record failed:',
      error
    );

    app.innerHTML = `
      <div class="comp-error">
        Unable to load employee Final Assessment record.
      </div>
    `;
  }
}

window.openEmployeeFinalAssessment = openEmployeeFinalAssessment;
async function refreshCompetencyWorkspace(){
  await renderCompetencyEvaluation();
}
window.refreshCompetencyWorkspace=refreshCompetencyWorkspace;

async function renderCompetencyCards(){
  const host=document.getElementById('competencyCards');
  if(!host) return;
  if(!selectedCompetencyUserId){host.innerHTML='<div class="comp-notice">Select an eligible employee to begin the practical evaluation.</div>';return;}
  const client=getSupabaseClient();
  const history=await client.from('competency_records')
    .select('id,user_id,competency_code,competency_name,result,evaluated_by,evaluated_at,evidence_reference,notes,curriculum_version_label,created_at')
    .eq('user_id',selectedCompetencyUserId).order('evaluated_at',{ascending:false});
  if(history.error){host.innerHTML=`<div class="comp-error">${esc(history.error.message)}</div>`;return;}
  const latest=new Map();
  (history.data||[]).forEach(r=>{if(!latest.has(r.competency_code)) latest.set(r.competency_code,r);});
  host.innerHTML='<div class="comp-list">'+competencyDefinitions.map(d=>{
    const r=latest.get(d.competency_code);
    const status=r?`${r.result.replaceAll('_',' ')} · ${r.evaluated_at?new Date(r.evaluated_at).toLocaleString():''}`:'No evaluation recorded';
    return `<div class="comp-card ${d.required?'required':'service'}">
      <div class="comp-head"><div><div class="comp-code">${esc(d.competency_code)}</div><div class="comp-name">${esc(d.competency_name)}</div><div class="comp-desc">${esc(d.description||'')}</div></div><span class="comp-tag">${d.required?'Required baseline':'Service-specific'}</span></div>
      <div class="comp-grid">
        <div class="comp-field"><label>Observed Result</label><select id="result-${d.competency_code}"><option value="not_observed">Not Observed</option><option value="competent">Competent</option><option value="needs_training">Needs Training</option></select></div>
        <div class="comp-field"><label>Evidence Reference</label><input id="evidence-${d.competency_code}" placeholder="e.g., CVN-COMP-001 signed checklist / observation reference"></div>
      </div>
      <div class="comp-field" style="margin-top:10px"><label>Evaluator Notes</label><textarea id="notes-${d.competency_code}" placeholder="Document what was observed, any gap, remediation required, or limitations. Do not document PHI."></textarea></div>
      <div class="comp-actions"><div><div class="comp-history">Latest record: ${esc(status)}</div><div class="comp-history" id="save-status-${d.competency_code}"></div></div><button type="button" class="comp-save" onclick="window.saveCompetency('${d.competency_code}',this); return false;">Record Evaluation</button></div>
    </div>`;
  }).join('')+'</div>';
}

async function saveCompetency(code,button){
  if(!selectedCompetencyUserId){alert('Select an employee first.');return;}
  const resultEl=document.getElementById('result-'+code);
  const evidenceEl=document.getElementById('evidence-'+code);
  const notesEl=document.getElementById('notes-'+code);
  const result=resultEl?resultEl.value:'not_observed';
  const evidence=evidenceEl?evidenceEl.value.trim():'';
  const notes=notesEl?notesEl.value.trim():'';
  if(result==='competent' && !evidence){alert('Enter an evidence reference before recording a Competent result.');return;}
  if(result==='needs_training' && !notes){alert('Document the training gap or remediation need in Evaluator Notes.');return;}
  const statusEl=document.getElementById('save-status-'+code);
  if(statusEl) statusEl.textContent='Saving evaluation…';
  button.disabled=true; button.textContent='Recording…';
  try{
    const rpc=await getSupabaseClient().rpc('record_competency_evaluation',{
      p_user_id:selectedCompetencyUserId,
      p_competency_code:code,
      p_result:result,
      p_evidence_reference:evidence||null,
      p_notes:notes||null
    });
    if(rpc.error) throw rpc.error;
    await renderCompetencyCards();
    const savedStatus=document.getElementById('save-status-'+code);
    if(savedStatus) savedStatus.textContent='Evaluation recorded successfully.';
  }catch(err){
    const message=err&&err.message?err.message:'Unable to record competency evaluation.';
    if(statusEl) statusEl.textContent='Save failed: '+message;
    alert(message);
    button.disabled=false; button.textContent='Record Evaluation';
  }
}
window.saveCompetency=saveCompetency;

// ═══════════════════════════════════════════════════════════════════
// FIELD TRAINING & ROUTE READINESS — CVN-FTR-001
// ═══════════════════════════════════════════════════════════════════
async function loadFieldTrainingWorkspace(){
  if(!canEvaluateCompetencies()) throw new Error('Trainer, leadership, or administrator access is required.');
  const client=getSupabaseClient();

  const defs=await client.from('competency_definitions')
    .select('competency_code,required,active,curriculum_version_id')
    .eq('active',true).eq('required',true).eq('curriculum_version_id',currentCurriculumVersionId)
    .order('display_order',{ascending:true});
  if(defs.error) throw defs.error;
  const requiredCodes=(defs.data||[]).map(x=>x.competency_code);

  const prof=await client.from('profiles')
    .select('user_id,full_name,role,job_title,department,active')
    .eq('active',true).order('full_name',{ascending:true});
  if(prof.error) throw prof.error;
  const employees = await scopeProfilesToLeadershipDepartment(client, prof.data||[]);
  const ids=employees.map(x=>x.user_id);

  let enrollments=[];
  if(ids.length){
    const er=await client.from('enrollments')
      .select('id,user_id,curriculum_version_id,assigned_role,active')
      .in('user_id',ids).eq('active',true);
    if(er.error) throw er.error;
    enrollments=er.data||[];
  }
  const enrollmentByUser=new Map(enrollments.filter(e=>e.curriculum_version_id===currentCurriculumVersionId).map(e=>[e.user_id,e]));
  const enrollmentIds=[...enrollmentByUser.values()].map(e=>e.id);

  let finals=[];
  if(enrollmentIds.length){
    const ar=await client.from('assessment_attempts')
      .select('enrollment_id,assessment_type,passed,submitted_at')
      .in('enrollment_id',enrollmentIds).eq('assessment_type','employee_final').eq('passed',true).is('unit_id',null);
    if(ar.error) throw ar.error;
    finals=ar.data||[];
  }
  const passedEnrollments=new Set(finals.map(a=>a.enrollment_id));

  let competencyRows=[];
  if(ids.length && requiredCodes.length){
    const cr=await client.from('competency_records')
      .select('user_id,competency_code,result,evaluated_at,created_at')
      .in('user_id',ids).in('competency_code',requiredCodes)
      .order('evaluated_at',{ascending:false});
    if(cr.error) throw cr.error;
    competencyRows=cr.data||[];
  }

  const latestByUser=new Map();
  competencyRows.forEach(r=>{
    if(!latestByUser.has(r.user_id)) latestByUser.set(r.user_id,new Map());
    const m=latestByUser.get(r.user_id);
    if(!m.has(r.competency_code)) m.set(r.competency_code,r);
  });

  fieldTrainingCandidates=employees.map(p=>{
    const e=enrollmentByUser.get(p.user_id);
    const finalPassed=!!(e&&passedEnrollments.has(e.id));
    const latest=latestByUser.get(p.user_id)||new Map();
    const baselineCompetent=requiredCodes.length>0 && requiredCodes.every(code=>latest.get(code)?.result==='competent');
    return {...p,enrollment_id:e?e.id:null,final_passed:finalPassed,baseline_competent:baselineCompetent,eligible:finalPassed&&baselineCompetent};
  });
}

async function renderFieldTrainingReadiness(){
  const root=document.getElementById('contentInner');
  if(!canEvaluateCompetencies()){
    root.innerHTML='<div class="comp-error">This control is available only to Trainer, Leadership, and Administrator roles.</div>';
    return;
  }
  root.innerHTML='<div class="comp-notice">Loading controlled field-training workspace…</div>';
  try{
    await loadFieldTrainingWorkspace();
    const eligible=fieldTrainingCandidates.filter(x=>x.eligible);
    if(selectedFieldTrainingUserId && !eligible.some(x=>x.user_id===selectedFieldTrainingUserId)) selectedFieldTrainingUserId=null;
    if(!selectedFieldTrainingUserId && eligible.length) selectedFieldTrainingUserId=eligible[0].user_id;

    let options='<option value="">Select employee</option>';
    fieldTrainingCandidates.forEach(p=>{
      let suffix='';
      if(!p.final_passed) suffix=' — Final assessment not passed';
      else if(!p.baseline_competent) suffix=' — Baseline competency incomplete';
      options+=`<option value="${p.user_id}" ${p.user_id===selectedFieldTrainingUserId?'selected':''} ${p.eligible?'':'disabled'}>${esc(p.full_name+suffix)}</option>`;
    });

    root.innerHTML=`
      <div class="unit-header">
        <div class="unit-eyebrow">QUALIFICATION CONTROL · CVN-FTR-001</div>
        <div class="unit-title">Field Training & Route Readiness</div>
        <div class="unit-subtitle">Document supervised field observation after the employee has passed the controlled final assessment and demonstrated all required baseline hands-on competencies. A Ready result advances the qualification file; it does not independently grant operational authorization.</div>
      </div>
      <div class="comp-notice">Eligibility requires a passed Employee Final Assessment plus current Competent results for all required baseline competencies (CVN-COMP-001-01 through 08). Service-specific competencies remain separate and support later endorsement decisions.</div>
      <div class="comp-toolbar">
        <div class="comp-field"><label>Employee</label><select id="fieldTrainingEmployee" onchange="changeFieldTrainingEmployee(this.value)">${options}</select></div>
        <button type="button" class="comp-save" onclick="refreshFieldTrainingWorkspace()">Refresh Records</button>
        <button type="button" class="secondary-btn" onclick="returnToPersonnelRecord(selectedFieldTrainingUserId)">← Back to Employee Record</button>
      </div>
      <div id="fieldTrainingPanel"></div>`;
    await renderFieldTrainingPanel();
  }catch(err){
    root.innerHTML=`<div class="comp-error">${esc(err&&err.message?err.message:'Unable to load field-training workspace.')}</div>`;
  }
}

async function changeFieldTrainingEmployee(userId){
  selectedFieldTrainingUserId=userId||null;
  await renderFieldTrainingPanel();
}
window.changeFieldTrainingEmployee=changeFieldTrainingEmployee;

async function refreshFieldTrainingWorkspace(){
  await renderFieldTrainingReadiness();
}
window.refreshFieldTrainingWorkspace=refreshFieldTrainingWorkspace;

async function renderFieldTrainingPanel(){
  const host=document.getElementById('fieldTrainingPanel');
  if(!host) return;
  if(!selectedFieldTrainingUserId){
    host.innerHTML='<div class="comp-notice">No employee is currently eligible. Complete the required baseline competency evaluations before beginning CVN-FTR-001.</div>';
    return;
  }
  const client=getSupabaseClient();
  const history=await client.from('field_training_records')
    .select('id,user_id,trainer_id,route_or_assignment,readiness_result,observed_at,evidence_reference,notes,created_at')
    .eq('user_id',selectedFieldTrainingUserId).order('observed_at',{ascending:false});
  if(history.error){host.innerHTML=`<div class="comp-error">${esc(history.error.message)}</div>`;return;}
  const rows=history.data||[];
  const latest=rows[0]||null;
  const latestText=latest?`${latest.readiness_result.replaceAll('_',' ')} · ${latest.observed_at?new Date(latest.observed_at).toLocaleString():''} · ${latest.route_or_assignment||''}`:'No field-training readiness record has been recorded.';
  const recentRows=rows.slice(0,5).map(r=>`<div class="comp-history" style="padding:8px 0;border-top:1px solid rgba(255,255,255,.06)"><strong style="color:var(--white)">${esc(r.readiness_result.replaceAll('_',' '))}</strong> · ${esc(r.route_or_assignment||'')} · ${r.observed_at?esc(new Date(r.observed_at).toLocaleString()):''}${r.evidence_reference?` · Evidence: ${esc(r.evidence_reference)}`:''}</div>`).join('');

  host.innerHTML=`
    <div class="comp-card required">
      <div class="comp-head"><div><div class="comp-code">CVN-FTR-001</div><div class="comp-name">Supervised Field Training & Route Readiness Observation</div><div class="comp-desc">Record what assignment was observed, the readiness decision, and the evidence supporting that decision. Do not document PHI in this record.</div></div><span class="comp-tag">Controlled record</span></div>
      <div class="comp-grid">
        <div class="comp-field"><label>Readiness Result</label><select id="ftrResult"><option value="ready">Ready</option><option value="additional_training_required">Additional Training Required</option><option value="restricted">Restricted</option></select></div>
        <div class="comp-field"><label>Route or Assignment</label><input id="ftrAssignment" placeholder="e.g., supervised route / service assignment"></div>
      </div>
      <div class="comp-grid">
        <div class="comp-field"><label>Evidence Reference</label><input id="ftrEvidence" placeholder="e.g., CVN-FTR-001 signed observation / ride-along reference"></div>
        <div class="comp-field"><label>Observer Notes</label><textarea id="ftrNotes" placeholder="Document readiness observations, gaps, restrictions, or remediation. Do not document PHI."></textarea></div>
      </div>
      <div class="comp-actions"><div><div class="comp-history">Latest record: ${esc(latestText)}</div><div class="comp-history" id="ftrSaveStatus"></div></div><button type="button" class="comp-save" id="ftrSaveBtn" onclick="window.saveFieldTrainingReadiness(this); return false;">Record Field Readiness</button></div>
    </div>
    ${rows.length?`<div class="comp-card" style="margin-top:12px"><div class="comp-name" style="margin-bottom:8px">Recent CVN-FTR-001 History</div>${recentRows}</div>`:''}`;
}

async function saveFieldTrainingReadiness(button){
  if(!selectedFieldTrainingUserId){alert('Select an eligible employee first.');return;}
  const result=document.getElementById('ftrResult')?.value||'ready';
  const assignment=(document.getElementById('ftrAssignment')?.value||'').trim();
  const evidence=(document.getElementById('ftrEvidence')?.value||'').trim();
  const notes=(document.getElementById('ftrNotes')?.value||'').trim();
  if(!assignment){alert('Enter the route or assignment that was observed.');return;}
  if(result==='ready'&&!evidence){alert('Enter an evidence reference before recording a Ready result.');return;}
  if((result==='additional_training_required'||result==='restricted')&&!notes){alert('Document the training need or restriction in Observer Notes.');return;}

  const statusEl=document.getElementById('ftrSaveStatus');
  if(statusEl) statusEl.textContent='Saving field-readiness record…';
  button.disabled=true; button.textContent='Recording…';
  try{
    const rpc=await getSupabaseClient().rpc('record_field_training_readiness',{
      p_user_id:selectedFieldTrainingUserId,
      p_route_or_assignment:assignment,
      p_readiness_result:result,
      p_evidence_reference:evidence||null,
      p_notes:notes||null
    });
    if(rpc.error) throw rpc.error;
    await renderFieldTrainingPanel();
    const saved=document.getElementById('ftrSaveStatus');
    if(saved) saved.textContent='Field-readiness record saved successfully.';
  }catch(err){
    const message=err&&err.message?err.message:'Unable to record field-training readiness.';
    if(statusEl) statusEl.textContent='Save failed: '+message;
    alert(message);
    button.disabled=false; button.textContent='Record Field Readiness';
  }
}
window.saveFieldTrainingReadiness=saveFieldTrainingReadiness;

// ═══════════════════════════════════════════════════════════════════
// CVN-AUTH-001 — OPERATIONAL ENDORSEMENT MATRIX
// ═══════════════════════════════════════════════════════════════════
const ENDORSEMENT_MATRIX = [
  {code:'CVN-END-GENERAL', name:'General Clinical Courier', competency:null},
  {code:'CVN-END-SPECIMEN', name:'Specimen Transport', competency:'CVN-COMP-001-09'},
  {code:'CVN-END-COLD', name:'Cold Chain Transport', competency:'CVN-COMP-001-10'},
  {code:'CVN-END-BLOOD', name:'Blood Product Transport', competency:'CVN-COMP-001-11'},
  {code:'CVN-END-PHARM-HOME', name:'Specialty Pharmacy & Home Patient Delivery', competency:'CVN-COMP-001-12'},
  {code:'CVN-END-STAT', name:'STAT & Critical Response', competency:'CVN-COMP-001-13'}
];

async function loadEndorsementWorkspace(){
  if(!canApproveEndorsements()) throw new Error('Leadership or Administrator access is required.');
  const client=getSupabaseClient();
  const [profilesRes,enrollRes,attemptRes,compRes,ftrRes]=await Promise.all([
    client.from('profiles').select('user_id,full_name,role,department,active').eq('active',true).order('full_name'),
    client.from('enrollments').select('id,user_id,assigned_role,active,curriculum_version_id').eq('active',true),
    client.from('assessment_attempts').select('enrollment_id,assessment_type,passed,submitted_at').eq('assessment_type','employee_final').eq('passed',true),
    client.from('competency_records').select('id,user_id,competency_code,result,evaluated_at,created_at').order('evaluated_at',{ascending:false}),
    client.from('field_training_records').select('id,user_id,readiness_result,observed_at,created_at').order('observed_at',{ascending:false})
  ]);
  for(const r of [profilesRes,enrollRes,attemptRes,compRes,ftrRes]) if(r.error) throw r.error;
  const employees = await scopeProfilesToLeadershipDepartment(
  client,
  profilesRes.data || []
);
  const enrollmentByUser=new Map((enrollRes.data||[]).map(e=>[e.user_id,e]));
  const passedEnrollments=new Set((attemptRes.data||[]).map(a=>a.enrollment_id));
  const latestComp=new Map();
  (compRes.data||[]).forEach(r=>{
    if(!latestComp.has(r.user_id)) latestComp.set(r.user_id,new Map());
    const m=latestComp.get(r.user_id); if(!m.has(r.competency_code)) m.set(r.competency_code,r);
  });
  const latestFtr=new Map();
  (ftrRes.data||[]).forEach(r=>{if(!latestFtr.has(r.user_id)) latestFtr.set(r.user_id,r);});
  endorsementCandidates=employees.map(p=>{
    const e=enrollmentByUser.get(p.user_id);
    const finalPassed=!!(e&&passedEnrollments.has(e.id));
    const ftr=latestFtr.get(p.user_id);
    const fieldReady=!!(ftr&&ftr.readiness_result==='ready');
    return {...p,enrollment_id:e?e.id:null,final_passed:finalPassed,field_ready:fieldReady,latest_competencies:latestComp.get(p.user_id)||new Map(),eligible:finalPassed&&fieldReady};
  });
}

async function renderOperationalEndorsements(){
  // Executive Demo must use simulated workforce data only.
  // Never load live employee qualification records while Demo Mode is active.
  if (demoMode && typeof renderDemoEndorsements === 'function') {
    renderDemoEndorsements();
    return;
  }

  const root=document.getElementById('contentInner');
  if(!canApproveEndorsements()){
    root.innerHTML='<div class="comp-error">CVN-AUTH-001 endorsement decisions are available only to Leadership and Administrator roles.</div>'; return;
  }
  root.innerHTML='<div class="comp-notice">Loading controlled operational endorsement matrix…</div>';
  try{
    await loadEndorsementWorkspace();
    const eligible=endorsementCandidates.filter(x=>x.eligible);
    if(selectedEndorsementUserId&&!eligible.some(x=>x.user_id===selectedEndorsementUserId)) selectedEndorsementUserId=null;
    if(!selectedEndorsementUserId&&eligible.length) selectedEndorsementUserId=eligible[0].user_id;
    let options='<option value="">Select employee</option>';
    endorsementCandidates.forEach(p=>{
      let suffix=''; if(!p.final_passed) suffix=' — Final assessment not passed'; else if(!p.field_ready) suffix=' — Field readiness not Ready';
      options+=`<option value="${p.user_id}" ${p.user_id===selectedEndorsementUserId?'selected':''} ${p.eligible?'':'disabled'}>${esc(p.full_name+suffix)}</option>`;
    });
    root.innerHTML=`
      <div class="unit-header"><div class="unit-eyebrow">QUALIFICATION CONTROL · CVN-AUTH-001</div><div class="unit-title">Operational Endorsement Matrix</div><div class="unit-subtitle">Leadership-controlled service-category decisions based on the employee qualification file. An Authorized endorsement defines a service category the employee may be considered for; it does not by itself grant final independent operational authorization.</div></div>
      <div class="comp-notice">Eligibility requires a passed Employee Final Assessment and a current CVN-FTR-001 result of Ready. Service-specific endorsements also require the matching hands-on competency to be currently Competent before Authorized can be recorded.</div>
      <div class="comp-toolbar"><div class="comp-field"><label>Employee</label><select id="endorsementEmployee" onchange="changeEndorsementEmployee(this.value)">${options}</select></div><button type="button" class="comp-save" onclick="refreshEndorsementWorkspace()">Refresh Records</button><button type="button" class="secondary-btn" onclick="returnToPersonnelRecord(selectedEndorsementUserId)">← Back to Employee Record</button></div>
     <button
  type="button"
  class="secondary-btn"
  onclick="returnToPersonnelRecord(selectedEndorsementUserId)"
>
  ← Back to Employee Record
</button>
      <div id="endorsementPanel"></div>`;
    await renderEndorsementPanel();
  }catch(err){root.innerHTML=`<div class="comp-error">${esc(err&&err.message?err.message:'Unable to load endorsement matrix.')}</div>`;}
}

async function changeEndorsementEmployee(userId){selectedEndorsementUserId=userId||null;await renderEndorsementPanel();}
window.changeEndorsementEmployee=changeEndorsementEmployee;
async function refreshEndorsementWorkspace(){await renderOperationalEndorsements();}
function openEmployeeOperationalEndorsements(userId) {
  selectedEndorsementUserId = userId || null;

  showView('endorsements');
}

window.openEmployeeOperationalEndorsements = openEmployeeOperationalEndorsements;
window.refreshEndorsementWorkspace=refreshEndorsementWorkspace;

async function renderEndorsementPanel(){
  const host=document.getElementById('endorsementPanel'); if(!host)return;
  if(!selectedEndorsementUserId){host.innerHTML='<div class="comp-notice">No employee is currently eligible. A Ready field-training result is required before CVN-AUTH-001 endorsement decisions begin.</div>';return;}
  const candidate=endorsementCandidates.find(x=>x.user_id===selectedEndorsementUserId);
  const res=await getSupabaseClient().from('endorsements').select('id,user_id,endorsement_code,endorsement_name,status,effective_at,expires_at,approved_by,evidence_reference,notes,updated_at').eq('user_id',selectedEndorsementUserId).order('updated_at',{ascending:false});
  if(res.error){host.innerHTML=`<div class="comp-error">${esc(res.error.message)}</div>`;return;}
  const byCode=new Map((res.data||[]).map(r=>[r.endorsement_code,r]));
  host.innerHTML=ENDORSEMENT_MATRIX.map(item=>{
    const cur=byCode.get(item.code); const comp=item.competency?candidate?.latest_competencies?.get(item.competency):null;
    const serviceCompetent=!item.competency||(comp&&comp.result==='competent');
    const statusText=cur?`${cur.status.replaceAll('_',' ')} · ${cur.updated_at?new Date(cur.updated_at).toLocaleString():''}`:'No endorsement decision recorded.';
    return `<div class="comp-card ${item.competency?'':'required'}" style="margin-bottom:12px">
      <div class="comp-head"><div><div class="comp-code">${esc(item.code)}</div><div class="comp-name">${esc(item.name)}</div><div class="comp-desc">${item.competency?`Required service competency: ${esc(item.competency)} · Current result: ${comp?esc(comp.result.replaceAll('_',' ')):'not recorded'}`:'Baseline endorsement supported by completed foundation qualification and Ready field-training result.'}</div></div><span class="comp-tag">${serviceCompetent?'Evidence gate met':'Competency required'}</span></div>
          <div class="comp-grid"><div class="comp-field"><label>Status</label><select id="endStatus-${item.code}"><option value="" ${!cur?.status?'selected':''}>Not Authorized</option><option value="authorized" ${cur?.status==='authorized'?'selected':''}>Authorized</option><option value="training_required" ${cur?.status==='training_required'?'selected':''}>Training Required</option><option value="restricted" ${cur?.status==='restricted'?'selected':''}>Restricted</option></select></div><div class="comp-field"><label>Evidence Reference</label><input id="endEvidence-${item.code}" value="${esc(cur?.evidence_reference||'')}" placeholder="Controlled qualification-file reference"></div></div>
      <div class="comp-actions"><div><div class="comp-history">Latest decision: ${esc(statusText)}</div><div class="comp-history" id="endSaveStatus-${item.code}"></div></div><button type="button" class="comp-save" onclick="window.saveOperationalEndorsement('${item.code}');return false;">Record Endorsement</button></div>
    </div>`;
  }).join('');
}
async function saveOperationalEndorsement(code){
  const item=ENDORSEMENT_MATRIX.find(x=>x.code===code); const candidate=endorsementCandidates.find(x=>x.user_id===selectedEndorsementUserId);
  if(!item||!candidate){alert('Select an eligible employee first.');return;}
  const status=document.getElementById(`endStatus-${code}`)?.value||'';
if(!status){alert('Select an endorsement decision before recording.');return;}
  const evidence=(document.getElementById(`endEvidence-${code}`)?.value||'').trim();
  const notes=(document.getElementById(`endNotes-${code}`)?.value||'').trim();
  if(status==='authorized'&&!evidence){alert('Enter an evidence reference before recording an Authorized endorsement.');return;}
  if((status==='training_required'||status==='restricted')&&!notes){alert('Document the training need or restriction in Leadership Notes.');return;}
  if(status==='authorized'&&item.competency){const comp=candidate.latest_competencies?.get(item.competency);if(!comp||comp.result!=='competent'){alert(`Authorized cannot be recorded until ${item.competency} is currently Competent.`);return;}}
  const statusEl=document.getElementById(`endSaveStatus-${code}`); if(statusEl)statusEl.textContent='Saving endorsement decision…';
  try{
    const rpc=await getSupabaseClient().rpc('record_operational_endorsement',{p_user_id:selectedEndorsementUserId,p_endorsement_code:item.code,p_endorsement_name:item.name,p_status:status,p_evidence_reference:evidence||null,p_notes:notes||null,p_expires_at:null});
    if(rpc.error)throw rpc.error; await renderEndorsementPanel(); const saved=document.getElementById(`endSaveStatus-${code}`);if(saved)saved.textContent='Endorsement decision saved successfully.';
  }catch(err){const message=err&&err.message?err.message:'Unable to record endorsement.';if(statusEl)statusEl.textContent='Save failed: '+message;alert(message);}
}
window.saveOperationalEndorsement=saveOperationalEndorsement;

// ═══════════════════════════════════════════════════════════════════
// OPERATIONAL AUTHORIZATION — FINAL INDEPENDENT-OPERATION GATE
// ═══════════════════════════════════════════════════════════════════
async function loadOperationalAuthorizationWorkspace(){
  const client=getSupabaseClient();
  const [profilesRes,enrollRes,attemptRes,defsRes,compRes,ftrRes,endRes,authRes]=await Promise.all([
    client.from('profiles')
  .select('user_id,full_name,role,job_title,department,reports_to_user_id,active')
  .eq('active',true),
    client.from('enrollments').select('id,user_id,assigned_role,active').eq('active',true),
    client.from('assessment_attempts').select('enrollment_id,assessment_type,passed,score_percent,submitted_at').eq('assessment_type','employee_final').eq('passed',true),
    client.from('competency_definitions').select('competency_code,required,active,role_scope,display_order').eq('active',true).eq('required',true).eq('role_scope','employee'),
    client.from('competency_records').select('id,user_id,competency_code,result,evaluated_at,created_at').order('evaluated_at',{ascending:false}),
    client.from('field_training_records').select('id,user_id,readiness_result,observed_at,created_at,evidence_reference').order('observed_at',{ascending:false}),
    client.from('endorsements').select('user_id,endorsement_code,endorsement_name,status,effective_at,evidence_reference,updated_at').order('updated_at',{ascending:false}),
    client.from('operational_authorizations').select('id,user_id,status,authorized_by,authorized_at,suspended_or_revoked_at,reason,qualification_file_audited,qualification_file_reference,created_at').order('created_at',{ascending:false})
  ]);
  for(const r of [profilesRes,enrollRes,attemptRes,defsRes,compRes,ftrRes,endRes,authRes]) if(r.error) throw r.error;

  let employees = await scopeProfilesToLeadershipDepartment(
  client,
  profilesRes.data || []
);
  const enrollmentByUser=new Map((enrollRes.data||[]).map(e=>[e.user_id,e]));
  const passedEnrollments=new Set((attemptRes.data||[]).map(a=>a.enrollment_id));
  const requiredCodes=(defsRes.data||[]).map(d=>d.competency_code);
  const latestComp=new Map();
  (compRes.data||[]).forEach(r=>{if(!latestComp.has(r.user_id))latestComp.set(r.user_id,new Map());const m=latestComp.get(r.user_id);if(!m.has(r.competency_code))m.set(r.competency_code,r);});
  const latestFtr=new Map();
  (ftrRes.data||[]).forEach(r=>{if(!latestFtr.has(r.user_id))latestFtr.set(r.user_id,r);});
  const endorsementsByUser=new Map();
  (endRes.data||[]).forEach(r=>{if(!endorsementsByUser.has(r.user_id))endorsementsByUser.set(r.user_id,[]);endorsementsByUser.get(r.user_id).push(r);});
  const latestAuth=new Map();
  (authRes.data||[]).forEach(r=>{if(!latestAuth.has(r.user_id))latestAuth.set(r.user_id,r);});
  authorizationCandidates=employees.map(p=>{
    const e=enrollmentByUser.get(p.user_id);
    const finalPassed=!!(e&&passedEnrollments.has(e.id));
    const cm=latestComp.get(p.user_id)||new Map();
    const competentCount=requiredCodes.filter(code=>cm.get(code)?.result==='competent').length;
    const competenciesComplete=requiredCodes.length>0&&competentCount===requiredCodes.length;
    const ftr=latestFtr.get(p.user_id); const fieldReady=!!(ftr&&ftr.readiness_result==='ready');
    const endorsements=endorsementsByUser.get(p.user_id)||[];
    const authorizedEndorsements=endorsements.filter(x=>x.status==='authorized');
    const endorsementsDocumented=endorsements.length>0;
    const eligible=finalPassed&&competenciesComplete&&fieldReady&&endorsementsDocumented;
   return {
  ...p,
  enrollment_id: e?.id || null,
  final_passed: finalPassed,
  required_count: requiredCodes.length,
  competent_count: competentCount,
  competencies_complete: competenciesComplete,
  competency_records: requiredCodes.map(code => ({
    competency_code: code,
    record: cm.get(code) || null
  })),
  field_ready: fieldReady,
  field_record: ftr || null,
  endorsements,
  authorized_endorsements: authorizedEndorsements,
  endorsements_documented: endorsementsDocumented,
  latest_authorization: latestAuth.get(p.user_id) || null,
  eligible
};
  });
}

function gateLine(ok,label,detail){return `<div style="display:flex;gap:10px;align-items:flex-start;padding:10px 0;border-bottom:1px solid rgba(255,255,255,.07)"><div style="font-weight:800;color:${ok?'#4ADE80':'#FCA5A5'};min-width:48px">${ok?'PASS':'HOLD'}</div><div><div style="font-weight:700;color:var(--white)">${esc(label)}</div><div class="comp-history">${esc(detail)}</div></div></div>`;}

async function renderOperationalAuthorization(){
    // Executive Demo must use simulated workforce data only.
  // Never load live employee qualification records while Demo Mode is active.
  if (demoMode && typeof renderDemoAuthorization === 'function') {
    renderDemoAuthorization();
    return;
  }
  const root=document.getElementById('contentInner');
  if(!canIssueOperationalAuthorization()){root.innerHTML='<div class="comp-error">Final Operational Authorization decisions are available only to Leadership and Administrator roles.</div>';return;}
  root.innerHTML='<div class="comp-notice">Loading controlled qualification-file review…</div>';
  try{
    await loadOperationalAuthorizationWorkspace();
    if(selectedAuthorizationUserId&&!authorizationCandidates.some(x=>x.user_id===selectedAuthorizationUserId))selectedAuthorizationUserId=null;
    if(!selectedAuthorizationUserId&&authorizationCandidates.length)selectedAuthorizationUserId=authorizationCandidates[0].user_id;
    let options='<option value="">Select employee</option>';
    authorizationCandidates.forEach(p=>{options+=`<option value="${p.user_id}" ${p.user_id===selectedAuthorizationUserId?'selected':''}>${esc(p.full_name)}</option>`;});
    root.innerHTML=`
      <div class="unit-header"><div class="unit-eyebrow">FINAL QUALIFICATION CONTROL</div><div class="unit-title">Operational Authorization</div><div class="unit-subtitle">Leadership review for independent operation. Authorization is issued only after the controlled qualification file is reviewed and remains limited to service categories carrying an Authorized endorsement.</div></div>
      <div class="comp-notice">This is the final release gate. Course completion, the Final Assessment, competency, field readiness, or an individual endorsement does not independently authorize field operation.</div>
      <div class="comp-toolbar"><div class="comp-field"><label>Employee</label><select id="authorizationEmployee" onchange="changeAuthorizationEmployee(this.value)">${options}</select></div><button type="button" class="comp-save" onclick="refreshAuthorizationWorkspace()">Refresh Qualification File</button><button type="button" class="secondary-btn" onclick="returnToPersonnelRecord(selectedAuthorizationUserId)">← Back to Employee Record</button> </div>
       <div id="authorizationPanel"></div>`;
    await renderAuthorizationPanel();
  }catch(err){root.innerHTML=`<div class="comp-error">${esc(err&&err.message?err.message:'Unable to load Operational Authorization workspace.')}</div>`;}
}
async function changeAuthorizationEmployee(userId){selectedAuthorizationUserId=userId||null;await renderAuthorizationPanel();}
window.changeAuthorizationEmployee=changeAuthorizationEmployee;
function openEmployeeOperationalAuthorization(userId) {
  selectedAuthorizationUserId = userId || null;

  showView('authorization');
}

window.openEmployeeOperationalAuthorization = openEmployeeOperationalAuthorization;
async function refreshAuthorizationWorkspace(){await renderOperationalAuthorization();}
window.refreshAuthorizationWorkspace=refreshAuthorizationWorkspace;
async function renderAuthorizationPanel(){
  const host=document.getElementById('authorizationPanel');if(!host)return;
  const c=authorizationCandidates.find(x=>x.user_id===selectedAuthorizationUserId);
  if(!c){host.innerHTML='<div class="comp-notice">Select an employee to review the qualification file.</div>';return;}
  const current=c.latest_authorization;
  const currentText=current?`${current.status.replaceAll('_',' ')} · ${new Date(current.created_at).toLocaleString()}`:'No Operational Authorization decision recorded.';
  const authNames=c.authorized_endorsements.length?c.authorized_endorsements.map(x=>x.endorsement_name).join(', '):'None currently Authorized';
  const matrixSummary=c.endorsements.length?c.endorsements.map(x=>`${x.endorsement_name}: ${x.status.replaceAll('_',' ')}`).join(' · '):'No endorsement decisions recorded';
  host.innerHTML=`
    <div class="comp-card required" style="margin-bottom:14px">
      <div class="comp-head"><div><div class="comp-code">QUALIFICATION FILE REVIEW</div><div class="comp-name">${esc(c.full_name)}</div><div class="comp-desc">Database-verified prerequisites are shown below. Leadership must still perform the actual qualification-file audit before selecting Authorized.</div></div><span class="comp-tag">Final gate</span></div>
      ${gateLine(c.final_passed,'Employee Final Assessment',c.final_passed?'Passed':'A passing Employee Final Assessment is required.')}
      ${gateLine(c.competencies_complete,'Required Hands-On Competencies',`${c.competent_count} of ${c.required_count} required competencies currently Competent.`)}
      ${gateLine(c.field_ready,'Field Training & Route Readiness',c.field_ready?'Latest CVN-FTR-001 result is Ready.':'Latest field-readiness result must be Ready.')}
      ${gateLine(c.endorsements_documented,'Operational Endorsement Matrix',matrixSummary)}
      <div style="margin-top:12px" class="comp-notice"><strong>Authorized service scope:</strong> ${esc(authNames)}. Final Operational Authorization does not expand this scope.</div>
    </div>
    <div class="comp-card" style="margin-bottom:14px">
      <div class="comp-head"><div><div class="comp-code">FINAL DECISION</div><div class="comp-name">Leadership / Administrator Authorization Record</div><div class="comp-desc">Record the qualification-file audit and final operating decision. Suspension or revocation requires a documented reason.</div></div></div>
      <div class="comp-grid"><div class="comp-field"><label>Decision</label><select id="authDecision"><option value="authorized">Authorized</option><option value="suspended">Suspended</option><option value="revoked">Revoked</option></select></div><div class="comp-field"><label>Qualification File Reference</label><input id="authFileRef" value="" placeholder="Controlled qualification-file reference"></div></div>
      <div class="comp-field" style="margin-top:10px"><label style="display:flex;align-items:center;gap:8px"><input type="checkbox" id="authFileAudited" style="width:auto"> I confirm the qualification file has been audited for this decision.</label></div>
      <div class="comp-field"><label>Reason / Leadership Notes</label><textarea id="authReason" placeholder="Document decision basis, restrictions, suspension/revocation reason, or other controlled notes. Do not document PHI."></textarea></div>
      <div class="comp-actions"><div><div class="comp-history">Latest authorization record: ${esc(currentText)}</div><div class="comp-history" id="authSaveStatus"></div></div><button type="button" class="comp-save" onclick="window.saveOperationalAuthorization();return false;">Record Authorization Decision</button></div>
    </div>`;
}

async function saveOperationalAuthorization(){
  const c=authorizationCandidates.find(x=>x.user_id===selectedAuthorizationUserId);if(!c){alert('Select an employee first.');return;}
  const status=document.getElementById('authDecision')?.value||'authorized';
  const audited=!!document.getElementById('authFileAudited')?.checked;
  const fileRef=(document.getElementById('authFileRef')?.value||'').trim();
  const reason=(document.getElementById('authReason')?.value||'').trim();
  if(status==='authorized'){
    if(!c.eligible){alert('This employee has not satisfied all database-verified qualification gates.');return;}
    if(!c.authorized_endorsements.length){alert('At least one service category should carry an Authorized endorsement before independent operation is released.');return;}
    if(!audited){alert('Confirm the qualification-file audit before recording Authorized.');return;}
    if(!fileRef){alert('Enter the controlled qualification-file reference before recording Authorized.');return;}
  }
  if((status==='suspended'||status==='revoked')&&!reason){alert('Document the reason for suspension or revocation.');return;}
  const statusEl=document.getElementById('authSaveStatus');if(statusEl)statusEl.textContent='Saving final authorization decision…';
  try{
    const rpc=await getSupabaseClient().rpc('record_operational_authorization',{p_user_id:selectedAuthorizationUserId,p_status:status,p_qualification_file_audited:audited,p_qualification_file_reference:fileRef||null,p_reason:reason||null});
    if(rpc.error)throw rpc.error;
    await loadOperationalAuthorizationWorkspace();await renderAuthorizationPanel();
    const saved=document.getElementById('authSaveStatus');if(saved)saved.textContent='Operational Authorization decision saved successfully.';
  }catch(err){const message=err&&err.message?err.message:'Unable to record Operational Authorization.';if(statusEl)statusEl.textContent='Save failed: '+message;alert(message);}
}
window.saveOperationalAuthorization=saveOperationalAuthorization;

// ═══════════════════════════════════════════════════════════════════
// UNIT VIEW
// ═══════════════════════════════════════════════════════════════════
function renderUnit(uid) {
  try {
    const allUnits = getUnitsForRole(currentRole);
    const u = allUnits.find(x => x.id === uid);
    if (!u) {
      throw new Error('Unit '+uid+' was not found in the loaded curriculum.');
    }

    const p = unitProgress[uid] || {};
    const isLeader = u.role === 'leadership';
    const accentColor = isLeader ? '#C4B5FD' : 'var(--teal)';
    const accentBg = isLeader ? 'rgba(124,58,237,.1)' : 'rgba(25,181,176,.06)';

    let html = `<div class="unit-header" style="${isLeader ? 'border-color:rgba(124,58,237,.3)' : ''}">
      <div class="unit-eyebrow" style="color:${accentColor}">UNIT ${u.num} ${isLeader ? '· LEADERSHIP' : ''}</div>
      <div class="unit-title">${esc(u.title)}</div>
      <div class="unit-subtitle">${esc(u.subtitle || '')}</div>
      ${u.quote ? `<div class="unit-quote" style="border-color:${accentColor};background:${accentBg};">"${esc(u.quote)}"</div>` : ''}
    </div>`;

    if (!Array.isArray(u.sections) || !u.sections.length) {
      html += `<div class="section-block"><div class="section-heading">Lesson content unavailable</div><div class="section-body">This controlled unit does not currently contain lesson sections. Contact Operations.</div></div>`;
    } else {
      html += `<div id="unitLessonContent${uid}">`;
      u.sections.forEach(function(sec){
        html += `<div class="section-block">
          <div class="section-heading">${esc(sec && sec.h != null ? sec.h : '')}</div>
          <div class="section-body">${esc(sec && sec.body != null ? sec.body : '')}</div>
        </div>`;
      });
      html += `</div>`;
    }

    if (!unitProgress[uid]) unitProgress[uid] = {read:false,score:null,passed:false};
    unitProgress[uid].read = true;

    if (Array.isArray(u.quiz) && u.quiz.length) {
      html += buildQuiz(u, p);
    } else {
      html += `<div class="section-block"><div class="section-heading">Knowledge Check</div><div class="section-body">No controlled quiz is currently configured for this unit. Contact Operations before this unit is marked complete.</div></div>`;
    }

    const idx = allUnits.findIndex(x => x.id === uid);
    const prev = idx > 0 ? allUnits[idx-1] : null;
    const next = idx < allUnits.length-1 ? allUnits[idx+1] : null;
    html += `
  <div class="btn-row" style="
    margin-top:32px;
    border-top:1px solid rgba(255,255,255,.06);
    padding-top:20px;
    display:flex;
    align-items:center;
    justify-content:space-between;
    gap:12px;
    flex-wrap:wrap;
  ">
    <button
      class="btn btn-outline"
      type="button"
      onclick="scrollToTop()"
    >
      ↑ Back to Top
    </button>

    <div style="display:flex;gap:10px;flex-wrap:wrap;">
      ${prev ? `
        <button
          class="btn btn-outline"
          type="button"
          onclick="navToUnit(${prev.id})"
        >
          ← Previous Unit
        </button>
      ` : ''}

      ${next && unitProgress[uid]?.passed ? `
        <button
          class="btn btn-primary"
          type="button"
          onclick="navToUnit(${next.id})"
        >
          Continue to Unit ${next.num} →
        </button>
      ` : ''}
    </div>
  </div>`;
    const target = document.getElementById('contentInner');
    if (!target) throw new Error('The lesson content container was not found.');
    target.innerHTML = html;
    target.scrollTop = 0;
    window.scrollTo({top:0,behavior:'instant'});

    // Persist only after the lesson has successfully rendered so a database/network
    // issue can never prevent the learner from seeing controlled curriculum content.
    if (!demoMode) {
      window.persistUnitStarted(u).then(function(){
        updateProgress();
      }).catch(function(err){
        console.error('Could not mark unit started:', err);
      });
    }
  } catch (err) {
    console.error('Corevian lesson render error:', err);
    const target = document.getElementById('contentInner');
    if (target) {
      target.innerHTML = `<div class="dash-hero"><div class="dash-welcome">Corevian University</div><div class="dash-name">Lesson could not be displayed</div><div class="dash-tagline">${esc(err && err.message ? err.message : 'Unknown lesson rendering error.')}</div><div class="btn-row"><button class="btn btn-outline" onclick="goDashboard(event)">Back to Dashboard</button></div></div>`;
    } else {
      alert('Lesson could not be displayed: '+(err && err.message ? err.message : err));
    }
  }
}

function buildQuiz(u, p) {
  const passed = p && p.passed;
  let html = `<div class="quiz-container">
    <div class="quiz-header">
      <div class="quiz-eyebrow">Knowledge Check</div>
      <div class="quiz-title">Unit ${u.num} · ${esc(u.title)}</div>
      <div class="quiz-meta">${u.quiz.length} questions · Answer without looking at your notes · ${Number(u.masteryPercent || 90).toFixed(0)}% to pass</div>
    </div>
    <div id="qz${u.id}">`;

  u.quiz.forEach((q, qi) => {
    html += `<div class="q-block" id="q${u.id}_${qi}">
      <div class="q-num">QUESTION ${qi+1} OF ${u.quiz.length}</div>
      <div class="q-text">${esc(q.q)}</div>
      <div class="q-opts">`;
    const attemptState = getQuizAttemptState(u.id);

if (!attemptState.optionOrders[qi]) {
  attemptState.optionOrders[qi] = shuffleQuizOptions(
    Array.isArray(q.opts) ? q.opts : []
  );
}

const options = attemptState.optionOrders[qi];

options.forEach((option, displayIndex) => {
  const originalIndex = option.originalIndex;

  html += `<div class="q-opt"
    id="qo${u.id}_${qi}_${originalIndex}"
    onclick="selectOpt(${u.id},${qi},${originalIndex},${Number(q.correct)})">
      <div class="opt-circle">${String.fromCharCode(65 + displayIndex)}</div>
      <div class="opt-text">${esc(option.text)}</div>
    </div>`;
});
    html += `</div>
      <div class="q-feedback" id="qf${u.id}_${qi}">
        <strong id="qfv${u.id}_${qi}"></strong>
        <span id="qft${u.id}_${qi}"></span>
      </div>
    </div>`;
  });

  html += `</div>
    <div id="qscore${u.id}" style="display:none"></div>
    <div class="btn-row" id="qbtn${u.id}">
      <button class="btn btn-primary" onclick="submitQuiz(${u.id})">Submit Answers</button>
    </div>
  </div>`;
  return html;
}
let quizAnswers = {};
let quizAttemptState = {};

function getQuizAttemptState(uid) {
  if (!quizAttemptState[uid]) {
    quizAttemptState[uid] = {
      attempt: 1,
      requiresReview: false,
      reviewConfirmed: false,
      optionOrders: {}
    };
  }

  return quizAttemptState[uid];
}
function shuffleQuizOptions(options) {
  const shuffled = options.map((text, originalIndex) => ({
    text,
    originalIndex
  }));

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}
function selectOpt(uid, qi, oi, correct) {
  // Clear other selections for this question
  const opts = document.querySelectorAll(`[id^="qo${uid}_${qi}_"]`);
  opts.forEach(o => {
    o.classList.remove('selected');
    const c = o.querySelector('.opt-circle');
    c.textContent = c.textContent.length === 1 ? c.textContent : c.textContent.slice(0,1);
  });
  const el = document.getElementById(`qo${uid}_${qi}_${oi}`);
  el.classList.add('selected');
  if (!quizAnswers[uid]) quizAnswers[uid] = {};
  quizAnswers[uid][qi] = oi;
}

async function submitQuiz(uid) {
  const allUnits = getUnitsForRole(currentRole);
  const u = allUnits.find(x => x.id === uid);
  const answers = quizAnswers[uid] || {};
  if (!u) return;
  const unanswered = u.quiz.map((q,i) => i).filter(i => answers[i] === undefined);
  if (unanswered.length > 0) { alert(`Please answer all questions before submitting. (${unanswered.length} unanswered)`); return; }

  let correct = 0;
  u.quiz.forEach((q, qi) => {
    const selected = answers[qi];
    const isCorrect = selected === q.correct;
    if (isCorrect) correct++;
   // Lock the submitted answers without revealing the correct answer.
const opts = document.querySelectorAll(`[id^="qo${uid}_${qi}_"]`);

opts.forEach((o, oi) => {
  o.onclick = null;

  // Show only the learner's selected response.
  // Do not reveal which option is correct.
  if (oi === selected) {
    o.classList.add(isCorrect ? 'correct' : 'incorrect');
  }
});

// Give outcome feedback without exposing the answer key.
const fb = document.getElementById(`qf${uid}_${qi}`);
const fv = document.getElementById(`qfv${uid}_${qi}`);
const ft = document.getElementById(`qft${uid}_${qi}`);

fb.classList.add('show', isCorrect ? 'correct' : 'incorrect');
fv.textContent = isCorrect ? 'CORRECT ✓' : 'INCORRECT X';

ft.textContent = isCorrect
  ? (q.feedback || '')
  : 'Review the applicable lesson content before your next attempt. The correct answer is not displayed.';
  });

  const score = correct / u.quiz.length;
  const pct = Math.round(score * 100);
  const masteryPercent = Number(u.masteryPercent || 90);
  const passed = pct >= masteryPercent;

  if (!demoMode) {
    try { await window.persistQuizResult(u, answers, pct, passed, correct); }
    catch (err) {
      console.error(err);
      alert('Your quiz was scored, but Corevian University could not save the result to the production training record. Your next unit will remain locked. Please contact Operations before retaking.');
      return;
    }
  } else {
    unitProgress[uid] = { read: true, score: score, passed: passed };
  }

  const scoreEl = document.getElementById(`qscore${uid}`);
  scoreEl.style.display = 'block';
  scoreEl.innerHTML = `<div class="quiz-score" style="margin-top:20px">
    <div class="score-big ${passed?'pass':'fail'}">${pct}%</div>
    <div class="score-label">${correct} of ${u.quiz.length} correct</div>
    <div class="score-verdict ${passed?'pass':'fail'}">${passed ? '✓ PASSED — Unit Complete' : '✗ Below '+masteryPercent.toFixed(0)+'% — Review and Retake'}</div>
    ${!passed ? `<p style="color:var(--slate);font-size:13px;margin-top:12px">Re-read the unit sections that correspond to the questions you missed, then retake this knowledge check.</p>` : ''}
  </div>`;

  const btnEl = document.getElementById(`qbtn${uid}`);
  if (passed) {
    const idx = allUnits.findIndex(x => x.id === uid);
    const next = idx < allUnits.length - 1 ? allUnits[idx+1] : null;
    btnEl.innerHTML = `<button class="btn btn-primary" onclick="${next ? 'navToUnit('+next.id+')' : "goDashboard(event)"}">${next ? 'Continue to Unit '+next.num+' →' : 'View Dashboard'}</button>
      <button class="btn btn-outline" onclick="goDashboard(event)">Back to Dashboard</button>`;
   } else {
  const state = getQuizAttemptState(uid);
  state.requiresReview = true;
  state.reviewConfirmed = false;

  btnEl.innerHTML = `
    <button class="btn btn-primary" onclick="retakeQuiz(${uid})">Retake Quiz</button>
    <button class="btn btn-outline" onclick="beginQuizReview(${uid})">Review Unit</button>
  `;
}
  updateProgress();
  scoreEl.scrollIntoView({behavior:'smooth', block:'center'});
}
/* ================================================================
   COREVIAN UNIVERSITY — KNOWLEDGE CHECK REVIEW GATE
   Production-integrated remediation workflow
   ================================================================ */

function removeQuizReviewListener(uid) {
  const state = getQuizAttemptState(uid);

  if (state._reviewScrollFn && state._reviewScrollEl) {
    state._reviewScrollEl.removeEventListener(
      'scroll',
      state._reviewScrollFn
    );

    window.removeEventListener(
      'scroll',
      state._reviewScrollFn
    );
  }

  if (state._reviewWatcherTimer) {
    clearTimeout(state._reviewWatcherTimer);
  }

  state._reviewScrollFn = null;
  state._reviewScrollEl = null;
  state._reviewWatcherTimer = null;
}


function setQuizReviewProgress(uid, activeStep) {
  for (let i = 1; i <= 4; i++) {
    const el = document.getElementById(`cvnRvStep_${uid}_${i}`);
    if (!el) continue;

    const circle = el.querySelector('.cvn-rv-circle');
    const label = el.querySelector('.cvn-rv-label');

    const isDone = i < activeStep;
    const isActive = i === activeStep;

    if (circle) {
      circle.style.borderColor =
        isDone
          ? 'rgba(25,181,176,.50)'
          : isActive
            ? 'rgba(25,181,176,.40)'
            : 'rgba(255,255,255,.10)';

      circle.style.background =
        isDone
          ? 'rgba(25,181,176,.18)'
          : isActive
            ? 'rgba(25,181,176,.10)'
            : 'rgba(255,255,255,.03)';

      circle.style.color =
        isDone
          ? '#19B5B0'
          : isActive
            ? '#FFFFFF'
            : '#3A5060';

      circle.textContent = isDone ? '✓' : String(i);
    }

    if (label) {
      label.style.color =
        isDone
          ? '#19B5B0'
          : isActive
            ? '#9DE7E3'
            : '#3A5060';
    }
  }
}


function quizReviewStepHTML(uid, num, labelText, stateName) {
  const isDone = stateName === 'done';
  const isActive = stateName === 'active';

  const border =
    isDone
      ? 'rgba(25,181,176,.50)'
      : isActive
        ? 'rgba(25,181,176,.40)'
        : 'rgba(255,255,255,.10)';

  const bg =
    isDone
      ? 'rgba(25,181,176,.18)'
      : isActive
        ? 'rgba(25,181,176,.10)'
        : 'rgba(255,255,255,.03)';

  const numberColor =
    isDone
      ? '#19B5B0'
      : isActive
        ? '#FFFFFF'
        : '#3A5060';

  const labelColor =
    isDone
      ? '#19B5B0'
      : isActive
        ? '#9DE7E3'
        : '#3A5060';

  return `
    <div
      id="cvnRvStep_${uid}_${num}"
      style="
        display:flex;
        flex-direction:column;
        align-items:center;
        gap:5px;
        flex:1;
        min-width:72px;
      "
    >
      <div
        class="cvn-rv-circle"
        style="
          width:30px;
          height:30px;
          border-radius:50%;
          border:1.5px solid ${border};
          background:${bg};
          color:${numberColor};
          display:flex;
          align-items:center;
          justify-content:center;
          font-size:12px;
          font-weight:800;
        "
      >
        ${isDone ? '✓' : num}
      </div>

      <div
        class="cvn-rv-label"
        style="
          font-size:10px;
          font-weight:700;
          letter-spacing:.04em;
          text-align:center;
          color:${labelColor};
          line-height:1.3;
        "
      >
        ${labelText}
      </div>
    </div>
  `;
}


function quizReviewConnectorHTML() {
  return `
    <div
      style="
        flex:1;
        height:1px;
        background:rgba(255,255,255,.07);
        margin-bottom:14px;
        min-width:10px;
      "
    ></div>
  `;
}


function unlockQuizReviewConfirmation(uid) {
  const state = getQuizAttemptState(uid);

  if (state.scrollCompleted) return;

  state.scrollCompleted = true;

  removeQuizReviewListener(uid);

  const checkbox =
    document.getElementById(`quizReviewConfirm${uid}`);

  const lockMessage =
    document.getElementById(`quizReviewLockMessage${uid}`);

  if (checkbox) {
    checkbox.disabled = false;
    checkbox.style.cursor = 'pointer';

    const label = checkbox.closest('label');
    if (label) {
      label.style.cursor = 'pointer';
    }
  }

  if (lockMessage) {
    lockMessage.style.borderColor =
      'rgba(25,181,176,.28)';

    lockMessage.style.background =
      'rgba(25,181,176,.07)';

    lockMessage.style.color =
      '#9DE7E3';

    const dot =
      lockMessage.querySelector('.cvn-review-dot');

    const text =
      lockMessage.querySelector('.cvn-review-txt');

    if (dot) {
      dot.style.background = '#4ADE80';
    }

    if (text) {
      text.textContent =
        'Lesson review complete — confirm below to unlock your retake.';
    }
  }

  setQuizReviewProgress(uid, 3);
}


function installQuizReviewWatcher(uid) {
  const state = getQuizAttemptState(uid);

  const lesson =
    document.getElementById(`unitLessonContent${uid}`);

  const content =
    document.querySelector(
      '#corevian-university-app .content'
    );

  if (!lesson) return;

  const scrollEl =
    content ||
    document.scrollingElement ||
    document.documentElement;

  if (!scrollEl) return;

  /*
     Baseline is captured only AFTER the system's automatic
     positioning has finished.

     Therefore system movement cannot satisfy remediation.
  */
  const baselineScrollTop =
    content
      ? content.scrollTop
      : window.scrollY;

  /*
     Determine how much real downward movement is physically
     available from the baseline before the lesson bottom.

     Normal lessons require at least 200px of learner movement.
     Short lessons use 80% of the available distance.
  */
  const scrollerRect =
    content
      ? content.getBoundingClientRect()
      : {
          top: 0,
          bottom: window.innerHeight
        };

  const lessonRect =
    lesson.getBoundingClientRect();

  const availableLessonTravel =
    Math.max(
      0,
      lessonRect.bottom -
      scrollerRect.bottom
    );

  const requiredMovement =
    availableLessonTravel >= 200
      ? 200
      : Math.max(
          1,
          Math.floor(availableLessonTravel * 0.8)
        );

  /*
     A lesson that is already fully visible cannot generate
     meaningful scroll movement. We do NOT automatically award
     review during initial rendering. Instead, an intentional
     learner interaction with the lesson is required.
  */
  if (availableLessonTravel <= 0) {
    const intentionalReview = function () {
      lesson.removeEventListener(
        'click',
        intentionalReview
      );

      lesson.removeEventListener(
        'keydown',
        intentionalReview
      );

      unlockQuizReviewConfirmation(uid);
    };

    lesson.addEventListener(
      'click',
      intentionalReview,
      { once: true }
    );

    lesson.addEventListener(
      'keydown',
      intentionalReview,
      { once: true }
    );

    return;
  }

  const onScroll = function () {
    const currentState =
      getQuizAttemptState(uid);

    if (currentState.scrollCompleted) {
      removeQuizReviewListener(uid);
      return;
    }

    const currentTop =
      content
        ? content.scrollTop
        : window.scrollY;

    const moved =
      currentTop - baselineScrollTop;

    /*
       Employee must physically move DOWN from the baseline.
       Programmatic positioning happened before this listener existed.
    */
    if (moved < requiredMovement) {
      return;
    }

    const currentLessonRect =
      lesson.getBoundingClientRect();

    const currentScrollerBottom =
      content
        ? content.getBoundingClientRect().bottom
        : window.innerHeight;

    const reachedLessonEnd =
      currentLessonRect.bottom <=
      currentScrollerBottom + 150;

    if (!reachedLessonEnd) {
      return;
    }

    unlockQuizReviewConfirmation(uid);
  };

  state._reviewScrollFn = onScroll;
  state._reviewScrollEl = scrollEl;

  scrollEl.addEventListener(
    'scroll',
    onScroll,
    { passive: true }
  );

  if (scrollEl !== window) {
    window.addEventListener(
      'scroll',
      onScroll,
      { passive: true }
    );
  }

  /*
     IMPORTANT:
     There is intentionally NO automatic call to onScroll().
     Completion can only occur from learner activity.
  */
}


function beginQuizReview(uid) {
  const state =
    getQuizAttemptState(uid);

  /*
     Destroy any watcher left from a previous remediation cycle.
  */
  removeQuizReviewListener(uid);

  /*
     Every failed attempt begins a completely new review cycle.
  */
  state.requiresReview = true;
  state.reviewConfirmed = false;
  state.scrollCompleted = false;

  const oldNotice =
    document.getElementById(
      `quizReviewNotice${uid}`
    );

  if (oldNotice) {
    oldNotice.remove();
  }

  /*
     System positioning happens BEFORE review tracking begins.
     This movement must never count as employee review.
  */
  const content =
    document.querySelector(
      '#corevian-university-app .content'
    );

  if (content) {
    content.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  } else {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  setTimeout(() => {
    const lesson =
      document.getElementById(
        `unitLessonContent${uid}`
      );

    if (!lesson) {
      console.error(
        `Corevian review gate: lesson content not found for unit ${uid}.`
      );
      return;
    }

    const notice =
      document.createElement('div');

    notice.id =
      `quizReviewNotice${uid}`;

    notice.className =
      'section-block';

    notice.style.marginBottom =
      '24px';

    notice.innerHTML = `
      <div
        style="
          margin-bottom:26px;
          border:1px solid rgba(25,181,176,.20);
          border-radius:18px;
          background:linear-gradient(
            180deg,
            rgba(14,30,50,.98),
            rgba(7,18,34,.99)
          );
          padding:28px;
          box-shadow:0 20px 50px rgba(0,0,0,.25);
        "
      >

        <div
          style="
            display:flex;
            align-items:flex-start;
            justify-content:space-between;
            gap:16px;
            flex-wrap:wrap;
            margin-bottom:20px;
          "
        >
          <div>
            <div
              style="
                color:#19B5B0;
                font-size:10px;
                font-weight:800;
                letter-spacing:.15em;
                text-transform:uppercase;
                margin-bottom:7px;
              "
            >
              Review Gate
            </div>

            <div
              style="
                font-size:21px;
                font-weight:800;
                color:#FFFFFF;
                line-height:1.25;
              "
            >
              Knowledge Check Review Required
            </div>

            <div
              style="
                font-size:13px;
                color:#8DAABF;
                margin-top:6px;
                line-height:1.6;
                max-width:600px;
              "
            >
              Review the lesson below before beginning another
              knowledge-check attempt. Focus on understanding the
              concepts covered by the questions you missed rather
              than memorizing answer choices.
            </div>
          </div>

          <div
            style="
              padding:6px 14px;
              border:1px solid rgba(25,181,176,.28);
              border-radius:999px;
              color:#7BE0DC;
              background:rgba(25,181,176,.08);
              font-size:10px;
              font-weight:800;
              letter-spacing:.10em;
              text-transform:uppercase;
              white-space:nowrap;
            "
          >
            Review Required
          </div>
        </div>

        <div
          style="
            display:flex;
            align-items:center;
            gap:0;
            margin-bottom:22px;
            overflow-x:auto;
            padding-bottom:2px;
          "
        >
          ${quizReviewStepHTML(
            uid,
            1,
            'Review Required',
            'done'
          )}

          ${quizReviewConnectorHTML()}

          ${quizReviewStepHTML(
            uid,
            2,
            'Review Lesson',
            'active'
          )}

          ${quizReviewConnectorHTML()}

          ${quizReviewStepHTML(
            uid,
            3,
            'Confirm Review',
            'pending'
          )}

          ${quizReviewConnectorHTML()}

          ${quizReviewStepHTML(
            uid,
            4,
            'Retake Check',
            'pending'
          )}
        </div>

        <div
          id="quizReviewLockMessage${uid}"
          style="
            display:flex;
            align-items:flex-start;
            gap:12px;
            padding:13px 16px;
            margin-bottom:18px;
            border:1px solid rgba(255,255,255,.08);
            border-radius:12px;
            background:rgba(255,255,255,.025);
            color:#8DAABF;
            font-size:13px;
            line-height:1.55;
          "
        >
          <span
            class="cvn-review-dot"
            style="
              width:8px;
              height:8px;
              min-width:8px;
              margin-top:5px;
              border-radius:50%;
              background:#19B5B0;
              box-shadow:0 0 8px rgba(25,181,176,.5);
            "
          ></span>

          <span class="cvn-review-txt">
            Review the lesson below to unlock the confirmation.
          </span>
        </div>

        <label
          style="
            display:flex;
            gap:12px;
            align-items:flex-start;
            padding:16px;
            margin-bottom:20px;
            border:1px solid rgba(255,255,255,.08);
            border-radius:12px;
            background:rgba(3,10,22,.45);
            color:#C8D6E3;
            line-height:1.6;
            cursor:default;
          "
        >
          <input
            type="checkbox"
            id="quizReviewConfirm${uid}"
            onchange="toggleQuizReviewConfirmation(${uid})"
            disabled
            style="
              width:18px;
              height:18px;
              margin-top:2px;
              flex:0 0 auto;
              accent-color:#19B5B0;
              cursor:not-allowed;
            "
          >

          <span style="font-size:13px;">
            I confirm that I reviewed this unit before beginning
            another knowledge-check attempt.
          </span>
        </label>

        <div
          style="
            display:flex;
            align-items:center;
            gap:14px;
            flex-wrap:wrap;
          "
        >
          <button
            id="quizReviewContinue${uid}"
            class="btn btn-primary"
            onclick="completeQuizReview(${uid})"
            disabled
            style="
              opacity:.38;
              cursor:not-allowed;
              min-width:200px;
              font-size:14px;
              font-weight:800;
              letter-spacing:.02em;
            "
          >
            Continue to Retake
          </button>

          <div
            id="quizReviewBtnHint${uid}"
            style="
              font-size:12px;
              color:#5A7285;
              line-height:1.5;
            "
          >
            Review the lesson and confirm your review to continue.
          </div>
        </div>

      </div>
    `;

    /*
       Gate belongs immediately ABOVE the actual lesson.
    */
    lesson.prepend(notice);

    /*
       Wait until the system smooth-scroll has settled.
       Only then arm employee review tracking.
    */
    state._reviewWatcherTimer =
      setTimeout(() => {
        state._reviewWatcherTimer = null;
        installQuizReviewWatcher(uid);
      }, 650);

  }, 80);
}


function toggleQuizReviewConfirmation(uid) {
  const state =
    getQuizAttemptState(uid);

  const checkbox =
    document.getElementById(
      `quizReviewConfirm${uid}`
    );

  const button =
    document.getElementById(
      `quizReviewContinue${uid}`
    );

  const hint =
    document.getElementById(
      `quizReviewBtnHint${uid}`
    );

  if (!checkbox || !button) return;

  /*
     Checkbox should never be usable until lesson review
     has actually been completed.
  */
  if (!state.scrollCompleted) {
    checkbox.checked = false;
    state.reviewConfirmed = false;

    button.disabled = true;
    button.style.opacity = '.38';
    button.style.cursor = 'not-allowed';

    return;
  }

  const confirmed =
    checkbox.checked === true;

  state.reviewConfirmed =
    confirmed;

  button.disabled =
    !confirmed;

  button.style.opacity =
    confirmed ? '1' : '.38';

  button.style.cursor =
    confirmed
      ? 'pointer'
      : 'not-allowed';

  if (hint) {
    hint.textContent =
      confirmed
        ? 'Ready — click to start your fresh knowledge-check attempt.'
        : 'Confirm your lesson review to continue.';

    hint.style.color =
      confirmed
        ? '#19B5B0'
        : '#5A7285';
  }

  setQuizReviewProgress(
    uid,
    confirmed ? 4 : 3
  );
}


function completeQuizReview(uid) {
  const state =
    getQuizAttemptState(uid);

  const checkbox =
    document.getElementById(
      `quizReviewConfirm${uid}`
    );

  /*
     DOUBLE VALIDATION:
     Review completion and confirmation are independent gates.
  */
  if (!state.scrollCompleted) {
    alert(
      'Please review the lesson content before retaking the knowledge check.'
    );
    return;
  }

  if (
    !checkbox ||
    checkbox.checked !== true ||
    state.reviewConfirmed !== true
  ) {
    alert(
      'Please confirm that you reviewed the unit before continuing.'
    );
    return;
  }

  removeQuizReviewListener(uid);

  const notice =
    document.getElementById(
      `quizReviewNotice${uid}`
    );

  if (notice) {
    notice.remove();
  }

  /*
     IMPORTANT:
     Keep reviewConfirmed TRUE for the handoff into the existing
     retakeQuiz() guard.

     retakeQuiz() is the existing production function that increments
     the attempt, clears optionOrders, resets the review state, and
     renders the fresh Knowledge Check.
  */
  state.requiresReview = true;
  state.reviewConfirmed = true;

  retakeQuiz(uid);
}
function retakeQuiz(uid) {
  const state = getQuizAttemptState(uid);

  // A failed learner must review the lesson before another attempt.
  if (state.requiresReview && !state.reviewConfirmed) {
    alert('Review the unit lesson before beginning another knowledge-check attempt.');
    scrollToTop();
    return;
  }

  // Start a fresh attempt.
  state.attempt += 1;
  state.requiresReview = false;
  state.reviewConfirmed = false;

  // Clear the previous answer order so every retake receives
  // a newly shuffled set of answer choices.
  state.optionOrders = {};

  // Clear previous responses.
  if (quizAnswers[uid]) delete quizAnswers[uid];

  const existing = unitProgress[uid] || {};
  unitProgress[uid] = {
    ...existing,
    read: true,
    score: null,
    passed: false,
    status: existing.status === 'passed' ? 'passed' : 'in_progress'
  };

  renderUnit(uid);

  // Move the learner directly to the fresh knowledge check.
  setTimeout(() => {
    const qz = document.getElementById('qz' + uid);
    if (qz) {
      qz.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, 100);
}

function markUnitPass(uid, correct, total) {
  unitProgress[uid] = {read:true, score:1, passed:true};
  updateProgress();
  const allUnits = getUnitsForRole(currentRole);
  const idx = allUnits.findIndex(x => x.id === uid);
  const next = allUnits[idx+1];
  if (next) navToUnit(next.id);
  else showView('dashboard');
}

function scrollToTop() {
  const content = document.querySelector('#corevian-university-app .content');
  const target = document.getElementById('contentInner');

  if (content) {
    content.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });

  if (target) {
    target.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }
}

// ═══════════════════════════════════════════════════════════════════
// RECERTIFICATION & RENEWAL TRACKING — LEADERSHIP / ADMIN
const RECERT_STATUS_LABELS={current:'Current',due_soon:'Due Soon',expired:'Expired',pending:'Pending',not_required:'Not Required'};
function recertFmtDate(v){if(!v)return 'Not set';const d=new Date(v);return Number.isNaN(d.getTime())?'Not set':d.toLocaleDateString();}
function recertStatusTone(s){return s==='current'?'#4ADE80':s==='due_soon'?'#FCD34D':s==='expired'?'#FCA5A5':s==='not_required'?'#94A3B8':'#7DD3FC';}
async function loadRecertificationWorkspace(){
 const c=getSupabaseClient(); const [p,i,a]=await Promise.all([c.from('profiles').select('user_id,full_name,role,job_title,department,active').eq('active',true),c.from('recertification_items').select('id,user_id,requirement_code,requirement_name,completed_at,due_at,status,evidence_reference,updated_at').order('updated_at',{ascending:false}),c.from('operational_authorizations').select('id,user_id,status,created_at').order('created_at',{ascending:false})]);
 for(const r of [p,i,a])if(r.error)throw r.error; const la=new Map();(a.data||[]).forEach(r=>{if(!la.has(r.user_id))la.set(r.user_id,r);}); const bi=new Map();(i.data||[]).forEach(r=>{if(!bi.has(r.user_id))bi.set(r.user_id,[]);bi.get(r.user_id).push(r);}); const scopedProfiles=await scopeProfilesToLeadershipDepartment(c,p.data||[]); recertificationCandidates=scopedProfiles.map(x=>({...x,items:bi.get(x.user_id)||[],latest_authorization:la.get(x.user_id)||null}));
}
async function renderRecertificationTracking(){
  // Executive Demo must use simulated workforce data only.
  // Never load live employee qualification records while Demo Mode is active.
  if (demoMode && typeof renderDemoRecertification === 'function') {
    renderDemoRecertification();
    return;
  }

  const root=document.getElementById('contentInner');
  if(!canManageRecertification()){
    root.innerHTML='<div class="comp-error">Recertification and renewal controls are available only to Leadership and Administrator roles.</div>';
    return;
  }

  root.innerHTML='<div class="comp-notice">Loading controlled recertification records…</div>';
 
 try{await loadRecertificationWorkspace();if(selectedRecertificationUserId&&!recertificationCandidates.some(x=>x.user_id===selectedRecertificationUserId))selectedRecertificationUserId=null;if(!selectedRecertificationUserId&&recertificationCandidates.length)selectedRecertificationUserId=recertificationCandidates[0].user_id;let o='<option value="">Select employee</option>';recertificationCandidates.forEach(p=>o+=`<option value="${p.user_id}" ${p.user_id===selectedRecertificationUserId?'selected':''}>${esc(p.full_name)}</option>`);root.innerHTML=`<div class="unit-header"><div class="unit-eyebrow">ONGOING QUALIFICATION CONTROL</div><div class="unit-title">Recertification & Renewal Tracking</div><div class="unit-subtitle">Track controlled renewal requirements after qualification. Enter renewal dates only when an applicable Corevian, client, manufacturer, or regulatory requirement establishes them.</div></div><div class="comp-notice"><strong>Control boundary:</strong> This record does not by itself create, restore, expand, or replace Operational Authorization or service-specific endorsements.</div><div class="comp-card"><label class="comp-label">Employee</label><select class="comp-select" onchange="changeRecertificationEmployee(this.value)">${o}</select></div><div id="recertPanel"></div>`;await renderRecertificationPanel();}catch(e){root.innerHTML=`<div class="comp-error">${esc(e?.message||'Unable to load recertification workspace.')}</div>`;}
}
async function changeRecertificationEmployee(v){selectedRecertificationUserId=v||null;selectedRecertificationItemCode=null;await renderRecertificationPanel();

 } 
 window.changeRecertificationEmployee=changeRecertificationEmployee;

 function openEmployeeRecertification(userId) {
  selectedRecertificationUserId = userId || null;
  selectedRecertificationItemCode = null;

  showView('recertification');
}

window.openEmployeeRecertification = openEmployeeRecertification;
 async function returnToPersonnelRecord(userId) {
  currentView = 'workforce';

  document
    .querySelectorAll('.nav-btn')
    .forEach(b => b.classList.remove('active'));

  const btn = document.getElementById('navWorkforceReadiness');
  if (btn) btn.classList.add('active');

  await renderWorkforceReadiness();

  if (userId) {
    selectWorkforceEmployee(userId);

    requestAnimationFrame(() => {
      const safeUserId = CSS.escape(String(userId));

      const card = document.querySelector(
        `.workforce-detail-card[data-workforce-detail-user="${safeUserId}"]`
      );

      if (card) {
        card.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });
  }
}

window.returnToPersonnelRecord = returnToPersonnelRecord;

async function returnToWorkforceEmployee() {
  await returnToPersonnelRecord(selectedRecertificationUserId);
}

window.returnToWorkforceEmployee = returnToWorkforceEmployee;
async function renderRecertificationPanel(){
  
 const p=document.getElementById('recertPanel');if(!p)return;const c=recertificationCandidates.find(x=>x.user_id===selectedRecertificationUserId);if(!c){p.innerHTML='<div class="comp-notice">Select an employee.</div>';return;}const items=c.items||[],counts={current:0,due_soon:0,expired:0,pending:0,not_required:0};items.forEach(x=>{if(x.status in counts)counts[x.status]++;});const auth=c.latest_authorization?`${String(c.latest_authorization.status).replaceAll('_',' ')} · ${new Date(c.latest_authorization.created_at).toLocaleString()}`:'No Operational Authorization decision recorded.';
 const editItem=selectedRecertificationItemCode?items.find(x=>x.requirement_code===selectedRecertificationItemCode):null;if(selectedRecertificationItemCode&&!editItem)selectedRecertificationItemCode=null;
 const dateInput=v=>{if(!v)return '';const d=new Date(v);if(Number.isNaN(d.getTime()))return '';return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;};
 const rows=items.length?items.map(x=>`<tr><td style="padding:9px"><strong>${esc(x.requirement_code)}</strong><br><span class="comp-history">${esc(x.requirement_name)}</span></td><td style="padding:9px;color:${recertStatusTone(x.status)};font-weight:800">${esc(RECERT_STATUS_LABELS[x.status]||x.status)}</td><td style="padding:9px">${esc(recertFmtDate(x.completed_at))}</td><td style="padding:9px">${esc(recertFmtDate(x.due_at))}</td><td style="padding:9px">${esc(x.evidence_reference||'—')}</td><td style="padding:9px"><button class="secondary-btn" onclick="editRecertificationItem('${String(x.requirement_code).replaceAll("'","\\'")}')">Edit</button></td></tr>`).join(''):'<tr><td colspan="6" style="padding:12px" class="comp-history">No recertification requirements recorded.</td></tr>';
 p.innerHTML=`<div class="comp-card"><div class="comp-card-title">${esc(c.full_name)} · Renewal File</div><div class="comp-history">Latest Operational Authorization: ${esc(auth)}</div><div class="stat-grid" style="margin-top:14px"><div class="stat-card"><div class="stat-num">${counts.current}</div><div class="stat-label">Current</div></div><div class="stat-card"><div class="stat-num">${counts.due_soon}</div><div class="stat-label">Due Soon</div></div><div class="stat-card"><div class="stat-num">${counts.expired}</div><div class="stat-label">Expired</div></div><div class="stat-card"><div class="stat-num">${counts.pending}</div><div class="stat-label">Pending</div></div></div></div><div class="comp-card"><div class="comp-card-title">${editItem?'Edit Existing Requirement':'Record New Requirement'}</div><div class="comp-notice">${editItem?'Existing values are loaded below. Change only the fields that should be updated, then save.':'Use the controlled requirement\'s actual code, name, evidence, and applicable dates. Do not invent a renewal interval.'}</div><div class="comp-grid"><div><label class="comp-label">Requirement Code</label><input class="comp-input" id="recertCode" value="${esc(editItem?.requirement_code||'')}" ${editItem?'readonly':''}></div><div><label class="comp-label">Requirement Name</label><input class="comp-input" id="recertName" value="${esc(editItem?.requirement_name||'')}"></div><div><label class="comp-label">Status</label><select class="comp-select" id="recertStatus"><option value="pending" ${editItem?.status==='pending'?'selected':''}>Pending</option><option value="current" ${editItem?.status==='current'?'selected':''}>Current</option><option value="due_soon" ${editItem?.status==='due_soon'?'selected':''}>Due Soon</option><option value="expired" ${editItem?.status==='expired'?'selected':''}>Expired</option><option value="not_required" ${editItem?.status==='not_required'?'selected':''}>Not Required</option></select></div><div><label class="comp-label">Evidence Reference</label><input class="comp-input" id="recertEvidence" value="${esc(editItem?.evidence_reference||'')}"></div><div><label class="comp-label">Completed Date</label><input class="comp-input" id="recertCompleted" type="date" value="${dateInput(editItem?.completed_at)}"></div><div><label class="comp-label">Due Date</label><input class="comp-input" id="recertDue" type="date" value="${dateInput(editItem?.due_at)}"></div></div><div style="margin-top:14px"><button class="primary-btn" onclick="saveRecertificationItem()">${editItem?'Save Requirement Update':'Record Recertification Item'}</button>${editItem?'<button class="secondary-btn" style="margin-left:10px" onclick="cancelRecertificationEdit()">Cancel Edit</button>':''}<span class="comp-history" id="recertSaveStatus" style="margin-left:12px"></span></div></div><div class="comp-card"><div class="comp-card-title">Recorded Requirements</div><div class="comp-notice">Use <strong>Edit</strong> to update an existing requirement. This loads its current values first so unchanged fields are preserved.</div><div style="overflow-x:auto"><table style="width:100%;border-collapse:collapse;font-size:12px"><thead><tr style="text-align:left;color:var(--slate)"><th style="padding:9px">Requirement</th><th style="padding:9px">Status</th><th style="padding:9px">Completed</th><th style="padding:9px">Due</th><th style="padding:9px">Evidence</th><th style="padding:9px">Action</th></tr></thead><tbody>${rows}</tbody></table></div></div>`;
 const backButton = document.createElement('button');

backButton.type = 'button';
backButton.textContent = '← Back to Employee Record';

backButton.style.cssText = `
  margin-bottom:16px;
  padding:10px 16px;
  border:1px solid rgba(125,227,223,.45);
  border-radius:8px;
  background:rgba(125,227,223,.08);
  color:#7DE3DF;
  font:inherit;
  font-size:12px;
  font-weight:800;
  cursor:pointer;
`;

backButton.onclick = returnToWorkforceEmployee;

p.prepend(backButton);
}
async function editRecertificationItem(code){selectedRecertificationItemCode=code;await renderRecertificationPanel();document.getElementById('recertCode')?.scrollIntoView({behavior:'smooth',block:'center'});} window.editRecertificationItem=editRecertificationItem;
async function cancelRecertificationEdit(){selectedRecertificationItemCode=null;await renderRecertificationPanel();} window.cancelRecertificationEdit=cancelRecertificationEdit;
async function saveRecertificationItem(){
 const c=recertificationCandidates.find(x=>x.user_id===selectedRecertificationUserId);if(!c){alert('Select an employee first.');return;}const code=(document.getElementById('recertCode')?.value||'').trim(),name=(document.getElementById('recertName')?.value||'').trim(),status=document.getElementById('recertStatus')?.value||'pending',evidence=(document.getElementById('recertEvidence')?.value||'').trim(),completed=document.getElementById('recertCompleted')?.value||'',due=document.getElementById('recertDue')?.value||'';if(!code||!name){alert('Requirement Code and Requirement Name are required.');return;}if(status==='current'&&!completed){alert('Enter the actual completion date for a Current requirement.');return;}if((status==='due_soon'||status==='expired')&&!due){alert('Enter the applicable due date.');return;}if(status==='current'&&!evidence){alert('Enter an evidence reference for a Current requirement.');return;}const st=document.getElementById('recertSaveStatus');if(st)st.textContent='Saving…';try{const r=await getSupabaseClient().rpc('record_recertification_item',{p_user_id:selectedRecertificationUserId,p_requirement_code:code,p_requirement_name:name,p_status:status,p_completed_at:completed?completed+'T12:00:00':null,p_due_at:due?due+'T12:00:00':null,p_evidence_reference:evidence||null});if(r.error)throw r.error;selectedRecertificationItemCode=null;await loadRecertificationWorkspace();await renderRecertificationPanel();const x=document.getElementById('recertSaveStatus');if(x)x.textContent='Recertification item saved successfully.';}catch(e){const m=e?.message||'Unable to record recertification item.';if(st)st.textContent='Save failed: '+m;alert(m);}}
window.saveRecertificationItem=saveRecertificationItem;

// ═══════════════════════════════════════════════════════════════════
// CONTROLLED PRODUCTION FINAL ASSESSMENT
// ═══════════════════════════════════════════════════════════════════
function getProductionAssessmentType(){
  const assigned = currentEnrollment && currentEnrollment.assigned_role;
  return (assigned === 'leadership' || assigned === 'admin') ? 'leadership_final' : 'employee_final';
}

async function loadProductionFinalAssessment(){
  if (demoMode) return null;
  if (!currentEnrollment || !currentCurriculumVersionId) throw new Error('No active production enrollment is loaded.');
  const assessmentType = getProductionAssessmentType();
  const result = await getSupabaseClient().from('final_assessments')
    .select('id,curriculum_version_id,assessment_type,title,passing_percent,question_count,content_json,active,created_at,updated_at')
    .eq('curriculum_version_id', currentCurriculumVersionId)
    .eq('assessment_type', assessmentType)
    .eq('active', true)
    .limit(1);
  if (result.error) throw result.error;
  const row = result.data && result.data[0];
  if (!row) throw new Error('No active controlled final assessment is configured for this curriculum and role.');
  const questions = row.content_json && Array.isArray(row.content_json.questions) ? row.content_json.questions : [];
  if (!questions.length) throw new Error('The controlled final assessment contains no questions.');
  if (Number(row.question_count) !== questions.length) throw new Error('Final assessment question-count control does not match the stored question bank. Contact Operations.');
  currentFinalAssessment = row;
  return row;
}

async function renderProductionFinalAssessment(){
  const host = document.getElementById('contentInner');
  const units = getUnitsForRole(currentRole);
  if (!allUnitsComplete(currentRole)) {
    const remaining = units.filter(u => !unitProgress[u.id] || !unitProgress[u.id].passed);
    host.innerHTML = `
      <div class="cert-header">
        <div class="cert-title">Corevian University™ · Final Assessment</div>
        <div class="cert-subtitle">Complete every required unit before the final assessment unlocks.</div>
      </div>
      <div style="background:var(--navy2);border:1px solid rgba(255,255,255,.06);border-radius:16px;padding:28px;text-align:center">
        <div style="font-size:18px;font-weight:800;color:var(--white);margin-bottom:8px">Final Assessment Locked</div>
        <div style="font-size:14px;color:var(--slate);margin-bottom:20px">${remaining.length} required unit${remaining.length===1?'':'s'} remaining.</div>
        <div style="display:flex;flex-wrap:wrap;gap:8px;justify-content:center">
          ${remaining.map(u=>`<span style="padding:6px 14px;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.1);border-radius:20px;font-size:12px;color:var(--slate)">Unit ${u.num} · ${esc(u.title)}</span>`).join('')}
        </div>
        <button class="btn btn-primary" style="margin-top:24px" onclick="goDashboard(event)">Back to Dashboard</button>
      </div>`;
    return;
  }

  host.innerHTML = `<div class="cert-header"><div class="cert-title">Loading controlled final assessment…</div></div>`;
  try {
    const a = await loadProductionFinalAssessment();
    const threshold = Number(a.passing_percent);
    const correctToPass = Math.ceil((threshold/100) * Number(a.question_count));
    const assigned = currentEnrollment.assigned_role;
    const roleLabel = assigned === 'admin' ? 'Administrator' : assigned === 'leadership' ? 'Leadership' : assigned === 'trainer' ? 'Trainer' : 'Employee';
    const instructions = a.content_json && a.content_json.instructions ? esc(a.content_json.instructions) : '';
    host.innerHTML = `
      <div class="cert-header">
        <div class="cert-title">${esc(a.title)}</div>
        <div class="cert-subtitle">${roleLabel} pathway · Controlled Curriculum v2.1</div>
        <div class="cert-rules">
          <div class="cert-rule"><div class="cr-val">${threshold}%</div><div class="cr-label">Passing Score</div></div>
          <div class="cert-rule"><div class="cr-val">${a.question_count}</div><div class="cr-label">Questions</div></div>
          <div class="cert-rule"><div class="cr-val">${correctToPass}</div><div class="cr-label">Correct to Pass</div></div>
        </div>
      </div>
      <div style="background:var(--navy2);border:1px solid rgba(255,255,255,.06);border-radius:16px;padding:28px;margin-bottom:24px">
        <h3 style="font-size:17px;font-weight:800;color:var(--white);margin-bottom:12px">Assessment Rules</h3>
        <p style="font-size:14px;color:var(--slate);line-height:1.8">${instructions}</p>
        ${finalAssessmentStatus.passed ? `<div style="margin-top:16px;padding:14px;border:1px solid rgba(22,163,74,.28);border-radius:10px;color:#4ADE80">Latest production status: Passed · Best score ${finalAssessmentStatus.bestScorePercent}% · ${finalAssessmentStatus.attemptCount} attempt${finalAssessmentStatus.attemptCount===1?'':'s'} recorded.</div>` : ''}
      </div>
      <div style="text-align:center;padding:20px">
  <div style="font-size:20px;font-weight:800;color:var(--white);margin-bottom:8px">
    Training Acknowledgment
  </div>

  <div style="font-size:14px;color:var(--slate);line-height:1.7;max-width:760px;margin:0 auto 18px">
    Before beginning the Final Assessment, confirm that you personally completed and reviewed the required Corevian University training assigned to your pathway.
  </div>

  <label style="display:flex;align-items:flex-start;gap:12px;max-width:760px;margin:0 auto 22px;padding:16px 18px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.10);border-radius:10px;text-align:left;cursor:pointer">
    <input
      type="checkbox"
      id="trainingAssessmentAck"
      onchange="toggleFinalAssessmentAcknowledgment()"
      style="margin-top:4px;width:18px;height:18px;flex:0 0 auto"
    >
    <span style="font-size:14px;color:var(--white);line-height:1.65">
      I acknowledge that I personally completed and reviewed the required Corevian University training for my assigned pathway and understand that I am responsible for the knowledge presented before beginning this Final Assessment.
    </span>
  </label>

  <div style="font-size:14px;color:var(--slate);margin-bottom:24px">
    Your submission will be written to the production assessment-attempt history.
  </div>

  <button
    id="beginFinalAssessmentBtn"
    class="btn btn-primary"
    style="font-size:16px;padding:16px 40px;opacity:.45;cursor:not-allowed"
    onclick="startExam()"
    disabled
  >
    Begin Final Assessment →
  </button>
</div>`;
  } catch (err) {
    console.error(err);
    host.innerHTML = `<div class="cert-header"><div class="cert-title">Final Assessment Unavailable</div><div style="margin-top:18px;padding:18px;border:1px solid rgba(220,38,38,.25);border-radius:12px;color:var(--slate);line-height:1.7">${esc(err.message || 'The controlled final assessment could not be loaded.')}</div></div>`;
  }
}
function toggleFinalAssessmentAcknowledgment(){
  const ack = document.getElementById('trainingAssessmentAck');
  const btn = document.getElementById('beginFinalAssessmentBtn');

  if (!ack || !btn) return;

  const confirmed = ack.checked === true;

  btn.disabled = !confirmed;
  btn.style.opacity = confirmed ? '1' : '.45';
  btn.style.cursor = confirmed ? 'pointer' : 'not-allowed';
}
function startProductionFinalAssessment(){
    const trainingAck = document.getElementById('trainingAssessmentAck');

  if (!trainingAck || !trainingAck.checked) {
    alert('You must acknowledge completion and review of the required Corevian University training before beginning the Final Assessment.');
    return;
  }
  const a = currentFinalAssessment;
  if (!a) { renderProductionFinalAssessment(); return; }
  const questions = a.content_json.questions.slice();
  const answers = {};
  window.examData = { exam:questions, examAnswers:answers, passThreshold:Number(a.passing_percent)/100, production:true, assessment:a, startedAt:new Date().toISOString() };
  let html = `<div class="cert-header"><div class="cert-title">Final Assessment · In Progress</div><div class="cert-subtitle">${questions.length} controlled questions · Complete every question before submitting</div></div>`;
  questions.forEach((q,i)=>{
    html += `<div class="q-block" id="eq${i}">
      <div class="q-num" style="color:var(--teal)">QUESTION ${i+1} OF ${questions.length}${q.unit ? ` <span style="font-size:9px;color:var(--slate);margin-left:8px">UNIT ${String(q.unit).padStart(2,'0')}</span>` : ''}</div>
      <div class="q-text">${esc(q.q)}</div><div class="q-opts">`;
    (q.opts||[]).forEach((opt,oi)=>{
      html += `<div class="q-opt" id="eq${i}_${oi}" onclick="selectExamOpt(${i},${oi},this,examData)"><div class="opt-circle">${String.fromCharCode(65+oi)}</div><div class="opt-text">${esc(opt)}</div></div>`;
    });
    html += `</div></div>`;
  });
 html += `
  <div id="examScoreBlock" style="display:none"></div>

  <div id="assessmentCertificationBlock"
       style="margin:28px 0 18px;padding:22px;background:var(--navy2);border:1px solid rgba(255,255,255,.10);border-radius:14px">

    <div style="font-size:12px;font-weight:800;letter-spacing:.08em;color:var(--teal);margin-bottom:12px">
      ASSESSMENT CERTIFICATION
    </div>

    <label style="display:flex;align-items:flex-start;gap:12px;cursor:pointer">
      <input
        type="checkbox"
        id="finalAssessmentCertification"
        onchange="toggleFinalAssessmentSubmission()"
        style="margin-top:4px"
      >
      <span style="font-size:14px;line-height:1.7;color:rgba(255,255,255,.82)">
        I certify that I completed this assessment and understand that my submitted responses and score will become part of my Corevian training and qualification record.
      </span>
    </label>

  </div>

  <div class="btn-row" id="examSubmitBtn">
    <button
      id="finalAssessmentSubmitBtn"
      class="btn btn-primary"
      style="font-size:15px;padding:14px 32px;opacity:.45;cursor:not-allowed"
      onclick="submitExam(examData,examData.passThreshold)"
      disabled
    >
      Submit Final Assessment
    </button>
  </div>`;
  document.getElementById('contentInner').innerHTML = html;
  window.scrollTo(0,0);
}
function toggleFinalAssessmentSubmission(){
  const certification = document.getElementById('finalAssessmentCertification');
  const button = document.getElementById('finalAssessmentSubmitBtn');

  if(!certification || !button) return;

  const certified = certification.checked === true;

  button.disabled = !certified;
  button.style.opacity = certified ? '1' : '.45';
  button.style.cursor = certified ? 'pointer' : 'not-allowed';
}
async function persistFinalAssessmentResult(data, correct, pct, passed){
  const client = getSupabaseClient();
  const assessmentType = data.assessment.assessment_type;
  const latest = await client.from('assessment_attempts')
    .select('attempt_number')
    .eq('enrollment_id', currentEnrollment.id)
    .eq('assessment_type', assessmentType)
    .is('unit_id', null)
    .order('attempt_number', {ascending:false})
    .limit(1);
  if (latest.error) throw latest.error;
  const attemptNumber = latest.data && latest.data[0] ? Number(latest.data[0].attempt_number)+1 : 1;
  const payload = {
    final_assessment_id:data.assessment.id,
    curriculum_version_id:currentCurriculumVersionId,
    assessment_type:assessmentType,
    total_questions:data.exam.length,
    correct_count:correct,
    passing_percent:Number(data.assessment.passing_percent),
    answers:data.exam.map((q,i)=>({question_id:q.id||i+1,unit:q.unit||null,selected_index:data.examAnswers[i],correct_index:q.correct,is_correct:data.examAnswers[i]===q.correct}))
  };
  const inserted = await client.from('assessment_attempts').insert({
    enrollment_id:currentEnrollment.id,
    unit_id:null,
    assessment_type:assessmentType,
    attempt_number:attemptNumber,
    score_percent:pct,
    passed:passed,
     certification_statement:'I certify that I completed this assessment and understand that my submitted responses and score will become part of my Corevian training and qualification record.',
     certified_at:new Date().toISOString(),
    graded_by:'System'
   });
  if (inserted.error) throw inserted.error;
  const oldBest = finalAssessmentStatus.bestScorePercent == null ? null : Number(finalAssessmentStatus.bestScorePercent);
  finalAssessmentStatus = {
    passed: Boolean(finalAssessmentStatus.passed || passed),
    bestScorePercent: oldBest == null ? pct : Math.max(oldBest,pct),
    attemptCount: attemptNumber,
    assessmentType:assessmentType
  };
  updateProgress();
  return attemptNumber;
}

// ═══════════════════════════════════════════════════════════════════
// CERTIFICATION EXAM
// ═══════════════════════════════════════════════════════════════════
function renderExam() {
  // Staff Mode loads the controlled final-assessment definition from Supabase.
  // Demo Mode keeps the legacy prototype exam isolated and non-production.
  if (!demoMode) {
    return renderProductionFinalAssessment();
  }

  const units = getUnitsForRole(currentRole);
  const ready = demoMode || allUnitsComplete(currentRole);
  const isLeader = currentRole === 'leadership';

  if (!ready) {
    const remaining = units.filter(u => !unitProgress[u.id] || !unitProgress[u.id].passed);
    document.getElementById('contentInner').innerHTML = `
      <div class="cert-header">
        <div class="cert-title">Certification Exam</div>
        <div class="cert-subtitle">Complete all units before taking the exam</div>
      </div>
      <div style="background:var(--navy2);border:1px solid rgba(255,255,255,.06);border-radius:16px;padding:28px;text-align:center">
        <div style="font-size:36px;margin-bottom:16px">🔒</div>
        <div style="font-size:18px;font-weight:800;color:var(--white);margin-bottom:8px">Exam Locked</div>
        <div style="font-size:14px;color:var(--slate);margin-bottom:20px">${remaining.length} unit${remaining.length>1?'s':''} still required:</div>
        <div style="display:flex;flex-wrap:wrap;gap:8px;justify-content:center">
          ${remaining.map(u=>`<span style="padding:6px 14px;background:rgba(255,255,255,.05);border:1px solid rgba(255,255,255,.1);border-radius:20px;font-size:12px;color:var(--slate)">Unit ${u.num} · ${esc(u.title)}</span>`).join('')}
        </div>
        <button class="btn btn-primary" style="margin-top:24px" onclick="goDashboard(event)">Back to Dashboard</button>
      </div>`;
    return;
  }

  const examUnits = isLeader ? 'all 17 units' : 'all 12 units';
  const examQ = isLeader ? 35 : 22;
  const passQ = isLeader ? 32 : 20;

  document.getElementById('contentInner').innerHTML = `
    <div class="cert-header">
      <div class="cert-title">Corevian University™ · Certification Exam</div>
      <div class="cert-subtitle">${currentRole === 'leadership' ? 'Leadership' : currentRole === 'trainer' ? 'Trainer' : 'Employee'} Operational Certification</div>
      <div class="cert-rules">
        <div class="cert-rule"><div class="cr-val">90%</div><div class="cr-label">Passing Score</div></div>
        <div class="cert-rule"><div class="cr-val">${examQ}</div><div class="cr-label">Questions</div></div>
        <div class="cert-rule"><div class="cr-val">${passQ}</div><div class="cr-label">Correct to Pass</div></div>
      </div>
    </div>
    <div style="background:var(--navy2);border:1px solid rgba(255,255,255,.06);border-radius:16px;padding:32px;margin-bottom:24px">
      <h3 style="font-size:17px;font-weight:800;color:var(--white);margin-bottom:12px">Exam Rules</h3>
      <p style="font-size:14px;color:var(--slate);line-height:1.8">
        This exam is taken without reference to your training materials. You have completed ${examUnits} — this exam confirms you have retained and understood the content.<br><br>
        Answer every question independently. Your score is calculated automatically. A score of 90% or higher records the written certification requirement as passed. Below 90%: your trainer will identify the units to review, and you will retake the exam after completing the review.<br><br>
        This written certification does not replace the practical competency demonstration and operational authorization required before independent field operation.
      </p>
    </div>
    <div style="text-align:center;padding:20px">
      <div style="font-size:48px;margin-bottom:16px">📋</div>
      <div style="font-size:20px;font-weight:800;color:var(--white);margin-bottom:8px">Ready to Begin?</div>
      <div style="font-size:14px;color:var(--slate);margin-bottom:24px">Once started, complete the exam in one session. Close your notes before continuing.</div>
      <button class="btn btn-primary" style="font-size:16px;padding:16px 40px" onclick="startExam()">Begin Certification Exam →</button>
    </div>`;
}

function startExam() {
  if (!demoMode) return startProductionFinalAssessment();

  // Build a mixed exam from all completed units
  const units = getUnitsForRole(currentRole);
  const allQuestions = [];
  units.forEach(u => {
    (u.quiz || []).forEach((q, qi) => {
      allQuestions.push({...q, unitId: u.id, unitTitle: u.title, unitNum: u.num, qi});
    });
  });

  // Shuffle and select
  const shuffled = allQuestions.sort(() => Math.random() - 0.5);
  const isLeader = currentRole === 'leadership';
  const count = isLeader ? 35 : 22;
  const exam = shuffled.slice(0, Math.min(count, shuffled.length));
  const examAnswers = {};
  const passThreshold = 0.9;

  let html = `<div class="cert-header">
    <div class="cert-title">Certification Exam · In Progress</div>
    <div class="cert-subtitle">${exam.length} questions · Do not close this window</div>
  </div>`;

  exam.forEach((q, i) => {
    html += `<div class="q-block" id="eq${i}">
      <div class="q-num" style="color:var(--teal)">QUESTION ${i+1} OF ${exam.length} <span style="font-size:9px;color:var(--slate);margin-left:8px">UNIT ${q.unitNum} · ${esc(q.unitTitle)}</span></div>
      <div class="q-text">${esc(q.q)}</div>
      <div class="q-opts">`;
    q.opts.forEach((opt, oi) => {
      html += `<div class="q-opt" id="eq${i}_${oi}" onclick="selectExamOpt(${i},${oi},this,examData)">
        <div class="opt-circle">${String.fromCharCode(65+oi)}</div>
        <div class="opt-text">${esc(opt)}</div>
      </div>`;
    });
    html += `</div></div>`;
  });

  html += `<div id="examScoreBlock" style="display:none"></div>
    <div class="btn-row" id="examSubmitBtn">
      <button class="btn btn-primary" style="font-size:15px;padding:14px 32px" onclick="submitExam(examData,${passThreshold})">Submit Exam</button>
    </div>`;

  // Store exam data on window for the onclick handlers
  window.examData = {exam, examAnswers, passThreshold};

  document.getElementById('contentInner').innerHTML = html;
  document.getElementById('contentInner').scrollIntoView();
}

function selectExamOpt(qi, oi, el, data) {
  const opts = document.querySelectorAll(`[id^="eq${qi}_"]`);
  opts.forEach(o => o.classList.remove('selected'));
  el.classList.add('selected');
  data.examAnswers[qi] = oi;
}

async function submitExam(data, passThreshold) {
    const certification = document.getElementById('finalAssessmentCertification');

  if (!certification || certification.checked !== true) {
    alert('Please complete the Assessment Certification before submitting your Final Assessment.');

    if (certification) {
      certification.scrollIntoView({ behavior: 'smooth', block: 'center' });
      certification.focus();
    }

    return;
  }
  const unanswered = data.exam.map((_,i)=>i).filter(i => data.examAnswers[i] === undefined);
  if (unanswered.length > 0) {
    alert(`Please answer all ${unanswered.length} remaining question${unanswered.length>1?'s':''} before submitting.`);
    // Scroll to first unanswered
    const el = document.getElementById('eq'+unanswered[0]);
    if (el) el.scrollIntoView({behavior:'smooth', block:'center'});
    return;
  }

  let correct = 0;
  data.exam.forEach((q, i) => {
    const sel = data.examAnswers[i];
    const ok = sel === q.correct;
    if (ok) correct++;
    // Show results
    const opts = document.querySelectorAll(`[id^="eq${i}_"]`);
    opts.forEach((o, oi) => {
      if (oi === q.correct) o.classList.add('correct');
      else if (oi === sel) o.classList.add('incorrect');
      else o.classList.add('revealed');
      o.onclick = null;
    });
  });

  const score = correct / data.exam.length;
  const passed = score >= passThreshold;
  const pct = Math.round(score * 100);

  let recordedAttempt = null;
  if (data.production) {
    const submitBtn = document.getElementById('examSubmitBtn');
    if (submitBtn) submitBtn.innerHTML = '<button class="btn btn-primary" disabled style="opacity:.65">Recording assessment…</button>';
    try {
      recordedAttempt = await persistFinalAssessmentResult(data, correct, pct, passed);
    } catch (err) {
      console.error(err);
      alert('Your answers were scored, but the production attempt could not be saved. Do not retake yet. Contact Operations. Details: '+(err.message||err));
      if (submitBtn) submitBtn.innerHTML = '<button class="btn btn-primary" onclick="submitExam(examData,examData.passThreshold)">Retry Save</button>';
      return;
    }
  }

  const block = document.getElementById('examScoreBlock');
  block.style.display = 'block';
  block.innerHTML = `<div class="quiz-score" style="margin:32px 0">
    <div class="score-big ${passed?'pass':'fail'}" style="font-size:64px">${pct}%</div>
    <div class="score-label" style="font-size:16px">${correct} of ${data.exam.length} correct</div>
    <div class="score-verdict ${passed?'pass':'fail'}" style="font-size:15px;margin-top:12px">
      ${data.production ? (passed ? 'FINAL ASSESSMENT PASSED — Knowledge Mastery Recorded' : 'RETAKE REQUIRED — Score Below 90%') : (passed ? '✓ CERTIFIED — Corevian University™ Certification Earned' : '✗ Retake Required — Score Below 90%')}
    </div>
    ${passed ? `
      <div style="margin-top:24px;padding:24px;background:rgba(22,163,74,.08);border:1px solid rgba(22,163,74,.2);border-radius:12px">
        <div style="font-size:13px;color:#4ADE80;font-weight:700;margin-bottom:8px">${data.production ? 'KNOWLEDGE ASSESSMENT PASSED' : 'CERTIFICATION EARNED'}</div>
        <div style="font-size:14px;color:rgba(255,255,255,.8);line-height:1.8">
          <strong style="color:var(--white)">${esc(currentUser)}</strong> has passed the Corevian University™ ${data.production ? 'Final Assessment' : ((currentRole === 'leadership' ? 'Leadership' : currentRole === 'trainer' ? 'Trainer' : 'Employee Operational Specialist') + ' Certification Exam')} with a score of ${pct}%.${data.production ? `<br><br>Production attempt ${recordedAttempt} has been recorded in Supabase.` : ''}<br><br>Passing the written assessment does not by itself grant practical competency, endorsement, qualification, or operational authorization for independent field work.
        </div>
      </div>` : `
      <div style="margin-top:20px;padding:20px;background:rgba(220,38,38,.06);border:1px solid rgba(220,38,38,.15);border-radius:12px">
        <div style="font-size:13px;color:#FCA5A5;font-weight:700;margin-bottom:8px">REVIEW REQUIRED</div>
        <div style="font-size:14px;color:rgba(255,255,255,.7);line-height:1.8">
          Your trainer will review the questions you missed and identify which units to re-read.
          After completing the review, schedule your retake with Operations.
        </div>
      </div>`}
  </div>`;

  document.getElementById('examSubmitBtn').innerHTML = `<button class="btn btn-outline" onclick="goDashboard(event)">Back to Dashboard</button>`;
  block.scrollIntoView({behavior:'smooth', block:'center'});
}

// ═══════════════════════════════════════════════════════════════════
// UTILS
// ═══════════════════════════════════════════════════════════════════
function esc(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g,'&amp;')
    .replace(/</g,'&lt;')
    .replace(/>/g,'&gt;')
    .replace(/"/g,'&quot;');
}

// INIT is handled by the Supabase-aware controller below.
